import moment from 'moment';
import {ActivityEndType} from '../backend/enums/activity-end-type';
import {AffiliationType} from '../backend/enums/affiliation-type';
import {BackendLanguageTag} from '../backend/enums/backend-language-tag';
import {CameraFacing} from '../backend/enums/camera-facing';
import {CaregiverRelationshipRole} from '../backend/enums/caregiver-relationship-role';
import {DeviceType} from '../backend/enums/device-type';
import {EntitySpecialPurpose} from '../backend/enums/entity-special-purpose';
import {EntityType} from '../backend/enums/entity-type';
import {InviteState} from '../backend/enums/invite-state';
import {PlaylistType} from '../backend/enums/playlist-type';
import {MobileNetworkType} from '../backend/enums/mobile-network-type';
import {OperativeSystemName} from '../backend/enums/operative-system-name';
import {ProcessStatus} from '../backend/enums/process-status';
import {RegionCode} from '../backend/enums/region-code';
import {RelationshipType} from '../backend/enums/relationship-type';
import {ReminderResponseValue} from '../backend/enums/reminder-response-value';
import {UserGender} from '../backend/enums/user-gender';
import {UserSignInType} from '../backend/enums/user-sign-in-type';
import {UserType} from '../backend/enums/user-type';
import {InviteContactMethod} from '../enums/invite-contact-method';
import {MomentLanguageCode} from '../enums/moment-language-code';
import {SignInError} from '../enums/sign-in-error';
import {TimeLapseType} from '../enums/time-lapse-type';
import {LanguageTranslation} from './types';
import {Religion} from '../backend/enums';
import {CaregiverTaskCompletion} from '../enums/caregiver-task-completion';
import {CareTaskNotDoneReason} from '../backend/enums/care-task-not-done-reason';
import {CareTaskFeature} from '../backend/enums/care-task-feature';
import {RolePermissionType} from '../backend/enums/role-permission-type';
import {PosologySchedule} from '../backend/enums/posology-schedule';
import {Frequency} from '../types';
import {MaritalStatus} from '../backend/enums/marital-status';
import {EducationLevel} from '../backend/enums/education-level';

// Overriding
moment.updateLocale(MomentLanguageCode.glES, {
  // Start week on Sunday
  // https://github.com/react-dates/react-dates/issues/60#issuecomment-244576967
  week: {
    dow: 0,
  },
});

export const gl_ES: LanguageTranslation = {
  legal_documents: {
    terms_and_conditions: 'Termos e condicións',
    privacy_policy: 'Política de privacidade',
  },
  products: {
    bluebird: 'Sioslife',
    bluebird_with_gender: 'a Sioslife',
  },
  family_applications: {
    title: 'Aplicacións',
    entity: 'Entidade',
    choose_entity: 'Seleccione unha entidade para ver as aplicacións desta persoa maior.',
    no_affiliations:
      'Esta persoa maior non ten afiliacións a entidades. As aplicacións non están dispoñibles.',
    missing_affiliations:
      'As afiliacións non están dispoñibles. Actualice a sesión ou contacte co soporte.',
    load_error:
      'Non se puideron cargar as aplicacións. Pode que se denegase o acceso ou que a aplicación non estea dispoñible.',
    retry: 'Tentar de novo',
    more: 'Cargar máis',
    back: 'Volver ás aplicacións',
    access: 'Acceso da persoa maior',
    enabled: 'Activado',
    disabled: 'Desactivado',
    enable: 'Activar acceso',
    disable: 'Desactivar acceso',
    confirm:
      'Cambiar o acceso desta persoa maior á aplicación? Aplícase a todas as entidades; non instala a aplicación nin cambia a dispoñibilidade na entidade.',
    write_error: 'Non se puido cambiar o acceso. Ténteo de novo.',
    write_success: 'Acceso actualizado.',
  },
  reminders: {
    group_time: 'Hora',
    group_reminder: 'Recordatorio',
    group_repeats: 'Repetición',
    group_recipients: 'Destinatarios',
    group_actions: 'Accións',
    no_entity_assigned: 'Sen entidade asignada',
    group_switch_entity: 'Cambie á entidade do recordatorio para xestionalo.',
    group_edit: 'Editar grupo de recordatorios',
    group_delete: 'Eliminar grupo de recordatorios',
    group_delete_confirm: 'Eliminar “{{title}}” para todos os destinatarios ({{count}})?',
    group_partial: 'Algúns cambios fallaron. Ténteo de novo ou peche para actualizar.',
    group_future_required: 'Escolle unha data/hora futura.',
    group_advanced:
      'A hora e a repetición desta programación avanzada son fixas. Pode cambiar a data de inicio.',
    my_title: 'Os meus recordatorios',
    my_scope: 'Recordatorios creados por ti en todas as entidades.',
    creation_scope:
      'Os novos recordatorios créanse para persoas maiores da entidade seleccionada: {{entity}}.',
    recipient: 'Destinatario',
    title: 'Recordatorios',
    subtitle: 'Xestiona os recordatorios desta persoa maior.',
    add: 'Crear recordatorio',
    bulk_title: 'Crear recordatorios',
    bulk_success: 'Creáronse {{success}} de {{total}} recordatorios.',
    bulk_partial:
      'Creáronse {{success}} de {{total}} recordatorios. Ténteo de novo para as demais persoas.',
    edit: 'Editar recordatorio',
    loading: 'Cargando recordatorios…',
    empty_title: 'Aínda non hai recordatorios',
    empty_description: 'Crea un recordatorio para a persoa seleccionada.',
    load_more: 'Cargar máis',
    retry: 'Tentar de novo',
    load_error: 'Non foi posible cargar os recordatorios.',
    senior_unavailable:
      'A persoa seleccionada non está dispoñible. Selecciónea de novo para continuar.',
    delete_title: 'Eliminar recordatorio',
    delete_description: 'Queres eliminar este recordatorio? Esta acción non se pode desfacer.',
    delete: 'Eliminar',
    cancel: 'Cancelar',
    enable: 'Activar recordatorio',
    disable: 'Desactivar recordatorio',
    status_active: 'Activo',
    status_inactive: 'Desactivado',
    next_run: 'Próxima execución',
    save_error: 'Non foi posible gardar o recordatorio.',
    save_success: 'Recordatorio gardado correctamente.',
    delete_error: 'Non foi posible eliminar o recordatorio.',
    delete_success: 'Recordatorio eliminado correctamente.',
    active_error: 'Non foi posible cambiar o estado do recordatorio.',
    active_success: 'Recordatorio activado correctamente.',
    inactive_success: 'Recordatorio desactivado correctamente.',
    response_types: {yes_no: 'Si ou non', ok: 'OK'},
    recurrence: {
      once: 'Unha vez',
      per_minute: 'Cada minuto',
      hourly: 'Cada {{count}} hora(s)',
      daily: 'Cada {{count}} día(s)',
      weekly: 'Cada {{count}} semana(s)',
      monthly: 'Cada {{count}} mes(es)',
      yearly: 'Cada {{count}} ano(s)',
      current_unsupported: 'Valor actual ({{value}})',
    },
    form: {
      title: 'Título',
      title_placeholder: 'Escribe o título do recordatorio',
      message: 'Mensaxe',
      message_placeholder: 'Escribe a mensaxe do recordatorio',
      response_type: 'Tipo de resposta',
      date: 'Data inicial',
      time: 'Hora inicial',
      recurrence: 'Repetición',
      recurrence_count: 'Repetir cada',
      save: 'Gardar',
      title_required: 'O título é obrigatorio.',
      title_too_long: 'O título non pode superar os {{max}} caracteres.',
      message_required: 'A mensaxe é obrigatoria.',
      message_too_long: 'A mensaxe non pode superar os {{max}} caracteres.',
      date_required: 'A data é obrigatoria.',
      time_required: 'A hora é obrigatoria.',
      invalid_date_time: 'Introduce unha data e hora válidas.',
      date_in_past: 'A data non pode estar no pasado.',
      time_in_past: 'A hora non pode estar no pasado.',
    },
  },
  family_services: {
    title: 'Servizos',
    choose: 'Escolla unha institución',
    none: 'Non hai institucións asociadas.',
    unavailable: 'Os servizos non están dispoñibles nas súas institucións.',
    empty: 'Non hai servizos dispoñibles.',
    error: 'Non se puideron cargar os servizos.',
    denied: 'Acceso denegado.',
    not_found: 'Institución ou servizo non dispoñible.',
    retry: 'Tentar de novo',
    call: 'Chamar',
    no_phone: 'Contacto telefónico non dispoñible.',
    unsupported: 'Acción de contacto non dispoñible para este servizo.',
    active: 'Activo',
    inactive: 'Inactivo',
    enable: 'Activar',
    disable: 'Desactivar',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    save_error: 'Non se puido actualizar o servizo.',
    saved: 'Servizo actualizado.',
    refresh_error: 'Actualizado, pero non se puido actualizar a lista.',
  },
  family_invite: {
    contact_method: 'Método de contacto',
    phone: 'Teléfono móbil',
    action: 'Convidar un coidador',
    context: 'Convidar un coidador para {{name}}',
    name: 'Nome',
    email: 'Correo electrónico',
    next: 'Continuar',
    back: 'Volver',
    send: 'Enviar convite',
    confirm: 'Enviar un convite a {{name}} a {{destination}}?',
    success: 'Convite enviado. A persoa debe aceptalo antes de ser contacto.',
    uncertain: 'Pode que se enviase o convite. Consulte coa persoa antes de enviar outro.',
    invalid: 'Comprobe o nome e os datos de contacto.',
    denied: 'Non ten permiso para enviar este convite.',
    unavailable: 'A persoa maior seleccionada non está dispoñible.',
  },
  family_profile: {
    user_details: 'Datos da persoa usuaria',
    remove_link: 'Eliminar vínculo',
    remove_link_success: 'A túa relación foi eliminada.',
    remove_link_confirm:
      'Eliminar a túa relación con {{name}}? Perderás o acceso á súa información. Isto non eliminará a persoa maior nin a relación doutra persoa.',
    remove_link_error: 'Non se puido eliminar a túa relación. Téntao de novo.',
    remove_link_denied: 'Non tes permiso para eliminar esta relación.',
    cancel: 'Cancelar',
    institutions: 'Institucións',
    senior_contacts: 'Contactos maiores',
    senior_empty: 'Non se atoparon contactos maiores.',
    title: 'Perfil',
    contacts: 'Coidadores',
    empty: 'Non se atoparon coidadores.',
    load_more: 'Cargar máis',
    loading: 'Cargando…',
    error: 'Non se puideron cargar os datos.',
    denied: 'Acceso denegado.',
    unavailable: 'Esta información non está dispoñible.',
    retry: 'Tentar de novo',
    close: 'Pechar',
  },
  family_media: {
    recording: 'Gravando',
    camera: 'Usar cámara',
    take_photo: 'Facer foto',
    record_video: 'Gravar vídeo',
    stop_recording: 'Deter gravación',
    camera_error: 'Cámara ou micrófono non dispoñible. Comprobe os permisos e ténteo de novo.',
    download: 'Descargar',
    share: 'Compartir',
    export_error: 'Non se puido preparar o ficheiro. Téntao de novo ou utiliza outro navegador.',
    title: 'Álbum da familia',
    upload: 'Subir',
    refresh: 'Actualizar',
    loading: 'Cargando o álbum…',
    empty_title: 'Aínda non hai fotos nin vídeos',
    empty_description: 'Aquí aparecerán as fotos e os vídeos compartidos con esta persoa maior.',
    load_error: 'Non foi posible cargar o álbum.',
    refresh_error: 'Non foi posible actualizar o álbum.',
    load_more: 'Cargar máis',
    loading_more: 'Cargando máis…',
    retry: 'Tentar de novo',
    senior_unavailable:
      'A persoa seleccionada non está dispoñible. Selecciónea de novo para continuar.',
    photo: 'Foto',
    video: 'Vídeo',
    processing: 'Procesando o vídeo…',
    processing_failed: 'Non foi posible procesar este contido.',
    uploaded_by: 'Subido por',
    you: 'Ti',
    created_at: 'Engadido o',
    description: 'Descrición',
    edit: 'Editar',
    delete: 'Eliminar',
    cancel: 'Cancelar',
    save: 'Gardar',
    saving: 'Gardando…',
    upload_photo: 'Subir foto ou vídeo',
    upload_progress: 'Subindo…',
    invalid_file_type: 'Selecciona un ficheiro JPG, PNG, GIF, MP4 ou MOV.',
    upload_request_error: 'Non foi posible iniciar a carga.',
    upload_transfer_error: 'Non foi posible subir o ficheiro.',
    upload_record_error:
      'Fallou a solicitude para gardar o ficheiro, pero pode que se gardase. Actualiza e comproba o álbum antes de tentalo de novo para evitar duplicados.',
    upload_success: 'Subida completada. Os vídeos poden tardar en procesarse.',
    edit_success: 'Descrición actualizada correctamente.',
    edit_error: 'Non foi posible actualizar a descrición.',
    router_prompt_edit_title: 'Saír sen gardar os cambios deste elemento do álbum?',
    router_prompt_edit_message: 'Ao saír, perderás os cambios feitos na descrición.',
    delete_success: 'Elemento do álbum eliminado correctamente.',
    delete_error: 'Non foi posible eliminar este elemento do álbum.',
    delete_confirm_title: 'Eliminar elemento do álbum',
    delete_confirm_description: 'Queres eliminar este elemento? Esta acción non se pode desfacer.',
    detail_not_found: 'Non se atopou este elemento do álbum.',
    detail_load_error: 'Non foi posible cargar este elemento.',
    detail_unavailable: 'Este elemento non está dispoñible.',
    signed_url_error: 'Non foi posible abrir este elemento. Téntao de novo.',
  },
  relationship_types: {
    [RelationshipType.Family]: 'Familiar',
    [RelationshipType.Friend]: 'Amigo',
    [RelationshipType.Healthcare]: 'Provedor de servizos sanitarios',
    [RelationshipType.Service]: 'Servizo',
    [RelationshipType.Other]: 'Outros',
  },
  user_types: {
    [UserType.Caregiver]: 'Coidador',
    [UserType.Follower]: 'Seguidor',
    [UserType.Senior]: 'Persoa maior',
  },
  genders: {
    [UserGender.Female]: 'Muller',
    [UserGender.Male]: 'Home',
    [UserGender.Undisclosed]: 'Sen especificar',
    [UserGender.Unspecified]: 'Outros',
  },
  marital_statuses: {
    [MaritalStatus.CivilUnion]: 'Vivindo xuntos',
    [MaritalStatus.Divorced]: 'Divorciado/a',
    [MaritalStatus.Married]: 'Casado/a',
    [MaritalStatus.Other]: 'Outros',
    [MaritalStatus.Single]: 'Solteiro/a',
    [MaritalStatus.Widowed]: 'Viúvo/a',
  },
  education_levels: {
    [EducationLevel.BachelorsDegree]: 'Graduado',
    [EducationLevel.BasicEducation]: 'Educación primaria',
    [EducationLevel.DoctoratePhd]: 'Doutoramento',
    [EducationLevel.MastersDegree]: 'Máster',
    [EducationLevel.NoFormalEducation]: 'Sen formación académica',
    [EducationLevel.Other]: 'Outros',
    [EducationLevel.PostgraduateDiploma]: 'Título de posgrao',
    [EducationLevel.PrimaryEducation]: 'Educación primaria',
    [EducationLevel.SecondaryEducation]: 'Educación secundaria',
    [EducationLevel.VocationalTechnicalTraining]: 'Educación profesional e técnica',
  },
  languages: {
    [BackendLanguageTag.ptPT]: 'Português (Portugal)',
    [BackendLanguageTag.enUS]: 'Inglês (EUA)',
    [BackendLanguageTag.esES]: 'Espanhol (Espanha)',
    [BackendLanguageTag.glES]: 'Galego (España)',
    [BackendLanguageTag.ptBR]: 'Português (Brasil)',
  },
  regions: {
    [RegionCode.PT]: 'Portugal',
    [RegionCode.ES]: 'España',
    [RegionCode.GL]: 'Galicia',
    [RegionCode.BR]: 'Brasil',
  },
  roles: {
    [CaregiverRelationshipRole.Administrator]: 'Director',
    [CaregiverRelationshipRole.Contact]: 'Contacto',
    [CaregiverRelationshipRole.Member]: 'Membro',
  },
  religions: {
    [Religion.AfricanTraditionalAndDiasporic]: 'Diáspora tradicional e africana',
    [Religion.Agnostic]: 'Agnosticismo',
    [Religion.Animism]: 'Animismo',
    [Religion.Atheist]: 'Ateísmo',
    [Religion.BahaI]: 'Bahaísmo',
    [Religion.Buddhism]: 'Budismo',
    [Religion.CaoDai]: 'Caodaísmo',
    [Religion.ChineseTraditionalReligion]: 'Chinés tradicional',
    [Religion.Christianity]: 'Cristianismo',
    [Religion.Hinduism]: 'Hinduísmo',
    [Religion.Islam]: 'Islam',
    [Religion.Jainism]: 'Xainismo',
    [Religion.Juche]: 'Xuche',
    [Religion.Judaism]: 'Xudaísmo',
    [Religion.NeoPaganism]: 'Neopaganismo',
    [Religion.Nonreligious]: 'Ningunha relixión',
    [Religion.Rastafari]: 'Rastafarismo',
    [Religion.Secular]: 'Secularismo',
    [Religion.Shinto]: 'Sintoísmo',
    [Religion.Sikhism]: 'Sixismo',
    [Religion.Spiritism]: 'Espiritismo',
    [Religion.Tenrikyo]: 'Tenrikyo',
    [Religion.UnitarianUniversalism]: 'Unitarismo-Universalismo',
    [Religion.Zoroastrianism]: 'Zoroastrismo',
    [Religion.PrimalIndigenous]: 'Indíxena',
    [Religion.Undisclosed]: 'Sen especificar',
    [Religion.Unspecified]: 'Outros',
  },
  affiliation_types: {
    [AffiliationType.Senior]: 'Persoa maior',
    [AffiliationType.Caregiver]: 'Coidador',
    [AffiliationType.Administrator]: 'Director',
    [AffiliationType.Manager]: 'Xerente',
    [AffiliationType.Operator]: 'Operador',
    [AffiliationType.MedicalProfessional]: 'Profesional sanitario',
  },
  role_permissions_section: {
    overview: 'Visión xeral',
    seniors: 'Persoas maiores',
    team: 'Equipo',
    'digital-inclusion': 'Inclusión dixital',
    settings: 'Configuracións',
    'daily-care-log': 'Rexistros de coidados',
    calendar: 'Calendario',
    occurrences: 'Incidentes',
    'quick-access-devices': 'Acceso rápido',
  },
  role_permissions: {
    [RolePermissionType.Overview_Activity]: 'Actividade xeral',
    [RolePermissionType.Overview_Statistics]: 'Estatísticas xerais',
    [RolePermissionType.Seniors_Dashboard_Activity]: 'Panel > Actividade',
    [RolePermissionType.Seniors_Dashboard_GeneralStatistics]: 'Panel > Estatísticas xerais',
    [RolePermissionType.Seniors_Dashboard_HealthStatistics]: 'Panel > Estatísticas de saúde',
    [RolePermissionType.Seniors_List]: 'Lista de persoas maiores',
    [RolePermissionType.Seniors_Dashboard]: 'Panel',
    [RolePermissionType.Seniors_IndividualPlan]: 'Plan individual',
    [RolePermissionType.Seniors_PersonalData_Reminders]: 'Recordatorios',
    [RolePermissionType.Seniors_BADL]: 'Plan de coidados > BADL',
    [RolePermissionType.Seniors_IADL]: 'Plan de coidados > IADL',
    [RolePermissionType.Seniors_ClinicalSupport]: 'Plan de coidados > Apoio clínico',
    [RolePermissionType.Seniors_PersonalDevelopment]: 'Plan de coidados > Desenvol. persoal',
    [RolePermissionType.Seniors_CareAndActivities]: 'Plan de coidados > Resumo de coidados',
    [RolePermissionType.Seniors_CareAndActivities_BADL]:
      'Plan de coidados > Resumo de coidados > BADL',
    [RolePermissionType.Seniors_CareAndActivities_IADL]:
      'Plan de coidados > Resumo de coidados > IADL',
    [RolePermissionType.Seniors_CareAndActivities_ClinicalSupport]:
      'Plan de coidados > Resumo de coidados > Apoio clínico',
    [RolePermissionType.Seniors_CareAndActivities_PersonalDevelopment]:
      'Plan de coidados > Resumo de coidados > Desenvol. persoal',
    [RolePermissionType.Seniors_CareServices]: 'Escala de Servizo',
    [RolePermissionType.Seniors_PersonalData_Profile]: 'Datos persoais > Perfil',
    [RolePermissionType.Seniors_PersonalData_Caregivers]: 'Datos persoais > Coidadores',
    [RolePermissionType.Seniors_PersonalData_Health]: 'Datos persoais > Saúde',
    [RolePermissionType.Seniors_DigitalInclusion_Applications]: 'Inclusión dixital > Aplicacións',
    [RolePermissionType.Seniors_DigitalInclusion_Devices]: 'Inclusión dixital > Dispositivos',
    [RolePermissionType.Seniors_DigitalInclusion_Playlists]:
      'Inclusión dixital > Listas de reprodución',
    [RolePermissionType.Seniors_DigitalInclusion_Cards]: 'Inclusión dixital > Tarxetas',
    [RolePermissionType.Seniors_PersonalData_Attachments]: 'Datos persoais > Anexos',
    [RolePermissionType.Team_Professionals]: 'Lista de Profesionais',
    [RolePermissionType.Team_CareServices]: 'Escala Xeral de Servizo',
    [RolePermissionType.Team_Professionals_Profile]: 'Profesional > Perfil',
    [RolePermissionType.Team_Professionals_Applications]: 'Profesional > Aplicacións',
    [RolePermissionType.Team_Professionals_Cards]: 'Profesionais > Tarxetas',
    [RolePermissionType.Team_Professionals_Playlists]: 'Profesionais > Listas de reprodución',
    [RolePermissionType.Team_Professionals_CareServices]: 'Profesionais > Escala de Servizo',
    [RolePermissionType.Team_Shifts_Management]: 'Xestión de quendas',
    [RolePermissionType.Team_Shifts_Attributions]: 'Asignación de quendas',
    [RolePermissionType.Team_Permissions]: 'Permisos',
    [RolePermissionType.Team_AttendanceTracker_Registry]: 'Rexistro de asistencia > Rexistros',
    [RolePermissionType.Team_AttendanceTracker_Assignments]: 'Rexistro de asistencia > Asignacións',
    [RolePermissionType.DigitalInclusion_Applications]: 'Aplicacións',
    [RolePermissionType.DigitalInclusion_Media]: 'Fotos',
    [RolePermissionType.DigitalInclusion_Devices]: 'Dispositivos',
    [RolePermissionType.DigitalInclusion_Cards]: 'Tarxetas',
    [RolePermissionType.DigitalInclusion_Playlists]: 'Listas de reprodución',
    [RolePermissionType.DigitalInclusion_Services]: 'Servizo',
    [RolePermissionType.Settings_Entity_Profile]: 'Institución > Perfil',
    [RolePermissionType.Settings_Entity_Settings]: 'Institución > Configuración',
    [RolePermissionType.Settings_Licenses]: 'Licenzas',
    [RolePermissionType.Settings_BADL]: 'BADL',
    [RolePermissionType.Settings_IADL]: 'IADL',
    [RolePermissionType.Settings_ClinicalSupport]: 'Apoio clínico',
    [RolePermissionType.Settings_PersonalDevelopment]: 'Desenvolvemento persoal',
    [RolePermissionType.Settings_Templates]: 'Modelos',
    [RolePermissionType.Settings_Occurrences]: 'Incidentes',
    [RolePermissionType.DailyCareLog]: 'Rexistros de coidados',
    [RolePermissionType.DailyCareLog_BADL]: 'Rexistro de coidados > BADL',
    [RolePermissionType.DailyCareLog_IADL]: 'Rexistro de coidados > IADL',
    [RolePermissionType.DailyCareLog_PersonalDevelopment]:
      'Rexistro de coidados > Desenvolvemento persoal',
    [RolePermissionType.DailyCareLog_ClinicalSupport]: 'Rexistro de coidados > Apoio clínico',
    [RolePermissionType.DailyCareLog_Occurrences]: 'Rexistro de coidados > Incidencias',
    [RolePermissionType.Calendar]: 'Calendario',
    [RolePermissionType.Calendar_BADL]: 'Calendario > BADL',
    [RolePermissionType.Calendar_IADL]: 'Calendario > IADL',
    [RolePermissionType.Calendar_PersonalDevelopment]: 'Calendario > Desenvolvemento persoal',
    [RolePermissionType.Calendar_ClinicalSupport]: 'Calendario > Apoio clínico',
    [RolePermissionType.Occurrences]: 'Lista de incidentes',
    [RolePermissionType.SettingsQuickAccessDevices]: 'Acceso rápido',
  },
  screen_paths: {
    professional_caregiver_seniors: 'Persoas maiores',
    professional_caregiver_team: 'Equipo',
    professional_caregiver_roles_and_permissions: 'Postos e permisos',
    senior_dashboard: 'Panel',
    senior_individual_plan: 'Plan individual',
    senior_care_plan: 'Plan de coidados',
    senior_care_and_activity: 'Resumo de coidados',
    senior_personal_data: 'Datos persoais',
    senior_profile: 'Perfil',
    senior_caregivers: 'Coidadores',
    senior_applications: 'Aplicacións',
    senior_devices: 'Dispositivos',
    senior_activity: 'Actividade',
    senior_user_cards: 'Tarxetas',
    senior_statistics: 'Estatísticas',
    senior_health: 'Saúde',
    senior_playlists: 'Listas de reprodución',
    senior_attachments: 'Anexos',
    institution_settings_profile: 'Perfil',
    institution_settings_settings: 'Configuración',
    institution_settings_activity: 'Actividade',
    institution_settings_user_cards: 'Tarxetas',
    institution_settings_devices: 'Dispositivos',
    institution_settings_services: 'Servizo',
    institution_settings_playlist: 'Lista de reprodución',
    institution_settings_statistics: 'Estatísticas',
    institution_settings_licenses: 'Licenzas',
    institution_settings_templates: 'Modelos',
    institution_settings_occurrences: 'Incidentes',
    institution_settings_quick_access_devices: 'Acceso rápido',
  },
  institution_settings_screen: {
    subtitle: {
      write: 'Podes configurar os axustes da institución',
      read: 'Podes consultar os axustes da institución',
    },
    columns: {
      setting: 'Configuración',
      active: 'Activa',
    },
    attendance_tracker: 'Rexistro de asistencia',
    load_error_toast: 'Non se puideron cargar os axustes',
    update_error_toast: 'Non se puido actualizar o axuste',
  },
  attendance_tracker_registry_screen: {
    subtitle: 'Rexistros de xornada dos profesionais',
    filters: {
      all_statuses: 'Todos os estados',
      all_day: 'Todo o día',
      all_professionals: 'Todos os profesionais',
    },
    empty: 'Non hai rexistros de xornada neste día.',
    load_error: 'Non se puideron cargar os rexistros de xornada.',
    retry: 'Tentar de novo',
    waiting_exit: 'A agardar o rexistro de saída',
    add: {
      title: 'Engadir rexistro de xornada',
      professional: 'Profesional',
      professionals_hint:
        'Só aparecen os profesionais co rexistro de xornada activo (obrigatorio ou opcional).',
      professionals_empty:
        'Non hai profesionais co rexistro de xornada activo (obrigatorio ou opcional).',
      professional_placeholder: 'Escolla o profesional',
      kind_label: 'Que desexa rexistrar?',
      kind_session: 'Sesión completa (entrada e saída)',
      kind_entry: 'Só a entrada (sesión en curso)',
      kind_exit: 'Saída pendente',
      open_session_info:
        'Este profesional ten unha entrada aberta desde {{date}}. Rexistre a saída pendente.',
      entry_date: 'Data da entrada',
      entry_time: 'Hora da entrada',
      exit_date: 'Data da saída',
      exit_time: 'Hora da saída',
      reason: 'Motivo',
      reason_placeholder: 'Ex.: o profesional esqueceu rexistrar a saída',
      notice:
        'Este rexistro quedará identificado como rexistrado por {{admin}} en nome de {{professional}} e non ten localización.',
      self_warning:
        'Non pode rexistrar a súa propia xornada por aquí, para evitar que rexistre no seu propio nome. Para rexistrar a súa entrada ou saída, utilice o {{app}}.',
      self_action: 'Abrir o {{app}}',
      cancel: 'Cancelar',
      continue: 'Continuar',
      back: 'Volver',
      confirm: 'Rexistrar',
      confirm_title: 'Confirme o rexistro',
      confirm_description:
        'Unha vez rexistrado, o rexistro de xornada non se pode modificar nin borrar.',
      summary_record: 'Rexistro',
      success: 'Rexistro de xornada engadido correctamente.',
      checking: 'Comprobando o rexistro de xornada do profesional...',
      error_future: 'A data e a hora non poden estar no futuro.',
      error_order:
        'A saída debe ser posterior á entrada e non pode haber outro rexistro na mesma data e hora.',
      error_overlap: 'As datas escollidas sobrepóñense a outro rexistro do profesional.',
      error_type:
        'O rexistro non encaixa cos rexistros que o profesional xa ten. Comprobe as datas e as horas.',
      error_conflict:
        'Os rexistros do profesional cambiaron entrementres. Comprobe os datos e ténteo de novo.',
      error_not_tracking: 'Este profesional non ten o rexistro de xornada activo.',
      error_generic: 'Non se puido engadir o rexistro de xornada. Ténteo de novo.',
      validation_future: 'Non pode estar no futuro.',
      validation_exit_before_entry: 'A saída debe ser posterior á entrada.',
      validation_exit_before_open_entry: 'A saída debe ser posterior á entrada aberta.',
      validation_reason_required: 'Indique o motivo.',
    },
    void: {
      title: 'Anular sesión',
      description:
        'Ao anular, a sesión deixa de contar como tempo traballado e ese período queda libre para novos rexistros. O rexistro consérvase como anulado, co seu nome e o motivo.',
      reason: 'Motivo da anulación',
      reason_placeholder: 'Ex.: rexistro feito por erro',
      cancel: 'Cancelar',
      confirm: 'Anular sesión',
      success: 'Sesión anulada correctamente.',
      error_conflict:
        'Os rexistros do profesional cambiaron entrementres. Actualice a lista e ténteo de novo.',
      error_self: 'Non pode anular os seus propios rexistros.',
      error_generic: 'Non se puido anular a sesión. Ténteo de novo.',
      validation_reason_required: 'Indique o motivo.',
    },
    correct: {
      title: 'Corrixir sesión',
      description:
        'A sesión actual anularase e substituirase polos rexistros que indique a continuación. O rexistro anulado consérvase, e o tempo que tiña queda libre para os novos rexistros.',
      current_session: 'Sesión actual',
      reason: 'Motivo da corrección',
      notice:
        'Os novos rexistros quedarán identificados como rexistrados por {{admin}} en nome de {{professional}} e non teñen localización. A sesión actual quedará anulada, con este motivo.',
      confirm_title: 'Confirme a corrección',
      confirm_description:
        'A sesión actual anularase e os novos rexistros ocuparán o seu lugar. Unha vez rexistrados, non se poden modificar nin borrar.',
      summary_old: 'Sesión a anular',
      summary_new: 'Nova sesión',
      confirm: 'Corrixir sesión',
      success: 'Sesión corrixida correctamente.',
    },
    details: {
      correct_action: 'Corrixir',
      void_action: 'Anular sesión',
      duration: 'Duración',
      title: 'Detalles do rexistro',
      close: 'Pechar',
    },
  },
  attendance_tracker_assignments_screen: {
    subtitle: {
      write: 'Podes definir quen debe rexistrar a asistencia',
      read: 'Podes consultar quen debe rexistrar a asistencia',
    },
    columns: {
      professional: 'Profesional',
      assignment: 'Rexistro de asistencia',
      last_change: 'Último cambio',
    },
    assignment_types: {
      mandatory: 'Obrigatorio',
      optional: 'Opcional',
      not_applicable: 'Non aplicable',
    },
    empty: 'Non hai profesionais',
    last_change_summary: 'Por {{name}} o {{date}}',
    last_change_summary_no_author: '{{date}}',
    load_error_toast: 'Non se puideron cargar as asignacións',
    update_error_toast: 'Non se puido actualizar a asignación',
  },
  date_time_picker: {
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
    clear_button: 'Eliminar',
    today: 'Hoxe',
  },
  date_range_input: {
    title: 'Escolle un intervalo de tempo',
    ok_button: 'Confirmar',
    cancel_button: 'Cancelar',
    presets: {
      day: 'Día',
      week: 'Semana',
      month: 'Mes',
      '7_days': '7 días',
      '15_days': '15 días',
      '30_days': '30 días',
      '180_days': '180 días',
      '365_days': '365 días',
      custom: 'Personalizado',
    },
    errors: {
      invalid_date_range: 'Intervalo de datas non válido',
    },
  },
  generic_header: {
    actions: {
      edit: 'Editar',
      delete: 'Eliminar plan',
      save: 'Gardar modelo',
      import: 'Importar modelo',
      archivePlan: 'Arquivar plan',
      remove: 'Eliminar',
    },
  },
  user_card: {
    actions: {
      edit: 'Editar',
      delete: 'Eliminar',
    },
  },
  role_autocomplete: {
    unselected: 'Sen seleccionar',
    no_matching: 'Non se atopou ningunha coincidencia para {{ type }}.',
  },
  view_type_toggler: {
    tooltip_message: 'Cambiar para ver en {{ type }}',
  },
  index_grid_screen: {
    total_counter: 'Total',
    add_button: 'Engadir',
    search_placeholder: 'Buscar',
    filter_placeholder: 'Filtro',
    empty_message_add_encouraging: 'Preme Engadir para rexistrar {{ type }}.',
    empty_search_message: {
      title: 'Sentímolo, non se atoparon resultados para “{{ name }}”.',
      subtitle: 'Proba unha busca diferente.',
    },
    empty_filters_message: {
      title: 'As opcións de filtro seleccionadas non devolveron ningún resultado.',
      subtitle: 'Proba con filtros diferentes.',
    },
    toggler: {
      grid: 'grella',
      list: 'lista',
    },
  },
  login: {
    welcome: 'Dámosche a benvida á plataforma Sioslife!',
    welcome_daily_care_log: 'Dámosche a benvida á aplicación de rexistro de Sioslife',
    welcome_accept_invite: 'Inicia sesión na túa conta para aceptar a invitación!',
    instruction: 'Usa as túas credenciais para iniciar sesión',
    email_input: 'Correo electrónico',
    password_input: 'Contrasinal',
    enter_button: 'Iniciar sesión',
    enter_button_microsoft: 'Iniciar sesión con Microsoft',
    enter_button_google: 'Iniciar sesión con Google',
    forgot_password: 'Recuperar contrasinal',
    quick_access: 'Iniciar sesión con acceso rápido',
    validate_invite: 'Convidáronte a ser coidador?',
    use_invite_code: 'Usar código de invitación',
    errors: {
      form_validation_invalid_field: 'Completa este campo para iniciar sesión na túa conta',
      [SignInError.Unknown]: 'Produciuse un erro. Téntao de novo máis tarde.',
      [SignInError.InvalidEmail]: 'Correo electrónico non válido',
      [SignInError.UserDisabled]: 'Usuario desactivado',
      [SignInError.UserNotFound]: 'Non se atopou o usuario',
      [SignInError.WrongPassword]: 'Contrasinal incorrecto',
      [SignInError.FailedToGetMe]:
        'Non se puido cargar a información do usuario. Ponte en contacto co servizo de atención ao cliente.',
    },
  },
  recover_password: {
    title: 'Esqueciches o contrasinal?',
    subtitle: 'Axudámosche!',
    instructions:
      'Para establecer un novo contrasinal, introduce o teu enderezo de correo electrónico. Enviarémosche unha mensaxe cunha ligazón que che permitirá facer o cambio.',
    email_input: 'Enderezo de correo electrónico',
    send_button: 'Enviar',
    password_remembered: 'Ao final lembrei o meu contrasinal!',
    go_back_button: 'Volver á pantalla de inicio de sesión',
    errors: {
      form_validation_invalid_field:
        'Cubre este campo para completar a recuperación do contrasinal',
      send_request_error_message: 'Produciuse un erro. Non se puido realizar a solicitude.',
    },
    success: {
      send_request_success_message:
        'Enviáronse as instrucións para cambiar o contrasinal ao enderezo de correo electrónico que proporcionaches.',
    },
  },
  replace_password: {
    title: 'Configurar un novo contrasinal',
    instructions:
      'Vale! Bo traballo! Configuremos un novo contrasinal. Escribe o teu novo contrasinal nos dous espazos seguintes para finalizar o proceso.',
    new_password_input: 'Novo contrasinal',
    repeat_password_input: 'Confirmar o novo contrasinal',
    submit_button: 'Confirmar',
    password_remembered: 'Ao final lembrei o meu contrasinal!',
    go_back_button: 'Ir á pantalla de inicio de sesión',
    errors: {
      form_validation_invalid_field:
        'Cubre este campo para completar a definición do novo contrasinal',
      form_validation_invalid_password_minimum_characters:
        'O contrasinal debe ter polo menos 6 caracteres',
      form_validation_invalid_password_blank_spaces:
        'O contrasinal non debe comezar nin rematar con espazos en branco',
      form_validation_invalid_repeat_password_mismatch:
        'Asegúrate de que os contrasinais son os mesmos',
      change_password_error_message:
        'Produciuse un erro. Non se puido establecer o teu novo contrasinal.',
    },
    success: {
      change_password_success_message:
        'Estableceuse o teu novo contrasinal. Agora podes iniciar sesión co teu novo contrasinal.',
    },
  },
  validate_invite: {
    title: 'Usar código de invitación',
    instructions_1:
      'Para ser coidador de {{brand}}, debes ter unha invitación para acceder á plataforma.',
    instructions_2:
      'Se xa solicitaches unha invitación, consulta a túa caixa de entrada de correo electrónico e/ou SMS e busca unha mensaxe de {{brand}}. Nesta mensaxe atoparás o código que debes introducir a continuación.',
    code_input: 'Código de invitación',
    send_button: 'Validar invitación e continuar',
    errors: {
      form_validation_invalid_field: 'Cubre este campo para completar a validación da invitación',
      invalid_invite_error_message:
        'A invitación que intentaches usar non é válida. Comproba que introduciches o código correcto. Se é así, solicita unha nova invitación.',
    },
  },
  accept_invite: {
    relationship_invite_title: 'Aceptar unha invitación para ser coidador de',
    relationship_invite_instructions:
      'Para ser coidador, debes ter unha conta para acceder á plataforma de {{brand}}. Se é a primeira vez que aceptas unha invitación, preme “{{button_create}}”. Se xa aceptaches outras invitacións no pasado, usa a túa conta existente e preme “{{button_login}}”.',
    affiliation_invite_title:
      'Aceptar unha invitación para unirse e converterse en coidador da organización',
    affiliation_invite_instructions:
      'Para ser coidador, debes ter unha conta para acceder á plataforma de {{brand}}. Se é a primeira vez que aceptas unha invitación, preme “{{button_create}}”. Se xa aceptaches outras invitacións no pasado, usa a túa conta existente e preme “{{button_login}}”.',
    affiliation_type_description:
      'Esta invitación permíteche ser membro {{type}} da organización {{name}}.',
    create_account_button: 'Aceptar e engadir unha nova conta',
    login_button: 'Iniciar sesión cunha conta existente',
    errors: {
      invalid_invite_error_message:
        'A invitación que intentaches usar non é válida. Solicita unha nova invitación.',
    },
  },
  quick_access_login: {
    instruction: 'Escolle o usuario para iniciar sesión',
    all_users: 'Todos os usuarios',
    error:
      'Non se pode continuar co acceso rápido. Téntao de novo. Se o problema persiste, ponte en contacto co teu administrador de sistema.',
    back_to_email_and_password_login: 'Iniciar sesión con correo electrónico e contrasinal',
    go_back_to_the_recent_users_list: 'Volver á lista de usuarios recentes',
    hello_user: 'Ola, {{name}}!',
    insert_your_quick_access_code: 'Introduce o teu código de acceso rápido',
    login_failed: 'Produciuse un erro ao tentar iniciar sesión. Téntao de novo.',
    login_failed_code: 'Código de acceso rápido incorrecto',
    try_again: 'Intentar outra vez',
    regain_access: 'Recuperar código de acceso',
    remove_recent: 'Queres eliminar o usuario da lista de usuarios recentes?',
    regain_access_message:
      'O teu código de acceso rápido pódese restablecer na Área de xestión da plataforma Sioslife, no teu perfil profesional.',
    regain_access_description:
      'Se non tes permisos de acceso á Área de xestión, ponte en contacto cun administrador da túa organización.',
    back_to_previous: 'Volver á pantalla anterior',
  },
  notifications: {
    date: 'Data',
    time: 'Hora',
    title: 'Notificacións',
    check_read: 'Marcar como lido',
    check_unread: 'Marcar como sen ler',
    read_all_notifications: 'Marcar todo como lido',
    read_all_notifications_dialog_title: 'Marcar todas as notificacións como lidas?',
    read_all_notifications_dialog_content:
      'Podes marcar individualmente cada notificación como non lida máis tarde se o desexas.',
    read_all_notifications_disabled_tooltip_message:
      'Xa marcaches todas as notificacións como lidas.',
    read_all_notifications_success_message: 'As notificacións foron todas marcadas como lidas.',
    read_all_notifications_error_message:
      'Non foi posible marcar todas as notificacións como lidas.',
  },
  push_notifications: {
    warnings: {
      unsubscribed_generic_warning_title: 'Non podes recibir notificacións en tempo real',
      unsubscribed_not_supported_warning_message:
        'O navegador que estás a usar non admite a recepción de notificacións. Usa Chrome, Firefox ou Edge para recibir notificacións en tempo real.',
      unsubscribed_error_warning_message:
        'Produciuse un erro, polo que non podes recibir notificacións en tempo real.',
      unsubscribed_permissions_denied_warning_message:
        'Denegáronse os permisos para recibir notificacións. Activa as notificacións para o navegador que estás a usar.',
    },
  },
  invite_modal: {
    caregiver_invite_title: 'Acepta a invitación para converterte en coidador',
    affiliation_type_description:
      'Esta invitación permíteche ser membro {{type}} da organización {{name}}.',
    relationship_type_input: 'Que tipo de relación tes con {{name}}?',
    invite_recipient_not_current_user_description:
      'Estás a usar unha conta con información diferente á da información desta invitación. Revisa con atención se queres continuar.',
    accept_button: 'Si, acepto',
    decline_button: 'Non, rexeito',
    relationship_already_exists_description:
      'Xa es coidador de {{name}} tras aceptar unha invitación anteriormente. Non necesitas facer nada máis.',
    affiliation_already_exists_description:
      'Xa tes unha afiliación coa organización {{name}} porque aceptaches unha invitación anterior. Non necesitas facer nada máis.',
    loading_invite_description_1: 'Cargando detalles da invitación...',
    loading_invite_description_2: 'Agarda por favor...',
    invite_done_unknown_description: 'A invitación xa non se pode usar.',
    invite_done_refused_description:
      'A invitación xa se rexeitou. A invitación xa non se pode usar.',
    invite_done_accepted_description:
      'A invitación xa se aceptou. A invitación xa non se pode usar.',
    invite_done_cancelled_description: 'Cancelouse a invitación. A invitación xa non se pode usar.',
    invite_done_expired_description: 'A invitación caducou. A invitación xa non se pode usar.',
    errors: {
      refuse_invite_error_message: 'Produciuse un erro. No se podía rexeitar a invitación.',
      accept_invite_error_message: 'Produciuse un erro. Non se pode aceptar a invitación.',
      fetch_invite_error_message: 'Produciuse un erro. Non se puido abrir a invitación.',
      fetch_invite_invalid_error_message: 'A invitación que intentaches abrir non é válida.',
    },
    success: {
      refuse_relationship_invite_success_message:
        'Rexeitouse a invitación. {{name}} non está na lista de contactos.',
      accept_relationship_invite_success_message:
        'Aceptouse a invitación. {{name}} está agora na lista de contactos.',
      refuse_affiliation_invite_success_message:
        'Rexeitouse a invitación. {{name}} non está na lista de partes afiliadas.',
      accept_affiliation_invite_success_message:
        'Aceptouse a invitación. {{name}} está agora na lista de partes afiliadas.',
    },
  },
  profile: {
    title: 'Perfil',
    exit_button: 'Saír',
    personal_information_section_title: 'Editar perfil',
    change_theme_section_title: 'O meu tema',
    legal_information_section_title: 'Información legal',
    relationships_section_title: 'As miñas relacións',
    affiliations_section_title: 'As miñas afiliacións',
    logout_normal_button: 'Pechar sesión',
    logout_loading_button: 'Pechando sesión...',
    logout_error_button: 'Erro ao pechar sesión',
    theme_input: 'Tema',
    dark_option: 'Escuro',
    light_option: 'Claro',
    system_option: 'Tema do sistema (automático)',
  },
  create_caregiver_account: {
    register_title: 'Rexistro',
    essential_information_step_description:
      'Dámosche a benvida á plataforma de {{brand}}! Imos engadir unha conta de usuario para que poidas converterte en coidador e, polo tanto, rematar o proceso de aceptación da invitación que recibiches.',
    essential_information_step: 'Información esencial',
    personal_information_step: 'Información persoal',
    optional_information_step: 'Información opcional',
    note_title: 'Notas',
    note_statement:
      'A información marcada cun “*” é necesaria para engadir unha conta de acceso para coidador.',
    continue_button: 'Continuar',
    create_account_button: 'Aceptar a invitación e engadir unha conta',
    start_using_button: 'Comezar',
    go_back_to_login_button: 'Ir á pantalla de inicio de sesión',
    go_back_button: 'Volver atrás',
    success_title: 'Éxito!',
    success_description:
      'Parabéns! Acabas de converterte en usuario da plataforma de {{brand}}! Grazas por unirte a esta comunidade. Agora podes proceder á túa conta de usuario.',
    error_title: 'Non se puido engadir a conta de usuario!',
    error_description:
      'Desafortunadamente, algo fallou ao engadir a túa conta de usuario. Ponte en contacto co servizo de atención ao cliente para obter axuda.',
    relationship_type_input: 'Tipo de relación con {{name}}',
    profession_input: 'Ámbito profesional',
    profession_description_input: 'Profesión/Ocupación',
    email_address_input: 'Enderezo de correo electrónico',
    password_input: 'Contrasinal',
    repeat_password_input: 'Repetir contrasinal',
    legal_documents_title: 'Documentos legais',
    legal_documents_statement:
      'Confirmo que lin e acepto as <0>Condicións xerais de uso</0> e a <1>Política de privacidade e protección de datos</1>.',
    full_name_input: 'Nome completo',
    display_name_input: 'Nome',
    mobile_number_input: 'Número de teléfono móbil',
    profile_picture_input: 'Foto de perfil',
    gender_input: 'Xénero',
    gender_placeholder_input: 'Selecciona un xénero',
    language_input: 'Idioma',
    language_placeholder_input: 'Selecciona un idioma',
    birthday_input: 'Data de nacemento',
    birthday_input_placeholder: 'Escolle unha data',
    tax_identification_number_input: 'Número de identificación fiscal',
    profile_picture_info_message: 'Para unha mellor resolución, a imaxe debe ser JPG/JPEG.',
    display_name_info_message:
      'Neste campo, introduce o nome co que te chaman a diario. Este é o nome que outros usuarios verán no sistema cando interactúen contigo.',
    errors: {
      form_validation_invalid_field:
        'Cubre este campo para completar a creación da conta de coidador',
      form_validation_invalid_email: 'O enderezo de correo electrónico proporcionado non é válido',
      form_validation_invalid_password_minimum_characters:
        'O contrasinal debe ter polo menos 6 caracteres',
      form_validation_invalid_password_blank_spaces:
        'O contrasinal non debe comezar nin rematar con espazos en branco',
      form_validation_invalid_repeat_password_mismatch:
        'Asegúrate de que os contrasinais son os mesmos',
      form_validation_invalid_full_name_maximum_characters:
        'O nome completo debe ter polo menos {{max}} caracteres',
      form_validation_invalid_display_name_maximum_characters:
        'O nome debe ter menos de {{max}} caracteres',
      form_validation_invalid_mobile_phone_number:
        'O número de teléfono móbil indicado non é válido',
      form_validation_not_mobile_phone_number:
        'O número indicado non corresponde a un número de teléfono móbil',
      form_validation_invalid_tax_identification_number:
        'O número de identificación fiscal indicado non é válido',
      form_validation_invalid_legal_documents:
        'Acepta os documentos legais para completar a creación da conta do coidador',
      enter_account_error_message:
        'Produciuse un erro. Non foi posible iniciar sesión na túa conta.',
      invalid_invite_error_message:
        'A invitación que intentaches usar para engadir unha conta non é válida.',
    },
  },
  office: {
    generics: {
      cancel: 'Cancelar',
      save: 'Gardar',
      exit: 'Saír',
      back: 'Atrás',
      apply: 'Aplicar',
      save_pdf: 'Gardar como PDF (.pdf)',
      save_xlsx: 'Gardar en Excel (.xlsx)',
    },
    localized_string_input: {
      fields: {
        placeholder: 'Introduce unha tradución',
        rules: {
          required: 'Campo obrigatorio',
          max_length: 'Non pode ser superar os {{ char }} caracteres.',
        },
      },
    },
    users_filters: {
      title: 'Filtros',
      clear_all: 'Borrar todo',
      sections: {
        socialResponses: {title: 'Servizos sociais'},
        shifts: {title: 'Quenda'},
        activeStatus: {
          title: 'Estados',
          options: {
            active: 'Activos',
            inactive: 'Inactivo',
          },
        },
        taskStatus: {
          title: 'Estados',
          options: {
            planned: 'Planificado',
            handled: 'Procesado',
            done: 'Realizado',
            not_done: 'Sen realizar',
            not_handled: 'Sen tratamento',
          },
        },
        taskGroups: {
          title: 'Tarefas',
        },
        patientOccurrences: {
          title: 'Incidencias de usuarios',
        },
        generalOccurrences: {
          title: 'Incidentes xerais',
        },
      },
    },
    profile_type_tabs: {
      informal_caregiver: 'Informal',
      professional_caregiver: 'Profesional',
      system_administrator: 'Administrador do sistema',
      partner: 'Socio',
    },
    informal_caregiver: {
      tabs: {
        activity: 'Actividade',
        profile: 'Perfil',
        media: 'Multimedia',
        reminders: 'Recordatorios',
      },
      add_menu: {
        button: 'Engadir',
        title: 'Que queres engadir?',
        create_reminder: 'Recordatorio',
        send_photo: 'Fotografía',
        send_video: 'Vídeo',
        send_invite: 'Invitación para o coidador',
      },
      relationship_picker: {
        caption: 'Estás a ver o perfil de:',
        dialog_title: 'Selecciona outro contacto',
      },
      caregiver_app_info_message:
        'Seguimos traballando para mellorar esta área. Para aproveitar ao máximo as funcións de {{brand}} como membro da familia, amigo ou coidador informal, usa a nosa aplicación para dispositivos móbiles <0>Android</0> ou <1>iOS</1>.',
    },
    professional_caregiver: {
      tabs: {
        statistics: 'Estatísticas',
        digital_inclusion: 'Inclusión dixital',
        seniors: 'Maiores',
        applications: 'Aplicacións',
        services: 'Servizo',
        professional_caregivers: 'Equipo',
        activity: 'Actividade',
        devices: 'Dispositivos',
        user_cards: 'Tarxetas',
        licenses: 'Licenzas',
        entity_photos: 'Fotos',
        playlists: 'Listas de reprodución',
        start: 'Comezar',
        planner: 'Calendario',
        occurrences: 'Incidentes',
        settings: 'Configuracións',
        sioslifeAcademy: 'Academia',
        myToDos: 'As miñas tarefas',
      },
      profile: {
        generic_header: {
          title: 'Perfil',
          subtitle: {
            write: 'Podes configurar o teu perfil e outra información',
            read: 'Podes verificar toda a información do perfil',
          },
          breadcrumbs: {
            team: 'Equipo',
            professionals: 'Profesionais',
            profile: 'Perfil',
          },
        },
      },
      affiliation_picker: {
        caption: 'Traballas na organización:',
        dialog_title: 'Seleccionar outra organización',
      },
      settings: {
        generic_header: {
          breadcrumbs: {
            settings: 'Configuracións',
            institution: 'Institución',
            licenses: 'Licenzas',
            templates: 'Modelos',
            occurrences: 'Incidentes',
            quick_access_devices: 'Acceso rápido',
          },
          subtitle: {
            institution: {
              write: 'Podes configurar a información na institución',
              read: 'Podes verificar toda a información na institución',
            },
            activities_sector: 'Podes configurar actividades no sector',
            licenses: 'Podes verificar as licenzas',
            templates: {
              write: 'Podes configurar todos os modelos',
              read: 'Podes ver todos os modelos',
            },
            occurrences: 'Configurar e xestionar os tipos de incidentes',
            quick_access_devices: 'Configurar e xestionar os dispositivos de acceso rápido',
          },
        },
        templates: {
          empty: 'Non existen modelos',
          name: 'Nome',
          area: 'Área',
          description: 'Descrición',
          update: 'Última modificación',
          edit: 'Editar',
          delete: 'Eliminar',
          revert: 'Reverter',
          duplicate: 'Duplicar',
          cancel: 'Cancelar',
          delete_title: 'Seguro que queres eliminar o modelo “{{name}}”?',
          delete_subtitle: 'O plan de coidados manterase cos usuarios',
          snackbar: {
            delete_success_message: 'O modelo “{{name}}” eliminouse con éxito.',
            delete_error_message: 'Non foi posible eliminar o modelo “{{name}}”.',
            duplicate_success_message: 'O modelo “{{name}}” duplicouse.',
            duplicate_error_message: 'Non foi posible duplicar o modelo “{{name}}”.',
            edit_name_success_message: 'O modelo “{{name}}” editouse con éxito.',
            edit_name_error_message: 'Non foi posible editar o modelo “{{name}}”.',
          },
          edit_name: 'Editar nome',
          alert: 'Os cambios realizados neste modelo non afectarán aos usuarios.',
          no_permissions: 'Non tes permisos para editar modelos',
        },
        occurrences: {
          patients: 'Usuarios',
          general: 'Xeral',
          general_plural: 'Xeral',
          occurrence: 'Incidente',
          active: 'Activo',
          action_required: 'Require acción',
          notify: 'Perfís para notificar',
          all_profiles: 'Todos os perfís',
          add: 'Engadir',
          add_modal_title: 'Engadir tipo de incidente: ',
          add_modal_name: 'Nome do tipo de incidente',
          add_name_error: 'O nome xa existe',
          delete_modal_title: 'Seguro que queres eliminar o tipo de incidente “{{name}}”?',
          revert_modal_title: 'Seguro que queres eliminar o tipo de incidente “{{name}}”?',
          delete_modal_subtitle: 'O tipo de incidente xa non estará dispoñible para o rexistro',
          revert_modal_subtitle: 'O tipo de incidente volverá á súa configuración predeterminada.',
          name_error: 'O nome do incidente debe ter menos de {{max}} caracteres',
          add_error_toast: 'Non foi posible engadir o tipo de incidente “{{name}}”.',
          add_success_toast: 'O tipo de incidente engadiuse correctamente!',
          edit_error_toast: 'Non foi posible editar o tipo de incidente “{{name}}”.',
          edit_success_toast: 'O tipo incidente editouse con éxito!',
          delete_error_toast: 'Non foi posible eliminar o tipo de incidente “{{name}}”.',
          delete_success_toast: 'O tipo de incidente “{{name}}” eliminouse con éxito!',
          revert_success_toast: 'O tipo de incidente “{{name}}” reverteuse con éxito!',
          delete_default: 'Configuración estándar: non é posible eliminar este tipo de incidente',
          edit_name_default: 'Configuración estándar: non é posible editar o nome',
          default: 'configuración estándar',
          empty_general: 'Aínda non existe ningún tipo de incidente xeral',
          empty_patients: 'Aínda non existe ningún tipo de incidente cos usuarios',
          no_permissions: 'Non tes permisos para editar os tipos de incidentes',
        },
        quick_access: {
          empty: 'Non existen dispositivos de acceso rápido',
          name: 'Nome',
          type: 'Tipo',
          access: 'Último acceso',
          professional: 'Profesional',
          deactivate: 'Desactivar',
          activate_device: 'Activar neste dispositivo',
          activate_disabled: 'O acceso rápido xa está activado neste dispositivo',
          activate_disabled_ist:
            'O acceso rápido xa está activado neste dispositivo na institución “{{name}}”',
          no_permissions: 'Non tes permisos para editar dispositivos de acceso rápido',
          activate_modal: {
            title: 'Activar o acceso rápido neste dispositivo',
            device_name: 'Nome do dispositivo',
            device_type: 'Tipo de dispositivo',
            success_message: 'O acceso rápido neste dispositivo activouse con éxito!',
            error_message: 'Non foi posible activar o acceso rápido neste dispositivo!',
            name_repeat_error: 'O nome do dispositivo xa está en uso',
          },
          edit_modal: {
            title: 'Editar dispositivo:',
            success_message: 'O dispositivo de acceso rápido editouse con éxito!',
            error_message: 'Non foi posible editar o dispositivo de acceso rápido!',
          },
          deactivate_modal: {
            title: 'Seguro que queres desactivar o acceso rápido para o dispositivo “{{name}}”?',
            subtitle:
              'O dispositivo aínda se pode usar normalmente con correo electrónico e contrasinal.',
            success_message: 'O acceso rápido neste dispositivo desactivouse con éxito!',
            error_message: 'Non foi posible activar o acceso rápido neste dispositivo!',
            button: 'Desactivar',
            no_name: 'Sen nome',
          },
        },
      },
      seniors: {
        edit: {
          profile: {
            generic_header: {
              subtitle: {
                write: 'Podes configurar o teu perfil e outra información importante',
              },
            },
          },
        },
        table_view_screen: {
          name: 'Nome',
          social_response: 'Servizo social',
          age: 'Idade',
          room: 'Dormitorio',
          status: 'Estado ',
          role: 'Posto',
          phone: 'Teléfono',
          email: 'Correo electrónico',
        },
      },
      shifts_allocations: {
        generic_header: {
          breadcrumbs: {
            team: 'Equipo',
            shifts_allocations: 'Asignación de quendas',
          },
        },
        exit_edit_mode: 'Saír da edición',
        table: {
          collaborators: 'Profesionais',
          today: 'Hoxe',
          select_shift: 'Seleccionar quenda',
        },
      },
      roles_and_permissions: {
        generic_header: {
          breadcrumbs: {
            team: 'Equipo',
            roles_and_permissions: 'Postos e permisos',
          },
          subtitle: 'Engadir roles e xestionar permisos',
        },
        create: 'Engadir',
        edit: 'Editar',
        delete: 'Eliminar',
        default_role: 'Configuración estándar',
        confirm_delete: {
          title: 'Eliminar posto: {{ name }}',
          copy: {
            title: 'Seguro que queres eliminar o posto?',
            subtitle: 'Esta acción non se pode reverter.',
          },
          snackbar: {
            error_message: 'Produciuse un erro ao eliminar o posto.',
          },
        },
      },
      occurrences: {
        empty_title: 'Aínda non existen incidentes rexistrados',
        empty_subtitle: 'Preme en engadir para rexistrar un incidente',
        table_header_date: 'Data',
        table_header_time: 'Hora',
        table_header_type: 'Tipo',
        table_header_patient: 'Usuario',
        table_header_responsible: 'Persoa responsable',
        table_header_registered: 'Rexistrado por',
        table_header_status: 'Estado',
        table_header_knowledge: 'Coñecemento',
        add_occurrence: 'Engadir incidente',
        type_placeholder: 'Selecciona un tipo de incidente',
        local_placeholder: 'Escribe a localización do incidente',
        patient_placeholder: 'Selecciona un usuario',
        responsible_placeholder: 'Escolle unha persoa responsable',
        details_placeholder: 'Introduce os detalles do incidente',
        status: {
          registered: 'Rexistrado',
          not_handled: 'Para ser tratado',
          handled: 'Procesado',
        },
        details: 'Detalles',
        information: 'Información do incidente',
        date_hour: 'Data e hora',
        acknowledge: 'Recoñecemento',
        add_notes: 'Engadir notas',
        write_notes: 'Introducir unha nota',
        acknowledge_success: 'Recoñeciches o incidente.',
        acknowledge_error: 'No se puido recoñecer o incidente.',
        handle_success: 'O incidente xestionouse con éxito!',
        handle_error: 'Non foi posible xestionar o incidente.',
        responsible: 'Persoa responsable',
        registered_by: 'Rexistrado por',
        took_acknowledge: 'Recoñecíchelo',
        history: {
          acknowledge: 'Recoñeciches o incidente',
          state_update: 'Modificouse o estado do incidente',
        },
        notes_success: 'As notas foron engadíronse con éxito.',
        notes_error: 'Non se puideron engadir as notas.',
      },
    },
    system_administrator: {
      tabs: {
        statistics: 'Estatísticas',
        activity: 'Actividade',
        devices: 'Dispositivos',
        apiDocs: 'Doc. API',
        applications: 'Aplicacións',
        entities: 'Organizacións',
        cards: 'Tarxetas',
        licenses: 'Licenzas',
        seniors: 'Maiores',
        caregivers: 'Coidadores',
        playlists: 'Listas de reprodución',
        gdprErasureRequests: 'Dereito ao esquecemento (RXPD)',
      },
    },
    partner: {
      tabs: {
        home: 'Comezar',
        apiDocs: 'Doc. API',
      },
    },
    legacy_integration: {
      entity_services_disabled_alert_title: 'A acción non se puido levar a cabo.',
      entity_services_disabled_alert_message:
        'Sentímolo, non tes acceso a esta área. Ponte en contacto co servizo de atención ao cliente para obter máis información.',
    },
    none: {
      tabs: {
        home: 'Comezar',
      },
    },
    permissions: {
      access_denied_message: 'Sentímolo, non tes permisos para acceder a esta páxina.',
    },
    access: {
      denied_message_first: 'Para acceder a esta área debes ter o ',
      denied_message_second: ' módulo de Sioslife.',
      care_management: 'Xestión de coidados',
      digital_inclusion: 'Inclusión dixital',
      speak: 'Fala con nós!',
      tooltip:
        'Para acceder a esta opción, debes ter o módulo Sioslife <strong>{{ module }}</strong>.',
    },
    pending_tasks: {
      alert:
        'Atención!<br>Identificamos {{number}} tarefas que xa se completaron e están pendentes de validación.',
      see_tasks: 'Ver tarefas',
      header_title: 'Tarefas pendentes',
      info: 'As tarefas presentadas complétanse automaticamente cada vez que os usuarios empregan o sistema interactivo de Sioslife.<br>O rexistro só se considerará efectivo unha vez que se validen as tarefas.',
      empty: 'Non existen rexistros pendentes.',
      all: 'Seleccionar todo',
      validate: 'Validar tarefas',
      modal_title: 'Seguro que queres validar {{number}} tarefas?',
      modal_observation: 'Introducir unha observación',
      modal_validate: 'Validar',
      snackbar_success: 'Todas as tarefas se validaron con éxito!',
      snackbar_error: 'Non foi posible validar as tarefas.',
      modal_task_validation_title:
        'Atención!<br>Identificamos que esta tarefa xa se completou e está pendente de validación.',
    },
  },
  seniors: {
    root: {
      title: 'Persoas maiores',
      add_button: 'Engadir',
      search_input_placeholder: 'Buscar persoas maiores',
      name_header: 'Nome',
      gender_header: 'Xénero',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      no_seniors: 'Aínda non existen persoas maiores rexistradas',
      deleted_senior_dialog_title: 'Perfil de persoa maior, eliminouse “{{name}}”',
      deleted_senior_dialog_message:
        'O perfil de persoa maior xa non existe na lista de persoas maiores.',
      deleted_senior_dialog_ok_button: 'Vale',
      senior: 'Persoa maior',
      seniors: 'Persoas maiores',
      modals: {
        add_senior_flow: {
          form: {
            title: 'Engadir Persoa maior',
            cancel: 'Cancelar',
            add: 'Engadir',
            inputs: {
              active: 'Activo',
              name: {
                label: 'Nome completo',
                placeholder: 'Nome e apelidos',
                required_message: 'Introduce un nome e apelidos',
                pattern_message: 'O nome introducido non é válido',
                max_message: 'O nome introducido non debe superar os {{ characters }} caracteres',
              },
              name_alias: {
                label: 'Como che gusta que te chamen',
                placeholder: 'Nome',
                required_message: 'Introduce un nome',
                pattern_message: 'O nome introducido non é válido',
                max_message: 'O nome introducido non debe superar os {{ characters }} caracteres',
              },
              birthdate: {
                label: 'Data de nacemento',
                placeholder: 'Selecciona unha data de nacemento',
                required_message: 'Selecciona unha data de nacemento',
              },
            },
          },
          confirm: {
            title: 'Engadir Persoa maior',
            confirm_title: 'Seguro que queres engadir a “{{ name }}”?',
            confirm_subtitle: 'A nova persoa maior engadirase ao sistema.',
            cancel: 'Cancelar',
            add: 'Engadir',
          },
          snackbar: {
            success_message: 'Creouse a persoa maior “{{name}}” con éxito!',
            error_message: 'Produciuse un erro ao gardar',
          },
        },
      },
    },
    edit: {
      senior_dcm: {
        careplan: {
          load_error: 'Non foi posible cargar os datos do plan de coidados.',
          caregiver: {
            title: 'Persoa responsable',
            unnasigned: 'Sen definir',
            unnasigned_tooltip: 'Non tes permisos para editar a persoa responsable',
          },
          activities: {
            add_button: 'Engadir',
            load_error: 'Non foi posible cargar grupos de actividades para engadir.',
            empty_state: {
              title: 'Aínda non existen actividades rexistradas.',
              subtitle: 'Preme en engadir para rexistrar unha actividade.',
            },
          },
          on_save: {
            snackbar: {
              success_message: 'Plan de coidados gardado',
              error_message: 'Non foi posible gardar o plan de coidados.',
            },
          },
          on_update: {
            snackbar: {
              success_message: 'Plan de coidados actualizado',
              error_message: 'Non foi posible actualizar o plan de coidados.',
            },
          },
          on_delete: {
            title: 'Seguro que queres eliminar o plan de coidados para o usuario “{{name}}”?',
            description: 'Esta acción eliminará o plan de coidados de forma permanente.',
            snackbar: {
              success_message: 'Plan de coidados eliminado',
              error_message: 'Non foi posible eliminar o plan de coidados.',
            },
          },
        },
        individual_plan: {
          add_cares: 'Engadir coidado',
          caregiver: {
            title: 'Persoa responsable',
            coordinator: 'Coordinador',
            unassigned: 'Sen definir',
            unassigned_tooltip: 'Non tes permisos para editar a persoa responsable',
          },
          observations: 'Notas',
          created_at: 'Creouse o',
          valid_until: 'Ten validez ata o',
          not_defined: 'Sen definir',
          empty_state: {
            title: 'Aínda non existen diagnósticos rexistrados no plan individual.',
            subtitle: 'Preme para engadir un diagnóstico.',
          },
          new_diagnostic: 'Novo diagnóstico',
          diagnostic: 'Diagnóstico',
          goal: 'Obxectivo xeral',
          new_goal: 'Novo obxectivo xeral',
          new_objective: 'Novo obxectivo específico',
          history: {
            action: 'Historial',
            title: 'Historial do plan individual',
            close: 'Pechar',
            version: 'Versión {{revision}}',
            unknown_author: 'Usuario descoñecido',
            load_error: 'Non se puido cargar o historial.',
            source: {
              manual: 'Manual',
              ai: 'IA',
              restore: 'Restauración',
            },
            summary: {
              entity: {
                plan: 'Plan',
                diagnostic: 'Diagnóstico',
                goal: 'Obxectivo xeral',
                objective: 'Obxectivo específico',
              },
              entity_plural: {
                plan: 'plans',
                diagnostic: 'diagnósticos',
                goal: 'obxectivos xerais',
                objective: 'obxectivos específicos',
              },
              field: {
                definition: 'definición',
                responsibleUserId: 'responsable',
                cares: 'coidados',
                activityTypesIds: 'actividades asociadas',
                indicators: 'indicadores',
                isActive: 'estado',
                coordinatorUserId: 'coordinador',
                validUntil: 'validez',
                observations: 'notas',
                diagnosticsOrder: 'orde dos diagnósticos',
              },
              created: '{{entity}} creado: "{{name}}"',
              created_unnamed: '{{entity}} creado',
              created_many: '{{count}} {{entities}} creados',
              deleted: '{{entity}} eliminado: "{{name}}"',
              deleted_unnamed: '{{entity}} eliminado',
              deleted_many: '{{count}} {{entities}} eliminados',
              updated: 'Cambio en {{entity}}: {{field}}',
              updated_many: 'Cambio en {{count}} {{entities}}: {{field}}',
              more: 'e {{count}} cambios máis',
              restored_from: 'Restaurado desde a versión {{revision}}',
            },
            restore: {
              button: 'Restaurar esta versión',
              prompt_title: 'Restaurar a versión {{revision}}?',
              prompt_description:
                'O plan individual pasará a ter o contido desta versión. Créase unha nova versión e non se elimina ningunha versión anterior, polo que podes volver atrás en calquera momento.',
              confirm: 'Restaurar',
              success: 'Versión {{revision}} restaurada.',
              conflict: 'O plan modificouse entrementres. Actualizouse, téntao de novo.',
              error: 'Non se puido restaurar a versión.',
            },
            view_version: 'Ver versión',
            back: 'Volver',
            detail_load_error: 'Non se puido cargar esta versión.',
            no_diagnostics: 'Esta versión non ten diagnósticos.',
            detail_note: 'Estado do plan nesta versión (só lectura).',
            change_type_unknown: 'Cambio',
            change_type: {
              baseline: 'Estado inicial (antes do historial)',
              'plan-created': 'Plan creado',
              'plan-updated': 'Plan actualizado',
              'plan-archived': 'Plan arquivado',
              'plan-reactivated': 'Plan reactivado',
              'diagnostic-created': 'Diagnóstico creado',
              'diagnostic-updated': 'Diagnóstico actualizado',
              'diagnostic-deleted': 'Diagnóstico eliminado',
              'goal-created': 'Obxectivo xeral creado',
              'goal-updated': 'Obxectivo xeral actualizado',
              'goal-deleted': 'Obxectivo xeral eliminado',
              'objective-created': 'Obxectivo específico creado',
              'objective-updated': 'Obxectivo específico actualizado',
              'objective-deleted': 'Obxectivo específico eliminado',
              'bulk-append': 'Diagnósticos engadidos en bloque',
              'bulk-replace': 'Diagnósticos substituídos en bloque',
              restored: 'Versión restaurada',
            },
          },
          ai_generate: {
            title: 'Xerar plan con IA',
            description:
              'Indica o tema clínico que queres priorizar ou deixa que a IA decida co plan automático. O texto só indica o tema, non son instrucións para a IA.',
            focus_label: 'Foco do plan',
            focus_placeholder:
              'Ex.: plan centrado nas actividades cognitivas para reducir o deterioro mental',
            automatic: 'Plan automático',
            with_focus: 'Xerar con foco',
          },
          ai_proposal: {
            title: 'Proposta da IA',
            subtitle: 'Esta é unha vista previa. Non se garda nada no plan individual ata aprobar.',
            discard: 'Descartar',
            approve: 'Aprobar',
            approved: 'Proposta da IA engadida ao plan individual.',
            replaced:
              'Plan individual substituído pola proposta da IA. A versión anterior quedou gardada no historial.',
            apply_prompt: {
              title: 'Queres borrar os datos do plan individual existente?',
              description:
                'O plan individual xa ten diagnósticos. Podes borralos e quedar só coa proposta da IA, ou engadir a proposta aos diagnósticos existentes. Se os borras, a versión anterior queda gardada no historial do plan.',
              replace: 'Borrar plan',
              append: 'Engadir ao plan',
            },
            rate_limited:
              'Acadaches o límite de xeracións con IA por hora. Téntao de novo dentro de {{minutes}} min.',
            save_error: 'Non se puido gardar a proposta no plan individual.',
            invalid_references:
              'Non se puido gardar: a proposta supera os límites permitidos ou ten referencias non válidas.',
            error: 'Non se puido xerar o plan con IA. Inténtao de novo.',
          },
          on_delete: {
            title: 'Seguro que queres arquivar o plan individual para o usuario “{{name}}”?',
            description: 'Esta acción arquivará o plan individual. Poderás consultalo máis tarde.',
            snackbar: {
              success_message: 'Plan individual arquivado',
              error_message: 'Non foi posible arquivar o plan individual.',
            },
          },
          add_diagnostic: {
            title: 'Engadir diagnóstico',
            edit: 'Editar diagnóstico',
            form: {
              definition: {
                label: 'Definición',
                placeholder: 'Breve descrición do diagnóstico',
                error_message: 'A definición debe ter entre 1 e {{max}} caracteres.',
              },
            },
          },
          on_diagnostic_remove: {
            header: 'Eliminar diagnóstico',
            title: 'Seguro que queres eliminar o diagnóstico de “{{name}}”?',
            description: 'Esta acción eliminará o diagnóstico permanentemente.',
          },
          add_diagnostic_goal: {
            title: 'Engadir obxectivo xeral',
            edit: 'Editar obxectivo xeral',
            form: {
              definition: {
                label: 'Definición',
                placeholder: 'Breve descrición do obxectivo xeral',
                error_message: 'A definición debe ter entre 1 e {{max}} caracteres.',
              },
              responsibleUser: {
                label: 'Persoa responsable',
                placeholder: 'Escolle unha persoa responsable',
              },
            },
          },
          on_diagnostic_goal_remove: {
            header: 'Eliminar o obxectivo xeral',
            title: 'Seguro que queres eliminar o obxectivo xeral para “{{name}}”?',
            description: 'Esta acción eliminará o obxectivo xeral de forma permanente.',
          },
          add_diagnostic_goal_objective: {
            title: 'Engadir obxectivo específico',
            edit: 'Editar obxectivo específico',
            form: {
              definition: {
                label: 'Definición',
                placeholder: 'Breve descrición do obxectivo específico',
                error_message: 'A definición debe ter entre 1 e {{max}} caracteres.',
              },
            },
            cares: {
              add_non_automatic: 'Engadir coidado non automático',
              non_automatic_title: 'Coidados non automáticos',
              max_reached: 'Pode engadir como máximo {{max}} coidados non automáticos.',
              too_long: 'O coidado non automático non pode ter máis de {{max}} caracteres.',
            },
            snackbar: {
              error: 'Non se puido gardar o obxectivo específico.',
              invalid_references:
                'Non se puido gardar: un dos coidados ou indicadores seleccionados non está dispoñible para esta entidade.',
            },
            table: {
              definition: 'Obxectivo específico',
              cares: 'Coidado',
              indicators: 'Indicadores',
              targets: 'Obxectivos',
              execution: 'Implementación',
            },
          },
          on_diagnostic_goal_objective_remove: {
            header: 'Eliminar obxectivo específico',
            title: 'Seguro que queres eliminar o obxectivo específico para “{{name}}”?',
            description: 'Esta acción eliminará o obxectivo específico de forma permanente.',
          },
        },
      },
    },
  },
  senior_tabs: {
    tabs: {
      profile: 'Perfil',
      caregivers: 'Coidadores',
      applications: 'Aplicacións',
      devices: 'Dispositivos',
      activity: 'Actividade',
      statistics: 'Estatísticas',
      user_cards: 'Tarxetas',
      health: 'Saúde',
      affiliations: 'Afiliacións',
      playlists: 'Listas de reprodución',
      external_accounts: 'Contas Externas',
    },
    errors: {
      senior_does_not_exist_error:
        'Non foi posible obter a persoa maior á que intentaches acceder.',
    },
  },
  health_profile_form: {
    clinical_conditions_title: 'Patoloxías/Limitacións',
    visual_difficulty_label: 'Dificultades visuais',
    hearing_difficulty_label: 'Dificultades auditivas',
    cognitive_deficit_label: 'Deterioro cognitivo',
    parkinson_label: 'Enfermidade de Parkinson',
    alzheimer_label: 'Enfermidade de Alzheimer',
    other_dementias_label: 'Outras demencias',
    reduced_mobility_upper_limbs_label: 'Diminución da mobilidade dos membros superiores',
    reduced_mobility_lower_limbs_label: 'Diminución da mobilidade dos membros inferiores',
    paralysis_left_limb_label: 'Parálise do membro superior esquerdo',
    paralysis_right_limb_label: 'Parálise do membro superior dereito',
    wheelchair_label: 'Cadeira de rodas',
    illiterate_label: 'Analfabetismo',
    disable_clinical_conditions_tooltip: 'Non tes permisos para condicións clínicas',
    errors: {
      update_health_profile_error_message:
        'Produciuse un erro. Non foi posible actualizar o perfil de saúde.',
    },
    noHealthMeasurements: 'Aínda non existen medidas sanitarias.',
  },
  health_charts: {
    noData: 'Non existen datos na actualidade.',
    heartRate: {
      subtitle1: 'normal ata 120 bpm',
    },
    bloodOxygen: {
      subtitle1: 'mínimo 50 %',
      subtitle2: 'máximo 100 %',
    },
    bloodPressure: {
      subtitle1: 'Sistólica: ata 120 mmHg',
      subtitle2: 'Diastólica: ata 80 mmHg',
    },
    bloodGlucose: {
      subtitle1: 'mínimo 80 mg/dL',
      subtitle2: 'máximo 180 mg/dL',
    },
    bodyTemperature: {
      subtitle1: 'normal ata 36 ºC',
    },
    bodyWeight: {
      subtitle1: undefined,
    },
    errors: {
      fetch: 'Produciuse un erro ao cargar os datos de saúde.',
    },
  },
  health_measurements: {
    title: 'Últimas Medidas',
    units: {
      beatsPerMinute: 'bpm',
      breathsPerMinute: 'rpm',
    },
  },
  senior_caregivers_listing: {
    invite_caregiver_button: 'Convidar coidador',
    invites_history_button: 'Ver invitacións',
    no_caregivers: 'Non existen coidadores.',
    name_header: 'Nome',
    relationship_type_header: 'Tipo',
    remove_relationship_header: 'Eliminar relación',
    remove_relationship_dialog_title:
      'Seguro que queres eliminar a relación entre a persoa maior {{senior}} e o coidador {{caregiver}}?',
    remove_relationship_dialog_message:
      'Ao eliminar esta relación, a persoa maior {{senior}} xa non poderá comunicarse co coidador {{caregiver}}.',
    remove_relationship_button: 'Eliminar',
    remove_relationship_button_tooltip: 'Non tes permisos para eliminar a relación',
    remove_relationship_error_message:
      'Non foi posible eliminar a relación entre a persoa maior {{senior}} e o coidador {{caregiver}}.',
    remove_relationship_success_message:
      'A relación entre a persoa maior {{senior}} e o coidador {{caregiver}} eliminouse foi con éxito.',
  },
  senior_relationships_invites_history: {
    title: 'Invitacións',
    no_invites: 'Non existen invitacións.',
    invite_caregiver_button: 'Convidar coidador',
    process_closed: 'Pechado',
    cancel_button: 'Cancelar',
    name_header: 'Nome',
    contact_header: 'Contacto',
    state_header: 'Estado da invitación',
    status_header: 'Procesado',
    cancel_invite_dialog_title:
      'Seguro que queres cancelar a invitación para que {{caregiver}} sexa coidador de {{senior}}?',
    cancel_invite_dialog_message:
      'Ao cancelar esta invitación, o coidador {{caregiver}} non poderá aceptar a invitación para comunicarse coa persoa maior {{senior}}.',
    cancel_invite_dialog_action_yes_button: 'Cancelar invitación',
    cancel_invite_dialog_action_no_button: 'Gardar invitación',
    cancel_invite_error_message:
      'Non foi posible cancelar a invitación a {{caregiver}} para ser coidador de {{senior}}',
    cancel_invite_success_message:
      'A invitación a {{caregiver}} para ser coidador de {{senior}} cancelouse.',
  },
  informal_caregiver_invite_form: {
    title: 'Convidar coidador',
    name_input: 'Nome',
    contact_method_input: 'Método de contacto',
    email_input: 'Enderezo de correo electrónico',
    mobile_phone_number_input: 'Número de teléfono móbil',
    save_button: 'Enviar invitación',
    saving_button: 'Enviando...',
    save_dialog_title: 'Enviar invitación a {{name}}?',
    save_dialog_message:
      'Ao enviala, {{name}} recibirá unha invitación para poñerse en contacto e ter acceso a algunha información de {{senior}}.',
    save_dialog_action_save_button: 'Enviar',
    router_prompt_title: 'Seguro que queres saír sen enviar a invitación?',
    router_prompt_message: 'Se saes, perderás toda a información que introduciches na invitación.',
    cancel_toast: 'Cancelouse a invitación.',
    success_message: 'A invitación enviouse a {{name}}.',
    errors: {
      form_validation_invalid_field: 'Cubre este campo para completar a invitación',
      form_validation_invalid_mobile_phone_number:
        'O número de teléfono móbil indicado non é válido',
      form_validation_not_mobile_phone_number:
        'O número indicado non corresponde a un número de teléfono móbil',
      form_validation_invalid_email: 'O enderezo de correo electrónico proporcionado non é válido',
      error_message: 'Non se puido enviar a invitación a {{name}}.',
    },
  },
  devices_associations: {
    identifier_header: 'Identificador',
    type_header: 'Tipo',
    state_header: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    remove_device_header: 'Eliminar un dispositivo',
    no_devices: 'Non existen dispositivos.',
    associate_device_button: 'Vincular dispositivo',
    remove_device_dialog_title:
      'Seguro que queres eliminar o vínculo entre o dispositivo co ID “{{identifier}}” e “{{name}}”?',
    remove_device_dialog_message:
      'Ao eliminar a ligazón, {{name}} xa non poderá usar o dispositivo para facer uso do sistema de {{brand}}.',
    remove_device_button: 'Eliminar',
    remove_device_button_disabled_tooltip: 'Non tes permisos para eliminar o dispositivo',
    remove_device_error_message:
      'Non foi posible eliminar o vínculo co dispositivo “{{identifier}}”.',
    remove_device_success_message:
      'O vínculo co dispositivo “{{identifier}}” eliminouse correctamente.',
  },
  user_cards_associations: {
    name_header: 'Nome',
    state_header: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    remove_card_header: 'Eliminar tarxeta',
    associate_card_button: 'Vincular tarxeta',
    no_cards: 'Non existen tarxetas.',
    remove_card_dialog_title:
      'Seguro que queres eliminar o vínculo entre a tarxeta “{{card}}” e {{name}}?',
    remove_card_dialog_message:
      'Ao eliminar o vínculo, {{name}} xa non poderá usar a tarxeta para acceder ao sistema de {{brand}}.',
    remove_card_button: 'Eliminar',
    remove_card_button_disabled_tooltip: 'Non tes permisos para eliminar a tarxeta',
    remove_card_error_message: 'Non foi posible eliminar o vínculo coa tarxeta “{{identifier}}”.',
    remove_card_success_message: 'O vínculo coa tarxeta “{{identifier}}” eliminouse con éxito.',
  },
  associate_user_card_form: {
    title: 'Vincular tarxeta',
    card_name_input: 'Nome da tarxeta',
    entity_input: 'Organización',
    card_name_info_message: 'Este campo só se pode cubrir se xa se seleccionou unha organización.',
    save_button: 'Gardar',
    saving_button: 'Gardando...',
    save_dialog_title: 'Vincular a tarxeta “{{card}}” con {{name}}?',
    save_dialog_message: 'Ao vincular a tarxeta, {{name}} poderá acceder ao sistema de {{brand}}.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Vincular',
    router_prompt_title: 'Seguro que queres saír sen vincular a tarxeta?',
    router_prompt_message: 'Se saes, perderás toda a información que introduciches.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Si, pechar sesión',
    cancel_toast: 'Cancelouse a ligazón.',
    no_affiliations_info_message:
      'O usuario non ten afiliación con ningunha organización. Polo tanto, non é posible asociar as tarxetas co usuario.',
    success: {
      save_success_button: 'Gardado!',
      success_message: 'A tarxeta vinculouse a {{name}}.',
    },
    errors: {
      error_message: 'Non foi posible vincular a tarxeta a {{name}}.',
      error_on_save_button: 'Produciuse un erro ao gardar',
      form_validation_invalid_field: 'Cubre este campo para completar a ligazón da tarxeta',
    },
  },
  associate_device_form: {
    title: 'Vincular dispositivo',
    identifier_input: 'Identificador do dispositivo',
    entity_input: 'Organización',
    save_button: 'Gardar',
    saving_button: 'Gardando...',
    save_dialog_title: 'Queres vincular o dispositivo coa identificación “{{device}}” a {{name}}?',
    save_dialog_message:
      'Ao vincular o dispositivo, {{name}} poderá acceder ao sistema de {{brand}}.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Vincular',
    router_prompt_title: 'Seguro que queres saír sen vincular o dispositivo?',
    router_prompt_message: 'Se saes, perderás toda a información que introduciches.',
    cancel_toast: 'Cancelouse a ligazón.',
    no_affiliations_info_message:
      'O usuario non ten afiliación con ningunha organización. Polo tanto, non é posible vincular dispositivos co usuario.',
    success: {
      save_success_button: 'Gardado!',
      success_message: 'O dispositivo vinculouse con {{name}}.',
    },
    errors: {
      error_message: 'Non foi posible de vincular o dispositivo con {{name}}.',
      error_on_save_button: 'Produciuse un erro ao gardar',
      form_validation_invalid_field: 'Cubre este campo para completar a vinculación do dispositivo',
    },
  },
  external_accounts: {
    types: {
      call: 'Voximplant',
      partner_p3_n1: 'Hope Care',
      partner_p3_n2: 'Altice SmartAL',
      partner_p3_n3: 'DeepClick',
      partner_p3_n4: 'Itelcare (Kerox)',
    },
    list: {
      type_header: 'Tipo',
      delete_account_header: 'Eliminar',
      delete_account_button: 'Eliminar',
      associate_account_button: 'Engadir conta',
      no_accounts: 'Non existen contas externas.',
      error_message: 'Non foi posible obter as contas externas.',
      delete_account_dialog_title: 'Eliminar a conta {{type}}?',
      delete_account_dialog_description: 'Estás seguro de que queres eliminar esta conta externa?',
      delete_account_dialog_cannot_be_undone: 'Esta acción non se pode desfacer.',
      delete_account_dialog_confirm_button: 'Eliminar',
      delete_account_dialog_cancel_button: 'Cancelar',
      delete_account_success_message: 'A conta {{type}} elimináronse correctamente.',
      delete_account_error_message: 'Non foi posible eliminar a conta {{type}}.',
    },
    associate: {
      title: 'Engadir conta externa',
      type_input: 'Tipo de conta',
      save_button: 'Crear',
      saving_button: 'Creando...',
      save_dialog_title: 'Crear a conta {{type}}?',
      save_dialog_message: 'Estás seguro de que queres crear esta conta externa?',
      save_dialog_action_cancel_button: 'Cancelar',
      save_dialog_action_save_button: 'Crear',
      no_available_types_message:
        'Xa existe unha conta externa de todos os tipos dispoñibles para este usuario.',
      success_message: 'A conta {{type}} creouse correctamente.',
      errors: {
        generic_message: 'Non foi posible crear a conta externa.',
        conflict_message: 'Xa existe unha conta externa deste tipo para este usuario.',
        provider_error_message:
          'O provedor externo fallou ao crear a conta. Téntao de novo máis tarde.',
        error_on_save_button: 'Erro',
      },
    },
    details: {
      delete_button: 'Eliminar conta',
      fields: {
        password: 'Contrasinal',
        application_name: 'Nome da aplicación',
        username: 'Usuario',
        language: 'Idioma',
        project_identification: 'Identificación do proxecto',
        serial: 'Número de serie',
        profile: 'Perfil',
        timezone: 'Fuso horario',
        institution_id: 'ID da institución',
        instance_name: 'Nome da instancia',
        manufacturer: 'Fabricante',
        model: 'Modelo',
        user_id: 'ID do usuario',
        patient_id: 'ID do paciente',
        email: 'Correo electrónico',
      },
      errors: {
        not_found_message: 'Non foi posible obter a conta externa.',
      },
    },
  },
  caregivers: {
    root: {
      title: 'Coidadores',
      search_input_placeholder: 'Buscar coidadores',
      name_header: 'Nome',
      email_header: 'Correo electrónico',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      no_caregivers: 'Non existen coidadores.',
    },
  },
  professional_caregivers: {
    root: {
      title: 'Profesionais',
      invite_button: 'Convidar a un profesional',
      search_input_placeholder: 'Buscar profesionais',
      name_header: 'Nome',
      email_header: 'Correo electrónico',
      affiliation_type_header: 'Tipo',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      no_professional_caregivers: 'Non existen profesionais.',
      deleted_professional_caregiver_dialog_title: 'Perfil profesional {{name}} eliminado',
      deleted_professional_caregiver_dialog_message:
        'O perfil profesional xa non existe na lista de profesionais.',
      deleted_professional_caregiver_dialog_ok_button: 'Vale',
      colaborator: 'profesional',
      colaborators: 'profesionais',
      modals: {
        add_staff_flow: {
          form: {
            title: 'Engadir profesional',
            cancel: 'Cancelar',
            send_invite: 'Enviar invitación',
            create_profile: 'Engadir perfil',
            inputs: {
              name: {
                label: 'Nome',
                placeholder: 'Nome e apelidos',
                required_message: 'Introduce o seu nome completo',
                pattern_message: 'O nome introducido non é válido',
                max_message: 'O nome introducido non debe superar os {{ characters }} caracteres',
              },
              email: {
                label: 'Correo electrónico',
                placeholder: 'Introduce un correo electrónico',
                required_message: 'O campo de correo electrónico é obrigatorio',
                pattern_message: 'O campo “Correo electrónico” non é válido',
              },
              roles: {
                label: 'Permisos',
                placeholder: 'Seleccionar permisos',
              },
              invitation_type: {
                label: 'Como creo un perfil?',
                placeholder: 'Selecciona unha opción',
                required_message: 'Debes seleccionar unha opción para enviar',
                via_email: 'Convidar por enderezo de correo electrónico',
                via_sms: 'Convidar por mensaxe SMS',
                via_quickAccessCode: 'Engadir cun código de acceso rápido',
              },
              phone_number: {
                label: 'Número de teléfono móbil',
                placeholder: 'Introduce un número de teléfono móbil',
                required_message: 'O campo do número de teléfono móbil é obrigatorio',
                is_valid_message: 'O número de móbil/teléfono non é válido ',
              },
              quick_access_code: {
                label: 'Código de acceso rápido',
                placeholder: 'Introduce un código de acceso rápido de 4 díxitos',
                required_message: 'O campo do código de acceso rápido é obrigatorio',
                is_valid_message: 'O código de acceso rápido non é válido',
              },
            },
          },
          confirm: {
            title: 'Enviar invitación',
            title_quickAccessCode: 'Engadir perfil',
            confirm_title: 'Seguro que queres enviarlle unha invitación a {{name}}?',
            confirm_title_quickAccessCode: 'Seguro que queres engadir un perfil para {{name}}?',
            confirm_subtitle: '{{name}} terá acceso a algunha información sobre {{institution}}.',
            cancel: 'Cancelar',
            send_invite: 'Enviar invitación',
            create_profile: 'Engadir perfil',
          },
          success: {
            title: 'Enviar invitación',
            title_quickAccessCode: 'Engadir perfil',
            confirm_title: 'Enviouse a invitación!',
            confirm_title_quickAccessCode: 'Creouse o perfil!',
            confirm_subtitle:
              'O profesional engadirase completamente ao equipo despois de confirmar a invitación.',
            confirm_subtitle_quickAccessCode:
              'O profesional engadiuse completamente ao equipo e debería cambiar o seu código de acceso rápido canto antes.',
            confirm: 'Pechar',
          },
          snackbar: {
            success_message: 'Enviouse unha invitación a {{name}}.',
            error_message: 'Erro ao enviar a invitación',
            success_message_create_profile: 'Creouse o perfil para {{name}}.',
            error_message_create_profile: 'Produciuse un erro ao engadir o perfil',
          },
        },
      },
    },
  },
  caregiver_tabs: {
    tabs: {
      profile: 'Perfil',
      affiliations: 'Afiliacións',
      applications: 'Aplicacións',
      user_cards: 'Tarxetas',
      playlists: 'Listas de reprodución',
    },
    errors: {
      caregiver_does_not_exist_error: 'O coidador que tentaches contactar non se pode contactar.',
    },
  },
  user_affiliations: {
    entity_name_header: 'Nome da organización',
    entity_type_header: 'Tipo de organización',
    affiliation_type_header: 'Tipo de afiliación',
    remove_affiliation_header: 'Eliminar afiliación',
    remove_affiliation_button: 'Eliminar',
    remove_affiliation_dialog_title:
      'Queres eliminar a afiliación entre o usuario {{user}} e a organización {{entity}}?',
    remove_affiliation_dialog_message:
      'Ao eliminar esta afiliación, o usuario {{user}} xa non terá acceso á organización {{entity}}.',
    remove_affiliation_dialog_action_yes_button: 'Si, eliminar',
    remove_affiliation_dialog_action_no_button: 'Cancelar',
    update_affiliation_dialog_title:
      'Cambiar o tipo de afiliación entre o usuario {{user}} e a organización {{entity}}?',
    update_affiliation_dialog_message:
      'Ao cambiar isto, o tipo de afiliación do usuario actualizarase a “{{type}}” na organización {{entity}}.',
    update_affiliation_dialog_action_yes_button: 'Si, cambiar',
    update_affiliation_dialog_action_no_button: 'Cancelar',
    no_affiliations: 'Non existen afiliacións.',
    errors: {
      remove_affiliation_error_message: 'Produciuse un erro. Non foi posible eliminar a filiación.',
      update_affiliation_error_message:
        'Produciuse un erro. Non foi posible cambiar o tipo de afiliación.',
    },
  },
  user_applications: {
    entity_filter: 'Organización',
    search_input_placeholder: 'Buscar aplicacións',
    name_header: 'Nome',
    category_header: 'Categoría',
    state_header: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    disabled_state_tooltip: 'Non tes permisos para cambiar o estado da aplicación',
    no_applications: 'Non existen afiliacións.',
    enable_application_dialog_title: 'Seguro que queres activar a aplicación para {{name}}?',
    enable_application_dialog_message:
      'Cando actives a aplicación, estará dispoñible para {{name}}.',
    enable_application_for_entity_dialog_message:
      'Cando actives a aplicación, estará dispoñible para {{name}} na organización {{entity}}.',
    enable_application_dialog_action_yes_button: 'Si, activar',
    enable_application_dialog_action_no_button: 'Cancelar',
    disable_application_dialog_title: 'Seguro que queres desactivar a aplicación para {{name}}?',
    disable_application_dialog_message:
      'Ao desactivar a aplicación, xa non estará dispoñible para {{name}}.',
    disable_application_for_entity_dialog_message:
      'Ao desactivar a aplicación, xa non estará dispoñible para {{name}} na organización {{entity}}.',
    disable_application_dialog_action_yes_button: 'Si, desactivar',
    disable_application_dialog_action_no_button: 'Cancelar',
    no_affiliations_info_message:
      'O usuario non ten afiliación con ningunha organización. Polo tanto, non é posible consultar as aplicacións do usuario.',
    errors: {
      enable_application_error_message: 'Produciuse un erro. Non foi posible activar a aplicación.',
      disable_application_error_message:
        'Produciuse un erro. Non foi posible desactivar a aplicación.',
      update_state_application_error_message:
        'Produciuse un erro. Non se puido actualizar o estado da aplicación.',
    },
  },
  user_form: {
    title_create_senior: 'Engadir persoa maior',
    title_current_user_profile: 'Perfil',
    delete_button: 'Eliminar este perfil',
    delete_caregiver_dialog_title: 'Eliminar o perfil do coidador para {{name}}?',
    delete_caregiver_dialog_content:
      'Ao eliminar este perfil, eliminarase permanentemente da lista de coidadores e eliminarase a afiliación de {{name}} coa organización {{entity}}.',
    delete_senior_dialog_title:
      'Seguro que queres eliminar a “{{name}}” da lista de persoas maiores?',
    delete_senior_dialog_content: 'A persoa maior marcarase como inactiva.',
    delete_caregiver_invite_title: 'Cancelar a invitación para {{name}}?',
    delete_caregiver_invite_content: 'Se cancelas, {{name}} non poderá aceptar a invitación.',
    entity_input: 'Organización',
    full_name_input: 'Nome completo',
    display_name_input: 'Como che gusta que te chamen?',
    state_input: 'Estado: {{ role }} ',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    gender_input: 'Xénero',
    gender_placeholder_input: 'Selecciona un xénero',
    birthday_input: 'Data de nacemento',
    date_placeholder_input: 'Escolle unha data',
    admission_date_input: 'Data de admisión',
    departure_date_input: 'Data de saída',
    address_input: 'Enderezo',
    address_placeholder_input: 'Introduce o teu enderezo',
    religion_input: 'Relixión',
    religion_placeholder_input: 'Selecciona unha rexión',
    profession_input: 'Ámbito profesional',
    profession_description_input: 'Profesión/Ocupación',
    social_network_city_input: 'Ubicación social',
    language_input: 'Idioma',
    language_placeholder_input: 'Selecciona un idioma',
    region_input: 'Rexión',
    region_placeholder_input: 'Selecciona unha rexión',
    input_disabled_by_unknown_region: 'Debes engadir a “Rexión” para editar este campo',
    email_input: 'Enderezo de correo electrónico',
    email_placeholder_input: 'Engadir correo electrónico',
    mobile_phone_number_input: 'Número de teléfono móbil',
    mobile_phone_number_placeholder_input: 'Engadir un número de teléfono móbil',
    mobile_and_landline_phone_number_input: 'Número de teléfono móbil',
    tax_payer_number_input: 'Número de identificación fiscal (NIF)',
    tax_payer_number_input_placeholder: 'Engadir número de identificación fiscal',
    patient_number_input: 'Número de usuario (SNS)',
    patient_number_input_placeholder: 'Engadir o número de usuario',
    social_security_number_input: 'Número da Seguridade Social (NISS)',
    social_security_number_input_placeholder: 'Engadir o número da Seguridade Social',
    room_input: 'Dormitorio',
    room_placeholder_input: 'O número de habitación onde se aloxa a persoa maior',
    social_responses_input: 'Servizos sociais',
    social_responses_placeholder_input: 'Selecciona os servizos sociais da túa organización',
    profile_picture_input: 'Foto de perfil',
    process_number_input: 'Número de caso',
    process_number_input_placeholder: 'Engadir o número de caso',
    recurring_fee_input: 'Pago mensual',
    recurring_fee_input_placeholder: '500',
    marital_status_input: 'Estado civil',
    marital_status_input_placeholder: 'Seleccionar estado civil',
    aditional_info_input: 'Outra información',
    additional_info_input_placeholder: 'Introducir...',
    education_level_input: 'Nivel de educación',
    education_level_input_placeholder: 'Selecciona o nivel de educación',
    citizen_card_input: 'Número de cédula de cidadanía (CC)',
    citizen_card_input_placeholder: 'Engadir número de cédula de cidadanía',
    cancel_button: 'Cancelar',
    save_button: 'Gardar',
    saving_button: 'Gardando...',
    deleting_button: 'Eliminando...',
    create_senior_dialog_title: 'Gardar a creación da persoa maior “{{name}}”?',
    create_senior_dialog_message:
      'Ao gardar, a nova persoa maior engadirase á lista de persoas maiores.',
    edit_senior_dialog_title: 'Gardar os cambios no perfil da persoa maior para “{{name}}”?',
    edit_senior_dialog_message:
      'Ao gardar, a persoa maior actualizarase coa información introducida recentemente.',
    edit_caregiver_dialog_title: 'Gardar os cambios no perfil do coidador para “{{name}}”?',
    edit_caregiver_dialog_message:
      'Ao gardar, o coidador actualizarase coa información introducida recentemente.',
    edit_caregiver_affiliation_type_dialog_title:
      'Gardar os cambios no tipo de afiliación para o coidador “{{name}}”?',
    edit_caregiver_affiliation_type_dialog_message:
      'Unha vez gardado, o tipo de afiliación do coidador actualizarase coa información introducida recentemente.',
    edit_current_user_profile_dialog_title: 'Queres gardar os cambios no teu perfil?',
    edit_current_user_profile_dialog_message:
      'Ao gardar, o teu perfil actualizarase coa información introducida recentemente.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Gardar',
    senior_router_prompt_edit_title:
      'Seguro que queres saír sen gardar os cambios no perfil de persoa maior de “{{name}}”?',
    senior_router_prompt_create_title:
      'Seguro que queres saír sen gardar a creación da persoa maior “{{name}}”?',
    senior_router_prompt_create_message:
      'Ao pechar sesión, a persoa maior non se gardará e toda a información introducida será eliminada.',
    senior_router_prompt_edit_message:
      'Ao pechar sesión, perderás todos os cambios que fixeches recentemente no perfil da persoa maior.',
    caregiver_edit_router_prompt_edit_title:
      'Seguro que queres saír sen gardar os cambios no perfil do coidador para “{{name}}”?',
    caregiver_edit_router_prompt_edit_message:
      'Ao pechar sesión, perderás todos os cambios que fixeches recentemente no perfil do coidador.',
    current_user_router_prompt_edit_title:
      'Seguro que queres saír sen gardar os cambios no teu perfil?',
    current_user_router_prompt_edit_message:
      'Ao pechar sesión, perderás todos os cambios que fixeches recentemente no teu perfil.',
    caregiver_edit_affiliation_type_router_prompt_edit_title:
      'Seguro que queres pechar sesión sen gardar os cambios no tipo de afiliación para {{name}}?',
    caregiver_edit_affiliation_type_router_prompt_edit_message:
      'Ao pechar sesión, perderás todos os cambios que fixeches recentemente no tipo de afiliación do coidador.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Si, pechar sesión',
    senior_create_cancel_toast: 'Cancelouse a creación da nova persoa maior.',
    senior_edit_cancel_toast: 'Cancelouse a edición da persoa maior “{{name}}”.',
    caregiver_edit_cancel_toast: 'Cancelouse a edición do coidador “{{name}}”.',
    current_user_edit_cancel_toast: 'Cancelouse a edición do teu perfil.',
    caregiver_edit_affiliation_type_cancel_toast:
      'Cancelouse a edición do tipo de afiliación para o coidador {{name}}.',
    current_user_edit_email_not_allowed_tooltip_title:
      'Non se pode cambiar o teu enderezo de correo electrónico neste momento.',
    caregiver_edit_email_not_allowed_tooltip_title:
      'Actualmente non é posible cambiar o enderezo de correo electrónico do coidador.',
    current_user_edit_state_not_allowed_tooltip_title:
      'Non se pode cambiar o estado da dispoñibilidade.',
    profile_picture_info_message: 'Para unha mellor resolución, a imaxe debe ser JPG/JPEG.',
    display_name_other_user_info_message:
      'Neste campo, introduce o nome polo que se chama ao usuario no día a día. Este é o nome que os membros da familia, amigos e outross coidadoress verán no sistema cando interactúen co usuario.',
    display_name_current_user_info_message:
      'Neste campo, introduce o nome polo que te chaman a diario. Este é o nome que outros usuarios verán no sistema cando interactúen contigo.',
    roles_ids_input: 'Permisos',
    roles_ids_placeholder_input: 'Seleccionar permisos',
    input_placeholder: 'Introduce: {{ type }}',
    not_specified: 'Sen especificar',
    input_read_only_tooltip: 'Non tes permisos para editar a información do perfil',
    quick_access_code_input: 'Código de acceso rápido',
    quick_access_code_redefine_button: 'Restablecer',
    quick_access_code_disabled:
      'Xa existe un código de acceso rápido. Para cambialo, fai clic en “Restablecer”.',
    success: {
      save_success_button: 'Gardado!',
      delete_success_button: 'Eliminouse!',
      create_senior_success_message: 'Creouse a persoa maior “{{name}}” con éxito!',
      edit_senior_success_message: 'Gardáronse os cambios no perfil da persoa maior.',
      edit_caregiver_success_message: 'Gardáronse os cambios no perfil do coidador.',
      edit_caregiver_roles_ids_success_message: 'Gardáronse os cambios nos postos do coidador.',
      edit_current_user_success_message: 'Gardáronse os cambios no teu perfil.',
      edit_current_user_app_language_to_chosen_language_success_message:
        'O idioma da aplicación actualizouse a {{language}}.',
      edit_current_user_app_language_to_browser_language_success_message:
        'O idioma da aplicación actualizouse ao idioma do teu navegador: {{language}}.',
      edit_current_user_app_language_to_default_language_success_message:
        'O idioma da aplicación actualizouse ao idioma usado por defecto: {{language}}.',
      success_delete_senior: 'Persoa maior ({{name}}) eliminada.',
      success_delete_caregiver: 'Coidador ({{name}}) eliminado.',
      success_delete_invite: 'Cancelarase a invitación para {{name}}.',
    },
    errors: {
      error_on_save_button: 'Produciuse un erro ao gardar',
      error_on_delete_senior_button: 'Produciuse un erro ao eliminar a persoa maior',
      error_on_delete_caregiver_button: 'Produciuse un erro ao eliminar o coidador',
      error_on_delete_invite: 'Produciuse un erro ao cancelar a invitación',
      edit_form_validation_invalid_field: 'Cubre este campo para completar a edición do perfil',
      caregiver_edit_affiliation_type_form_validation_invalid_field:
        'Cubre este campo para completar a edición do tipo de afiliación do coidador',
      form_validation_invalid_phone_number: 'O número de teléfono móbil indicado non é válido',
      form_validation_not_mobile_phone_number:
        'O número indicado non corresponde a un número de teléfono móbil',
      form_validation_invalid_email: 'O enderezo de correo electrónico proporcionado non é válido',
      form_validation_invalid_number:
        'Segundo a rexión de {{region}}, o número debe ter {{digits}} caracteres',
      form_validation_invalid_room_name: 'O nome da sala indicado non é válido',
      form_validation_room_maximum_characters:
        'O nome da sala indicada non pode ter máis de {{max}} caracteres',
      form_validation_invalid_display_name_maximum_characters:
        'O nome debe ter menos de {{max}} caracteres',
      create_active_senior_licenses_number_exceeded_error:
        'Non foi posible engadir unha persoa maior activa. A organización xa alcanzou o número máximo de persoas maiores activas para o número de licenzas activas.',
      activate_senior_licenses_number_exceeded_error:
        'Non se puido desactivar á persoa maior. A organización xa alcanzou o número máximo de persoas maiores activas para o número de licenzas activas.',
      form_validation_invalid_quick_access_code:
        'O código de acceso rápido debe ter {{digits}} díxitos',
      form_validation_process_number_max_chars:
        'O número de caso debe ter menos de {{max}} caracteres',
      form_validation_recurring_fee_max_chars:
        'A cota mensual debe ter polo menos {{max}} caracteres',
      form_validation_aditional_info_max_chars:
        'Outra información debe ter polo menos {{max}} caracteres',
    },
  },
  user_attachments: {
    list: {
      headers: {
        name: 'Nome',
        description: 'Descrición',
        author: 'Engadiuse por',
        date: 'Data',
        size: 'Tamaño',
      },
      actions: {
        edit: 'Editar',
        delete: 'Eliminar',
        download: 'Transferencia',
      },
    },
    empty: {
      title: 'Aínda non existen anexos rexistrados.',
      subtitle: 'Preme en engadir para engadir un anexo.',
    },
    modals: {
      create: {
        title: 'Engadir anexo',
      },
      edit: {
        title: 'Editar anexo',
        name: 'Nome',
        description: 'Descrición',
        errors: {
          name_required: 'O nome do anexo é obrigatorio',
          name_max_length: 'O nome do anexo debe ter polo menos {{max}} caracteres',
          description_max_length: 'A descrición do anexo debe ter polo menos {{max}} caracteres',
        },
      },
      delete: {
        title: 'Seguro que queres eliminar o diagnóstico de<br/> “{{name}}”?',
        description: 'O anexo eliminarase permanentemente.',
      },
    },
    success: {
      create: 'Engadíronse con éxito os anexos',
      edit: 'O anexo editouse con éxito',
      delete: 'O anexo eliminouse con éxito',
    },
    error: {
      create: 'Produciuse un erro ao engadir anexos',
      edit: 'Produciuse un erro ao editar o anexo',
      delete: 'Produciuse un erro ao eliminar o anexo',
      download: 'Produciuse un erro ao transferir o anexo',
    },
    access_denied: {
      create: 'Non tes permisos para engadir anexos',
      edit: 'Non tes permisos para editar anexos',
      delete: 'Non tes permisos para eliminar anexos',
    },
  },
  entity_services: {
    root: {
      title: 'Servizo',
      add_button: 'Engadir servizo',
      search_input_placeholder: 'Buscar servizos',
      name_header: 'Nome',
      display_order_header: 'Orde',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      no_services: 'Non existen servizos configurados.',
      deleted_service_dialog_title: 'Servizo “{{name}}” eliminado',
      deleted_service_dialog_message1: 'Eliminouse “{{name}}”.',
      deleted_service_dialog_message2:
        'O servizo xa non está dispoñible na aplicación {{caregiversAppName}} nin en {{seniorsDeviceName}}.',
      deleted_service_dialog_ok_button: 'Vale',
    },
    edit: {
      title_create: 'Engadir servizo',
      title_edit: 'Editar servizo',
      title_view: 'Ver servizo',
      exit_button: 'Saír',
      save_button: 'Gardar',
      saving_button: 'Gardando...',
      title_input: 'Nome',
      state_input: 'Estado',
      phone_number_input: 'Número de contacto',
      phone_number_use_general_number: 'Usar número xeral',
      phone_number_use_specific_number: 'Usar número específico',
      display_order_input: 'Orde',
      description_input: 'Descrición',
      details_input: 'Detalles',
      attach_document_input: 'Engadir anexo en PDF',
      document_informative_note_input: 'Mensaxe sobre información contractual',
      icon_input: 'Iconas',
      image_input: 'Imaxe',
      button_preview: 'Visualización de botóns',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      number_input: 'Número',
      upload: 'Achegar',
      remove_document: 'Eliminar',
      view_document: 'Ver',
      image_info:
        'A imaxe debe estar en formato JPG/JPEG. Para obter mellores resultados, debes usar unha resolución de 292 x 288 px.',
      delete_button: 'Eliminar servizo',
      deleting_button: 'Eliminando...',
      delete_dialog_title: 'Eliminar o servizo “{{name}}“?',
      delete_dialog_content:
        'Ao eliminar este servizo, todos os usuarios da aplicación {{caregiversAppName}} e do servizo {{seniorsDeviceName}} perderán o acceso a eles. O servizo tamén se eliminará permanentemente da lista de servizos da organización.',
      delete_dialog_action_yes_button: 'Si, eliminar',
      delete_dialog_action_no_button: 'Cancelar',
      router_prompt_edit_title:
        'Seguro que queres pechar sesión sen gardar os cambios nos detalles do servizo “{{name}}”?',
      router_prompt_create_title:
        'Seguro que queres saír sen gardar a creación do servizo “{{name}}”?',
      router_prompt_create_message:
        'Ao pechar sesión, o servizo non se gardará e toda a información introducida será eliminada.',
      router_prompt_edit_message:
        'Ao saír, perderás todos os cambios que introduciches recentemente nos detalles do servizo.',
      router_prompt_action_cancel_button: 'Cancelar',
      router_prompt_action_ok_button: 'Si, pechar sesión',
      create_service_dialog_title: 'Gardar a creación do servizo “{{name}}”?',
      create_service_dialog_message: 'Ao gardar, o novo servizo engadirase á lista de servizos.',
      edit_service_dialog_title: 'Gardar os cambios nos detalles do servizo “{{name}}”?',
      edit_service_dialog_message:
        'Ao gardar, o servizo actualizarase coa información introducida recentemente.',
      save_service_dialog_action_cancel_button: 'Cancelar',
      save_service_dialog_action_save_button: 'Gardar',
      service_create_cancel_toast: 'Cancelouse a creación do novo servizo.',
      service_edit_cancel_toast: 'Cancelouse a edición do servizo “{{name}}”.',
      errors: {
        error_on_save_button: 'Produciuse un erro ao gardar',
        error_on_delete_button: 'Erro ao eliminar',
        edit_form_validation_invalid_field: 'Cubre este campo para completar a edición do servizo',
        create_form_validation_invalid_field:
          'Cubre este campo para completar a creación do servizo',
        create_form_validation_invalid_icon:
          'Escolle unha icona para completar a creación do servizo',
        create_form_validation_invalid_image:
          'Carga unha imaxe para completar a creación do servizo',
        form_validation_invalid_phone_number: 'O número indicado non é válido',
        service_does_not_exist_error:
          'Non foi posible obter o servizo ao que estabas a tentar acceder.',
      },
      success: {
        save_success_button: 'Gardado!',
        delete_success_button: 'Eliminouse!',
        create_success_message: 'O servizo “{{name}}” creouse con éxito!',
        edit_success_message: 'Gardáronse os cambios nos detalles do servizo.',
      },
    },
  },
  applications: {
    system_administrator: {
      root: {
        title: 'Aplicacións',
        search_input_placeholder: 'Buscar aplicacións',
        name_header: 'Nome',
        category_header: 'Categoría',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_applications: 'Non existen afiliacións.',
        add_button: 'Engadir aplicación',
      },
      edit: {
        entities: {
          search_input_placeholder: 'Buscar organizacións',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_entities: 'Non existen organizacións.',
          enable_application_dialog_message:
            'Cando actives a aplicación, estará dispoñible para a organización {{name}}.',
          enable_application_dialog_action_yes_button: 'Si, activar',
          enable_application_dialog_action_no_button: 'Cancelar',
          disable_application_dialog_message:
            'Ao desactivar a aplicación, xa non estará dispoñible para a organización {{name}}.',
          disable_application_dialog_action_yes_button: 'Si, desactivar',
          disable_application_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_application_error_message:
              'Produciuse un erro. Non foi posible activar a aplicación.',
            disable_application_error_message:
              'Produciuse un erro. Non foi posible desactivar a aplicación.',
            update_state_application_error_message:
              'Produciuse un erro. Non se puido actualizar o estado da aplicación.',
          },
        },
      },
    },
    professional_caregiver: {
      root: {
        search_input_placeholder: 'Buscar aplicacións',
        name_header: 'Nome',
        category_header: 'Categoría',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_applications: 'Non existen afiliacións.',
        enable_application_dialog_message:
          'Cando actives a aplicación, estará dispoñible para todos os usuarios na organización {{name}}.',
        enable_application_dialog_action_yes_button: 'Si, activar',
        enable_application_dialog_action_no_button: 'Cancelar',
        disable_application_dialog_message:
          'Ao desactivar a aplicación, xa non estará dispoñible para todos os usuarios na organización {{name}}.',
        disable_application_dialog_action_yes_button: 'Si, desactivar',
        disable_application_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_application_error_message:
            'Produciuse un erro. Non foi posible activar a aplicación.',
          disable_application_error_message:
            'Produciuse un erro. Non foi posible desactivar a aplicación.',
          update_state_application_error_message:
            'Produciuse un erro. Non se puido actualizar o estado da aplicación.',
        },
      },
      edit: {
        users: {
          search_input_placeholder: 'Buscar usuarios',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_users: 'Non existen usuarios.',
          enable_application_dialog_message:
            'Cando actives a aplicación, estará dispoñible para {{name}}.',
          enable_application_dialog_action_yes_button: 'Si, activar',
          enable_application_dialog_action_no_button: 'Cancelar',
          disable_application_dialog_message:
            'Ao desactivar a aplicación, xa non estará dispoñible para {{name}}.',
          disable_application_dialog_action_yes_button: 'Si, desactivar',
          disable_application_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_application_error_message:
              'Produciuse un erro. Non foi posible activar a aplicación.',
            disable_application_error_message:
              'Produciuse un erro. Non foi posible desactivar a aplicación.',
            update_state_application_error_message:
              'Produciuse un erro. Non se puido actualizar o estado da aplicación.',
          },
        },
      },
    },
  },
  application_tabs: {
    tabs: {
      details: 'Detalles',
      entities: 'Organizacións',
      users: 'Usuarios',
    },
    errors: {
      application_does_not_exist_error:
        'Non foi posible obter a aplicación á que estabas a tentar acceder.',
    },
  },
  application_form: {
    name_input: 'Nome',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    description_input: 'Descrición',
    is_system_application_input: 'Aplicación do sistema',
    is_system_application_value_yes: 'Si',
    is_system_application_value_no: 'Non',
    available_for_user_types_input: 'Tipos de usuarios para os que está dispoñible a aplicación',
    icon_input: 'Iconas',
    picture_input: 'Imaxe',
    category_input: 'Categoría',
    activity_type_input: 'Tipo de actividade',
    windows_key_input: 'Clave de Windows',
    android_key_input: 'Clave de Android',
    input_disabled_tooltip: 'Non tes permisos para editar a aplicación',
  },
  devices: {
    root: {
      search_input_placeholder: 'Buscar dispositivos',
      identifier_header: 'Identificador',
      type_header: 'Tipo',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      user_header: 'Usuario',
      no_devices: 'Non existen dispositivos.',
      add_button: 'Engadir dispositivo',
    },
  },
  device_tabs: {
    device_title: 'Dispositivo “{{name}}”',
    tabs: {
      details: 'Detalles',
      health: 'Saúde',
      information: 'Información',
    },
    errors: {
      device_does_not_exist_error:
        'Non foi posible obter o dispositivo ao que estabas a tentar acceder.',
    },
  },
  user_cards: {
    root: {
      title: 'Tarxetas',
      search_input_placeholder: 'Buscar tarxetas',
      name_header: 'Nome',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      user_header: 'Usuario',
      no_cards: 'Non existen tarxetas.',
      add_button: 'Engadir tarxeta',
      deleted_card_dialog_title: 'Eliminouse a tarxeta “{{name}}”',
      deleted_card_dialog_message: 'A tarxeta xa non existe no sistema.',
      deleted_card_dialog_ok_button: 'Vale',
    },
    edit: {
      errors: {
        card_does_not_exist_error: 'Non se puido recuperar a tarxeta á que intentaches acceder.',
      },
    },
  },
  gdpr_erasure_requests: {
    root: {
      title: 'Dereito ao esquecemento (RXPD)',
      add_button: 'Solicitar eliminación',
      no_requests: 'Non existen solicitudes de eliminación.',
      status_header: 'Estado',
      requested_at_header: 'Solicitado o',
      completed_at_header: 'Completado o',
      error_header: 'Erro',
      user_id_hash_header: 'Hash do usuario',
      user_email_hash_header: 'Hash do correo',
      status_processing: 'En proceso',
      status_completed: 'Completado',
      status_failed: 'Fallou',
    },
    create_modal: {
      title: 'Solicitar eliminación de datos persoais',
      email_label: 'Correo electrónico do usuario',
      email_placeholder: 'usuario@exemplo.com',
      email_required: 'O correo electrónico é obrigatorio.',
      email_invalid: 'Introduce un correo electrónico válido.',
      cancel_button: 'Cancelar',
      submit_button: 'Continuar',
      submitting_button: 'Enviando...',
      confirm_title: 'Confirmar eliminación de datos',
      confirm_description:
        'Esta acción eliminará/anonimizará permanentemente os datos persoais do usuario asociado a este correo.',
      confirm_cannot_be_undone: 'Esta acción é irreversible e non se pode desfacer.',
      confirm_button: 'Eliminar definitivamente',
      cancel_confirm_button: 'Cancelar',
    },
    errors: {
      forbidden: 'Non tes permiso para realizar esta acción.',
      user_not_found: 'Non existe ningún usuario con ese correo.',
      unknown: 'Produciuse un erro ao procesar a solicitude. Téntao de novo.',
    },
    success: {
      request_created: 'Solicitude de eliminación creada correctamente.',
    },
  },
  entity_photos: {
    root: {
      add_button: 'Engadir',
      deleted_photo_dialog_title: 'Foto eliminada',
      deleted_photo_dialog_message:
        'A foto xa non está dispoñible na pantalla de bloqueo do ordenador de {{brand}}.',
      deleted_photo_dialog_ok_button: 'Vale',
      entity_photos_only_for_computer_info_message:
        'As fotos engadidas pódense ver en ordenadores e tabletas de Sioslife.',
    },
    edit: {
      title_create: 'Engadir fotos',
      title_edit: 'Editar foto',
      photo_input: 'Fotografía',
      photos_input: 'Fotos',
      description_input: 'Descrición',
      exit_button: 'Saír',
      save_button: 'Gardar',
      saving_button: 'Gardando...',
      photos_create_cancel_toast: 'Cancelouse a creación das novas fotos.',
      photo_edit_cancel_toast: 'Cancelouse a edición da foto.',
      delete_button: 'Eliminar foto',
      deleting_button: 'Eliminando...',
      delete_dialog_title: 'Seguro que queres eliminar a foto?',
      delete_dialog_content:
        'Cando elimines a foto, deixará de aparecer na túa pantalla de bloqueo do ordenador de {{brand}} e eliminarase permanentemente.',
      delete_dialog_action_yes_button: 'Si, eliminar',
      delete_dialog_action_no_button: 'Cancelar',
      router_prompt_edit_title: 'Seguro que queres saír sen gardar os cambios na foto?',
      router_prompt_edit_message:
        'Ao pechar sesión, perderás todos os cambios que fixeches recentemente na túa foto.',
      router_prompt_create_title: 'Seguro que queres saír sen gardar a creación da foto?',
      router_prompt_create_message:
        'Ao saír, as fotos non se gardarán e eliminarase toda a información que introduciches.',
      router_prompt_action_cancel_button: 'Cancelar',
      router_prompt_action_ok_button: 'Si, pechar sesión',
      photos_info: 'As fotos deben estar en formato JPG/JPEG.',
      choose_photos_button: 'Escoller fotos',
      input_disabled_tooltip: 'Non tes permisos para editar os detalles da foto',
      errors: {
        error_on_save_button: 'Produciuse un erro ao gardar',
        error_on_delete_button: 'Erro ao eliminar',
        photo_does_not_exist_error: 'Non foi posible obter a foto á que estabas a tentar acceder.',
        create_form_validation_invalid_field:
          'Cubre este campo para completar a creación das fotos',
        form_validation_invalid_description_maximum_characters:
          'A descrición debe ter polo menos {{max}} caracteres',
      },
      success: {
        save_success_button: 'Gardado!',
        delete_success_button: 'Eliminouse!',
        create_success_message: 'As fotos creáronse con éxito!',
        edit_success_message: 'Gardáronse os cambios na foto.',
      },
      warnings: {
        partial_create_success_message:
          'Algunhas fotos creáronse con éxito! Con todo, non foi posible de engadir {{total}} foto(s): {{files}}.',
      },
    },
  },
  entities: {
    root: {
      title: 'Organizacións',
      search_input_placeholder: 'Buscar organizacións',
      name_header: 'Nome',
      type_header: 'Tipo',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      no_entities: 'Non existen organizacións.',
      add_button: 'Engadir organización',
      deleted_entity_dialog_title: 'Eliminouse a organización “{{name}}”',
      deleted_entity_dialog_message: 'A organización xa non existe no sistema.',
      deleted_entity_dialog_ok_button: 'Vale',
    },
    edit: {
      tabs: {
        details: 'Detalles',
        seniors: 'Persoas maiores',
        professional_caregivers: 'Profesionais',
        applications: 'Aplicacións',
        devices: 'Dispositivos',
        user_cards: 'Tarxetas',
        activity: 'Actividade',
        statistics: 'Estatísticas',
        licenses: 'Licenzas',
        playlists: 'Listas de reprodución',
      },
      seniors: {
        search_input_placeholder: 'Buscar persoas maiores',
        name_header: 'Nome',
        gender_header: 'Xénero',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_seniors: 'Non existen persoas maiores.',
        enable_senior_dialog_title: 'Activar a persoa maior {{name}}?',
        enable_senior_dialog_message:
          'Ao activar a persoa maior, poderá usar o sistema de {{brand}}.',
        enable_senior_dialog_action_yes_button: 'Si, activar',
        enable_senior_dialog_action_no_button: 'Cancelar',
        disable_senior_dialog_title: 'Activar a persoa maior {{name}}?',
        disable_senior_dialog_message:
          'Se desactivas a persoa maior, xa non poderá usar o sistema {{brand}}.',
        disable_senior_dialog_action_yes_button: 'Si, desactivar',
        disable_senior_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_senior_error_message:
            'Produciuse un erro. Non se puido desactivar á persoa maior.',
          disable_senior_error_message:
            'Produciuse un erro. Non foi posible desactivar a persoa maior.',
          update_state_senior_error_message:
            'Produciuse un erro. Non foi posible actualizar o estado da persoa maior.',
        },
      },
      professional_caregivers: {
        search_input_placeholder: 'Buscar profesionais',
        invite_button: 'Convidar a un profesional',
        name_header: 'Nome',
        email_header: 'Correo electrónico',
        affiliation_type_header: 'Tipo',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_professional_caregivers: 'Non existen profesionais.',
      },
      applications: {
        search_input_placeholder: 'Buscar aplicacións',
        name_header: 'Nome',
        category_header: 'Categoría',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_applications: 'Non existen afiliacións.',
        enable_application_dialog_message:
          'Cando actives a aplicación, estará dispoñible para a organización {{name}}.',
        enable_application_dialog_action_yes_button: 'Si, activar',
        enable_application_dialog_action_no_button: 'Cancelar',
        disable_application_dialog_message:
          'Ao desactivar a aplicación, xa non estará dispoñible para a organización {{name}}.',
        disable_application_dialog_action_yes_button: 'Si, desactivar',
        disable_application_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_application_error_message:
            'Produciuse un erro. Non foi posible activar a aplicación.',
          disable_application_error_message:
            'Produciuse un erro. Non foi posible desactivar a aplicación.',
          update_state_application_error_message:
            'Produciuse un erro. Non se puido actualizar o estado da aplicación.',
        },
      },
      devices: {
        search_input_placeholder: 'Buscar dispositivos',
        identifier_header: 'Identificador',
        type_header: 'Tipo',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        user_header: 'Usuario',
        no_devices: 'Non existen dispositivos.',
      },
      user_cards: {
        search_input_placeholder: 'Buscar tarxetas',
        name_header: 'Nome',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        user_header: 'Usuario',
        no_cards: 'Non existen tarxetas.',
        front_card_design_button: 'Deseño frontal da tarxeta',
        back_card_design_button: 'Deseño posterior da tarxeta',
      },
      playlists: {
        search_input_placeholder: 'Buscar listas de reprodución',
        type_filter: 'Tipo',
        name_header: 'Nome',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_playlist: 'Non existen listas de reprodución.',
        enable_playlist_dialog_title: 'Activar a lista de reprodución “{{name}}”?',
        enable_playlist_dialog_message:
          'Ao activar a lista de reprodución, estará dispoñible para a organización {{name}}.',
        enable_playlist_dialog_action_yes_button: 'Si, activar',
        enable_playlist_dialog_action_no_button: 'Cancelar',
        disable_playlist_dialog_title: 'Activar a lista de reprodución “{{name}}”?',
        disable_playlist_dialog_message:
          'Ao activar a lista de reprodución, estará dispoñible para a organización “{{name}}”.',
        disable_playlist_dialog_action_yes_button: 'Si, desactivar',
        disable_playlist_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_playlist_error_message:
            'Produciuse un erro. Non foi posible activar a lista de reprodución.',
          disable_playlist_error_message:
            'Produciuse un erro. Non foi posible desactivar a lista de reprodución.',
          update_state_playlist_error_message:
            'Produciuse un erro. Non foi posible actualizar o estado da lista de reprodución.',
        },
      },
      playlist_items: {
        title: 'Lista de reprodución “{{name}}”',
        search_input_placeholder: 'Buscar elementos',
        title_header: 'Título',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_items: 'Non hai elementos na lista de reprodución.',
        enable_item_dialog_title: 'Queres activar o elemento “{{name}}” na lista de reprodución?',
        enable_item_dialog_message:
          'Ao activar o elemento da lista de reprodución, estará dispoñible para a organización “{{name}}”.',
        enable_item_dialog_action_yes_button: 'Si, activar',
        enable_item_dialog_action_no_button: 'Cancelar',
        disable_item_dialog_title:
          'Queres desactivar o elemento “{{name}}” na lista de reprodución?',
        disable_item_dialog_message:
          'Ao desactivar o elemento da lista de reprodución, deixará de estar dispoñible para a organización “{{name}}”.',
        disable_item_dialog_action_yes_button: 'Si, desactivar',
        disable_item_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_item_error_message:
            'Produciuse un erro. Non foi posible activar o elemento na lista de reprodución.',
          disable_item_error_message:
            'Produciuse un erro. Non foi posible activar o elemento na lista de reprodución.',
          update_state_item_error_message:
            'Produciuse un erro. Non foi posible actualizar o estado do elemento na lista de reprodución.',
          playlist_does_not_exist_error:
            'Non foi posible recuperar a lista de reprodución á que intentaches acceder.',
        },
      },
      licenses: {
        number_of_monthly_active_licenses_not_defined: 'Sen definir',
        number_of_monthly_active_licenses_title: 'Licenzas mensuais activas',
        save_button: 'Gardar',
        saving_button: 'Gardando...',
        edit_dialog_title: 'Gardar os cambios no número de licenzas activas mensuais?',
        edit_dialog_content:
          'Ao gardar, actualizarase o número de licenzas activas mensuais ({{total}}) para a organización {{entity}}.',
        edit_dialog_action_cancel_button: 'Cancelar',
        edit_dialog_action_save_button: 'Gardar',
        router_prompt_title:
          'Seguro que queres pechar sesión sen gardar os cambios no número de licenzas activas mensuais?',
        router_prompt_message:
          'Ao marchar, perderás todos os cambios que fixeches recentemente no número de licenzas activas mensuais para a organización {{entity}}.',
        router_prompt_action_cancel_button: 'Cancelar',
        router_prompt_action_ok_button: 'Si, pechar sesión',
        number_of_monthly_active_licenses_edit_cancel_toast:
          'Cancelouse a edición do número de licenzas mensuais.',
        success: {save_success_button: 'Gardado!'},
        errors: {
          error_on_save_button: 'Produciuse un erro ao gardar',
          edit_form_validation_invalid_field:
            'Cubre este campo para completar a edición do número de licenzas activas mensuais',
          form_validation_invalid_number_of_monthly_active_licenses:
            'O número de licenzas activas mensuais non é válido',
        },
      },
      errors: {
        entity_does_not_exist_error:
          'Non foi posible obter a organización á que intentaches acceder.',
      },
    },
  },
  affiliated_entity_profile: {
    errors: {
      entity_does_not_exist_error:
        'Non foi posible obter a organización á que intentaches acceder.',
    },
  },
  section_tabs: {exit_button: 'Saír'},
  profession_input: {no_options: 'Sen opcións', loading: 'Cargando...'},
  social_network_city_input: {no_options: 'Sen opcións', loading: 'Cargando...'},
  user_menu: {
    logout: 'Pechar sesión',
    profile: 'Perfil',
    settings: 'Configuracións',
    office: 'Área de xestión',
    dailyCareLog: 'Rexistros de coidados',
    errors: {
      logout_error_message: 'Produciuse un erro. Non foi posible pechar sesión.',
    },
  },
  error_box: {
    title: 'A acción non se puido levar a cabo.',
    description:
      'Sentímolo, pero produciuse un erro ao tentar realizar a acción. Ponte en contacto co servizo de atención ao cliente, indicando os datos proporcionados.',
    details_button: 'Ver detalles',
  },
  country_select: {
    title: 'Código do país',
    no_options: 'Sen opcións',
  },
  tablet_button: {
    empty_title: '(Título do botón)',
  },
  icons_picker: {
    title: 'Selector de iconas',
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
  },
  image_cropper_dialog: {
    title: 'Preparar unha imaxe',
    zoom: 'Achegar',
    rotation: 'Rotación',
    save_button: 'Gardar',
    cancel_button: 'Cancelar',
  },
  loading_content: {
    loading: 'Cargando...',
  },
  pdf_viewer: {
    error: 'Non foi posible amosar o PDF.',
  },
  activity_card: {
    today: 'Hoxe',
    yesterday: 'Onte',
    this_week: 'Esta semana',
    this_month: 'Este mes',
    previous_month: 'O mes pasado',
    past: 'Último',
    entity: 'Organización',
    device: 'Dispositivo',
    duration: 'Duración',
    end_type: 'Tipo de finalización',
    participants: 'Participantes',
    application: 'Aplicación',
    application_battle_with: 'con',
    level_on_app: 'Nivel {{level}} na aplicación',
    missed_call: 'intentou chamar, pero a chamada non foi atendida.',
    rejected_call: 'intentei chamar, pero a chamada foi rexeitada.',
    call_started: 'chamou',
    drawing_sent: 'Deseño enviado a',
    drawing_saved: 'Deseño compartido con',
    drawing_shared: 'Deseño compartido no Álbum familiar',
    painting_sent: 'Pintura enviada a',
    painting_saved: 'Pintura compartida con',
    painting_shared: 'Pintura compartida no Álbum familiar',
    and: 'e',
    photo_sent: 'Foto enviada a',
    photo_saved: 'Foto compartida con',
    photo_shared: 'Foto compartida no Álbum familiar',
    camera_used: 'Cámara utilizada',
    response: 'Resposta',
    video_message_sent_to: 'Mensaxe de vídeo enviada a',
    video_message_sent_by: 'Mensaxe de vídeo enviada por',
    video_sent: 'Vídeo enviado a',
    video_saved: 'Vídeo compartido con',
    video_shared: 'Vídeo compartido no Álbum familiar',
    currently_happening: 'Está acontecendo agora mesmo',
  },
  notification_card: {
    open_invite: 'Invitación aberta',
    open_occurrences: 'Ir ao incidente',
    relationship_invite: {
      message_sender:
        '{{sender}} enviouche unha invitación para engadir a {{target}} á túa lista de contactos.',
      message_sender_same_as_target:
        '{{sender}} enviouche unha invitación para engadir á túa lista de contactos.',
      message_sender_unknown:
        'Recibiches unha invitación para engadir a {{target}} á túa lista de contactos.',
    },
    affiliation_invite: {
      message_sender: '{{sender}} enviouche unha invitación para unirte á organización {{target}}.',
      message_sender_same_as_target: '{{sender}} enviouche unha invitación para facerte afiliado.',
      message_sender_unknown: 'Recibiches unha invitación para unirte á organización {{target}}.',
    },
    user_signed_in: '{{name}} iniciou sesión en {{brand}}.',
    photo_sent: '{{name}} enviouche unha foto.',
    photo_shared: '{{name}} compartiu unha foto.',
    drawing_sent: '{{name}} enviouche un deseño.',
    drawing_shared: '{{name}} compartiu un deseño.',
    painting_sent: '{{name}} enviouche unha pintura.',
    painting_shared: '{{name}} compartiu unha pintura.',
    video_sent: '{{name}} enviouche un vídeo.',
    video_shared: '{{name}} compartiu un vídeo.',
    without_license: '{{name}} non ten licenza para usar os sistemas de {{brand}}.',
    missed_call: '{{name}} intentou chamarte, pero non recibiu resposta.',
    video_message: '{{name}} enviouche unha mensaxe de vídeo.',
    sos_request: 'Solicitude de axuda SOS de {{name}}. Verifica que todo estea ben.',
    reminder_response: {
      message: '{{name}} {{response}}; debe lembrárselle “{{reminder}}”.',
      responses: {
        positive: 'respondeu “Si”',
        negative: 'respondeu “Non”',
        not_responding: 'non respondeu',
      },
    },
    relationship_invite_request_result: {
      message_accepted: '{{name}} aceptou a {{target}} como contacto.',
      message_declined: '{{name}} non aceptou a {{target}} como contacto.',
    },
    caregiver_administrator_role_request_result: {
      message_accepted: '{{name}} aceptou a {{target}} como administrador.',
      message_declined: '{{name}} non aceptou a {{target}} como administrador.',
    },
    relationship_role_change: {
      message_senior_myself: '{{name}} estableceu o seu perfil como {{role}} para {{brand}}.',
      message_senior_other: '{{name}} estableceu {{target}} como {{role}} para {{brand}}.',
      message_caregiver_myself:
        '{{name}} estableceu o seu perfil como {{role}} para {{senior}} {{brand}}.',
      message_caregiver_other:
        '{{name}} estableceu {{target}} como {{role}} para {{senior}}{{brand}}.',
      message_unknown_myself: 'O teu perfil estableceuse como {{role}} para o {{senior}}{{brand}}.',
      message_unknown_other: '{{target}} estableceuse como {{role}} para {{senior}} {{brand}}.',
    },
    personal_data_sharing_preferences_change: {
      message: '{{name}}, cambiaches a configuración de uso compartido de datos para {{brand}}.',
      current_data_settings_title: 'A configuración actual é:',
      can_access_profile_data: 'Información do perfil',
      can_access_contacts: 'Contactos',
      can_access_applications: 'Aplicacións',
      can_access_multimedia: 'Multimedia',
      can_access_reminders: 'Recordatorios',
      can_access_activity_feed: 'Rexistro de actividades',
    },
    relationship_end: {
      message_myself:
        '{{name}}, eliminaches o teu perfil da túa lista de contactos. De agora en diante, non poderás comunicarte a través de {{brand}}.',
      message_other:
        '{{name}} eliminaches a {{target}} da túa lista de contactos. De agora en diante, {{target}} non poderá contactar con {{name}} a través dos sistemas de {{brand}}.',
    },
    media_processing_status: {
      video_message: {
        [ProcessStatus.Done]: 'A mensaxe de vídeo enviouse a {{name}}.',
        [ProcessStatus.Failed]:
          'Produciuse un erro ao enviar a mensaxe de vídeo. {{name}} non recibirá a mensaxe.',
      },
      video_shared: {
        [ProcessStatus.Done]: 'O vídeo compartiuse con {{name}}.',
        [ProcessStatus.Failed]: 'Produciuse un erro ao compartir o vídeo. {{name}} non o recibirá.',
      },
      video_saved: {
        [ProcessStatus.Done]: 'O vídeo gardouse con éxito.',
        [ProcessStatus.Failed]: 'Produciuse un erro ao gardar o vídeo.',
      },
      photo_shared: {
        [ProcessStatus.Done]: 'A foto compartiuse con {{name}}.',
        [ProcessStatus.Failed]: 'Produciuse un erro ao compartir a foto. {{name}} non a recibirá.',
      },
      photo_saved: {
        [ProcessStatus.Done]: 'A foto gardouse con éxito.',
        [ProcessStatus.Failed]: 'Produciuse un erro ao gardar a foto.',
      },
      unknown: {
        [ProcessStatus.Done]: 'O elemento multimedia gardouse con éxito.',
        [ProcessStatus.Failed]: 'Produciuse un erro ao gardar o vídeo.',
      },
    },
    media_empty: '{{name}} pediu compartir fotos e vídeos.',
    new_occurrence: 'Novo incidente “{{name}}” para recoñecer.',
    new_occurrence_patient: 'Novo incidente “{{name}}” do usuario {{patient}} para recoñecer.',
    new_occurrence_assigned:
      '{{registered}} indicou que es a persoa responsable do incidente “{{name}}”.',
    new_occurrence_assigned_patient:
      '{{registered}} indicou que es a persoa responsable do incidente “{{name}}” asociado co usuario {{patient}}.',
  },
  section_list: {
    refresh: 'Actualizar',
    no_more_data: 'Non existen máis datos',
    without_data: 'Non existen datos',
    today: 'Hoxe',
    old_f: 'Máis antigo',
    old_m: 'Máis antigo',
  },
  activity_timeline: {title: 'Actividade'},
  clipboard_text_field: {
    copy_to_clipboard_button: 'Copiar',
    copy_to_clipboard_tooltip_message: 'Copiar ao portapapeis',
    success: {
      copy_to_clipboard_success_message: 'Texto copiado ao portapapeis!',
    },
  },
  activity_end_types: {
    [ActivityEndType.UserAction]: 'Acción do usuario',
    [ActivityEndType.SystemTimeout]: 'Tempo de espera do sistema',
  },
  device_types: {
    [DeviceType.Handset]: 'Teléfono móbil',
    [DeviceType.Tablet]: 'Tableta',
    [DeviceType.Desktop]: 'Ordenador',
    [DeviceType.Laptop]: 'Portátil',
    [DeviceType.Unknown]: 'Descoñecido',
  },
  camera_facing: {
    [CameraFacing.Front]: 'Cámara frontal',
    [CameraFacing.Rear]: 'Cámara traseira',
    [CameraFacing.External]: 'Cámara externa',
    [CameraFacing.Unknown]: 'Cámara descoñecida',
  },
  reminder_response_values: {
    [ReminderResponseValue.Positive]: 'Si/Ben',
    [ReminderResponseValue.Negative]: 'Non',
    [ReminderResponseValue.NotResponding]: 'Non respondeu',
  },
  invite_states: {
    [InviteState.Accepted]: 'Aceptouse',
    [InviteState.Cancelled]: 'Cancelouse',
    [InviteState.Open]: 'Abrir',
    [InviteState.Pending]: 'Pendente',
    [InviteState.Refused]: ' Rexeitado',
    [InviteState.RefusedBySenior]: 'Rexeitado pola persoa maior',
    [InviteState.WaitingForSeniorApproval]: 'Agardando a aprobación da persoa maior',
    [InviteState.AcceptedBySenior]: 'Aceptado pola persoa maior',
    [InviteState.Expired]: 'Caducou',
  },
  process_statuses: {
    [ProcessStatus.Done]: 'Completouse',
    [ProcessStatus.InProgress]: 'En curso',
    [ProcessStatus.ToDo]: 'Para realizar',
    [ProcessStatus.Undefined]: 'Sen definir',
    [ProcessStatus.Failed]: 'Fallou',
  },
  entity_types: {
    [EntityType.DayCareCenter]: 'Centro de día',
    [EntityType.Family]: 'Familia',
    [EntityType.Generic]: 'Xenérico',
    [EntityType.HomeSupport]: 'Axuda no fogar',
    [EntityType.Institution]: 'Institución',
    [EntityType.InsuranceCompany]: 'Aseguradora',
    [EntityType.TechCompany]: 'Empresa de tecnoloxía',
  },
  care_taks_not_done_reasons: {
    [CareTaskNotDoneReason.SeniorRefusal]: 'Rexeitamento do usuario',
    [CareTaskNotDoneReason.seniorAbsence]: 'Ausencia do usuario',
    [CareTaskNotDoneReason.LackOfMaterial]: 'Falta de material',
    [CareTaskNotDoneReason.SeniorWasNotFeelingWell]: 'O usuario non se sentía ben',
    [CareTaskNotDoneReason.Other]: 'Outro motivo (obs)',
  },
  time_units: {
    seconds: 's',
    minutes: 'min',
    hours: 'h',
  },
  entity_form: {
    exit_button: 'Cancelar',
    create_entity_title: 'Engadir organización',
    picture_input: 'Foto',
    name_input: 'Nome da institución',
    name_placeholder_input: 'Introduce o nome da institución',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    description_input: 'Descrición',
    type_input: 'Tipo',
    language_input: 'Idioma',
    language_placeholder_input: 'Selecciona un idioma',
    region_input: 'Rexión',
    region_placeholder_input: 'Selecciona unha rexión',
    time_zone_input: 'Fuso horario',
    time_zone_placeholder_input: 'Selecciona un fuso horario',
    email_input: 'Enderezo de correo electrónico',
    email_placeholder_input: 'Engadir correo electrónico',
    short_name_input: 'Nome curto',
    short_name_placeholder_input: 'Introduce un nome curto',
    anniversary_input: 'Data de fundación/Aniversario',
    anniversary_placeholder_input: 'Escolle unha data',
    capacity_input: 'Capacidade',
    capacity_placeholder_input: 'Capacidade de acollida na institución',
    address_input: 'Enderezo',
    address_placeholder_input: 'Introduce o teu enderezo',
    website_input: 'Sitio web',
    website_placeholder_input: 'Engadir sitio web',
    number_input: 'Código',
    landline_phone_number_input: 'Número de teléfono',
    mobile_phone_number_input: 'Número de teléfono móbil',
    phone_number_placeholder: 'Inserir un número de teléfono',
    services_name_input: 'Nome dos servizos',
    services_name_placeholder_input: 'Nome dos servizos',
    services_phone_number_input: 'Número de teléfono de servizo',
    services_image_url_input: 'Imaxe do servizo',
    services_image_info_message: 'Para unha mellor resolución, a imaxe debe ser JPG/JPEG.',
    picture_info_message: 'Para unha mellor resolución, a imaxe debe ser JPG/JPEG.',
    special_purpose_input: 'Propósito da organización',
    special_purpose_placeholder_input: 'Selecciona un propósito',
    social_responses_input: 'Servizos sociais',
    social_responses_placeholder_input: 'Selecciona os servizos sociais da túa organización',
    save_button: 'Gardar',
    saving_button: 'Gardando...',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar organización',
    delete_dialog_title: 'Eliminar a organización “{{name}}”',
    delete_dialog_content: 'Ao eliminar a organización, eliminarase permanentemente do sistema.',
    delete_dialog_action_yes_button: 'Si, eliminar',
    delete_dialog_action_no_button: 'Cancelar',
    router_prompt_create_title:
      'Seguro que queres saír sen gardar a creación da organización, “{{name}}”?',
    router_prompt_create_message:
      'Ao pechar sesión, a organización non se gardará e eliminarase toda a información introducida nela.',
    router_prompt_edit_title:
      'Seguro que queres saír sen gardar os cambios no perfil da organización para “{{name}}”?',
    router_prompt_edit_message:
      'Ao pechar sesión, perderás todos os cambios que fixeches recentemente no perfil da organización.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Si, pechar sesión',
    edit_cancel_toast: 'Cancelouse a edición da organización “{{name}}”.',
    create_cancel_toast: 'Cancelouse a creación da nova organización.',
    edit_special_purpose_not_allowed_tooltip_title:
      'Non tes permisos para indicar o propósito da organización.',
    edit_is_active_not_allowed_tooltip_title:
      'Non tes permisos para indicar se a organización está activa.',
    input_disabled_tooltip: 'Non tes permisos para editar a información da organización',
    not_specified: 'Sen especificar',
    alert: {
      short_name_input: 'Este é o nome que se mostrará no sistema.',
    },
    success: {
      save_success_button: 'Gardado!',
      delete_success_button: 'Eliminouse!',
      create_success_message: 'A organización “{{name}}” creouse con éxito!',
      edit_success_message: 'Gardáronse os cambios no perfil da organización.',
    },
    errors: {
      error_on_save_button: 'Produciuse un erro ao gardar',
      error_on_delete_button: 'Erro ao eliminar',
      edit_form_validation_invalid_field:
        'Cubre este campo para completar a edición da organización',
      create_form_validation_invalid_field:
        'Cubre este campo para completar a creación da organización',
      form_validation_invalid_full_name_maximum_characters:
        'O nome completo debe ter polo menos {{max}} caracteres',
      form_validation_invalid_landline_phone_number: 'O número de teléfono indicado non é válido',
      form_validation_invalid_mobile_phone_number:
        'O número de teléfono móbil indicado non é válido',
      form_validation_not_mobile_phone_number:
        'O número indicado non corresponde a un número de teléfono móbil',
      form_validation_invalid_services_phone_number:
        'O número de teléfono para os servizos indicados non é válido',
      form_validation_invalid_email: 'O enderezo de correo electrónico proporcionado non é válido',
      form_validation_invalid_website: 'O sitio web indicado non é válido',
      form_validation_invalid_capacity: 'A capacidade indicada non é válida',
      form_validation_required_social_responses: 'Indica os servizos sociais da organización',
    },
  },
  professional_caregiver_invite_form: {
    exit_button: 'Saír',
    title: 'Convidar a un profesional',
    entity_input: 'Organización',
    name_input: 'Nome',
    contact_method_input: 'Método de contacto',
    email_input: 'Enderezo de correo electrónico',
    mobile_phone_number_input: 'Número de teléfono móbil',
    affiliation_type_input: 'Tipo de afiliación',
    save_dialog_title: 'Enviar invitación a {{name}}?',
    save_dialog_message:
      'Ao enviar a solicitude, {{name}} recibirá unha invitación para converterse en profesional e ter acceso a parte da información da organización {{entity}}.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Enviar',
    router_prompt_title: 'Seguro que queres saír sen enviar a invitación?',
    router_prompt_message: 'Se saes, perderás toda a información que introduciches na invitación.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Si, pechar sesión',
    cancel_toast: 'Cancelouse a invitación.',
    save_button: 'Enviar invitación',
    saving_button: 'Enviando...',
    success: {
      save_success_button: 'Enviouse!',
      success_message: 'A invitación enviouse a {{name}}.',
    },
    errors: {
      error_on_save_button: 'Produciuse un erro ao enviar',
      form_validation_invalid_field: 'Cubre este campo para completar a invitación',
      form_validation_invalid_mobile_phone_number:
        'O número de teléfono móbil indicado non é válido',
      form_validation_not_mobile_phone_number:
        'O número indicado non corresponde a un número de teléfono móbil',
      form_validation_invalid_email: 'O enderezo de correo electrónico proporcionado non é válido',
    },
  },
  invite_contact_methods: {
    [InviteContactMethod.Email]: 'Enderezo de correo electrónico',
    [InviteContactMethod.MobilePhoneNumber]: 'Número de teléfono móbil',
  },
  search_select_input: {
    no_options: 'Sen opcións',
    loading: 'Cargando...',
    more_button: 'Ademais',
    items_showing_information: 'Para mostrar {{value}} de {{total}} elementos',
  },
  user_card_form: {
    exit_button: 'Saír',
    create_user_card_title: 'Engadir tarxeta',
    edit_user_card_title: 'Editar tarxeta',
    view_user_card_title: 'Detalles da tarxeta',
    name_input: 'Nome',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    tag_input: 'Número de identificación',
    entity_input: 'Organización',
    user_input: 'Usuario',
    user_info_message: 'Este campo só se pode cubrir se xa se seleccionou unha organización.',
    save_button: 'Gardar',
    saving_button: 'Gardando...',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar tarxeta',
    delete_dialog_title: 'Seguro que queres eliminar a tarxeta “{{name}}”?',
    delete_dialog_content: 'Se eliminas a tarxeta, eliminarase permanentemente do sistema.',
    create_dialog_title: 'Gardar a creación da tarxeta “{{name}}”?',
    create_dialog_message: 'Ao gardar, a nova tarxeta engadirase ao sistema.',
    edit_dialog_title: 'Gardar os cambios de “{{name}}”?',
    edit_dialog_message:
      'Ao gardar, a tarxeta actualizarase coa información introducida recentemente.',
    router_prompt_create_title:
      'Seguro que queres saír sen gardar a creación da tarxeta “{{name}}”?',
    router_prompt_create_message:
      'Ao pechar sesión, a tarxeta non se gardará e eliminarase toda a información introducida nela.',
    router_prompt_edit_title: 'Seguro que queres saír sen gardar os cambios na tarxeta “{{name}}”?',
    router_prompt_edit_message:
      'Ao pechar sesión, perderás todos os cambios que introduciches recentemente na tarxeta.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Si, pechar sesión',
    edit_cancel_toast: 'Cancelouse a edición da tarxeta “{{name}}”.',
    create_cancel_toast: 'Cancelouse a creación da nova tarxeta.',
    disabled_tooltip: 'Non tes permisos para editar a tarxeta',
    success: {
      save_success_button: 'Gardado!',
      delete_success_button: 'Eliminouse!',
      create_success_message: 'A tarxeta “{{name}}” creouse con éxito!',
      edit_success_message: 'Gardáronse os cambios na tarxeta.',
      senior_inactive_success: 'A persoa maior {{name}} marcouse como inactiva.',
    },
    errors: {
      error_on_save_button: 'Produciuse un erro ao gardar',
      error_on_delete_button: 'Erro ao eliminar',
      edit_form_validation_invalid_field: 'Cubre este campo para completar a edición da tarxeta',
      create_form_validation_invalid_field: 'Cubre este campo para completar a creación da tarxeta',
      error_on_delete_user: 'Produciuse un erro ao eliminar o usuario',
      error_on_marking_senior_as_inactive:
        'Produciuse un erro ao marcar a persoa maior como inactiva',
    },
  },
  device_form: {
    exit_button: 'Saír',
    save_button: 'Gardar',
    saving_button: 'Gardando...',
    create_device_title: 'Engadir dispositivo',
    type_input: 'Tipo',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    number_input: 'Número',
    entity_input: 'Organización',
    user_input: 'Usuario',
    language_input: 'Idioma',
    language_placeholder_input: 'Selecciona un idioma',
    sign_in_type_input: 'Tipo de inicio de sesión',
    sign_in_type_placeholder_input: 'Selecciona un tipo',
    mac_address_input: 'Enderezo MAC',
    serial_number_input: 'Número de serie',
    user_info_message: 'Este campo só se pode cubrir se xa se seleccionou unha organización.',
    edit_type_not_allowed_tooltip_title: 'Non tes permisos para indicar o tipo de dispositivo.',
    edit_serial_number_not_allowed_tooltip_title:
      'Non tes permisos para introducir o número de serie.',
    edit_mac_address_not_allowed_tooltip_title: 'Non tes permisos para introducir o enderezo MAC.',
    edit_number_not_allowed_tooltip_title: 'Non tes permisos para introducir o número.',
    edit_sign_in_type_not_allowed_tooltip_title:
      'Non tes permisos para introducir o tipo de inicio de sesión.',
    input_disabled_tooltip: 'Non tes permisos para editar os detalles do dispositivo',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar dispositivo',
    delete_dialog_title: 'Seguro que queres eliminar o dispositivo etiquetado como “{{name}}”?',
    delete_dialog_content: 'Se eliminas o dispositivo, eliminarase permanentemente do sistema.',
    create_dialog_title: 'Gardar a creación do dispositivo etiquetado como “{{name}}”?',
    create_dialog_message: 'Ao gardar, o novo dispositivo engadirase ao sistema.',
    edit_dialog_title: 'Gardar os cambios no dispositivo etiquetado como “{{name}}”?',
    edit_dialog_message:
      'Ao gardar, o dispositivo actualizarase coa información introducida recentemente.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Gardar',
    router_prompt_create_title:
      'Seguro que queres pechar sesión sen gardar a creación do dispositivo etiquetado como “{{name}}”?',
    router_prompt_create_message:
      'Ao pechar sesión, o dispositivo non se gardará e eliminarase toda a información introducida nela.',
    router_prompt_edit_title:
      'Seguro que queres saír sen gardar os cambios no dispositivo etiquetado como “{{name}}”?',
    router_prompt_edit_message:
      'Ao pechar sesión, perderás todos os cambios que introduciches recentemente no dispositivo.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Si, pechar sesión',
    edit_cancel_toast: 'Cancelouse a edición do dispositivo etiquetado como “{{name}}”.',
    create_cancel_toast: 'Cancelouse a creación do novo dispositivo.',
    success: {
      create_message: 'O dispositivo etiquetado como “{{name}}” creouse correctamente!',
      edit_message:
        'Os cambios no dispositivo etiquetado como “{{name}}” gardáronse correctamente!',
      deleted_message: 'Eliminouse o dispositivo etiquetado como “{{name}}”.',
    },
    errors: {
      create_message: 'Non foi posible engadir o dispositivo etiquetado como “{{name}}”.',
      edit_message:
        'Non foi posible gardar os cambios realizados no dispositivo etiquetado como “{{name}}”.',
      edit_form_validation_invalid_field:
        'Cubre este campo para completar a edición do dispositivo',
      create_form_validation_invalid_field:
        'Cubre este campo para completar a creación do dispositivo',
      deleted_message: 'Non foi posible eliminar o dispositivo etiquetado como “{{name}}”.',
    },
    isInDebugMode_input: 'Modo de depuración',
    isInDebugMode_placeholder_input: 'Por definir',
  },
  user_sign_in_types: {
    [UserSignInType.Card]: 'Tarxeta',
    [UserSignInType.Quick]: 'Rápido',
    [UserSignInType.Code]: 'Código',
  },
  entity_special_purposes: {
    [EntitySpecialPurpose.Commercial]: 'Comercial',
    [EntitySpecialPurpose.Demonstration]: 'Demostracións',
    [EntitySpecialPurpose.Research]: 'Investigar',
    [EntitySpecialPurpose.Testing]: 'Probar',
  },
  entity_statistics_dashboard: {
    title: 'Estatísticas',
    refresh_button: 'Actualizar',
    text_charts: {
      number_of_active_seniors_title: 'Persoas maiores',
      number_of_active_seniors_subtitle: 'Persoas maiores activas',
      number_of_active_professional_caregivers_title: 'Coidadores profesionais',
      number_of_active_professional_caregivers_subtitle:
        'Coidadores activos afiliados á organización',
      number_of_active_informal_caregivers_title: 'Coidadores informais',
      number_of_active_informal_caregivers_subtitle:
        'Coidadores activos que manteñen relacións con persoas maiores',
      number_of_pending_invites_title: 'Invitacións pendentes',
      number_of_pending_invites_subtitle: 'Invitacións pendentes de resposta',
      number_of_calls_title: 'Chamadas',
      number_of_calls_subtitle: 'Duración maior ou igual a 5 segundos',
      number_of_outgoing_calls_title: 'Chamadas saíntes',
      number_of_outgoing_calls_subtitle: 'Duración maior ou igual a 5 segundos',
      number_of_incoming_calls_title: 'Chamadas recibidas',
      number_of_incoming_calls_subtitle: 'Duración maior ou igual a 5 segundos',
    },
    table_charts: {
      number_of_active_devices_title: 'Número de dispositivos activos',
      number_of_active_digital_inclusion_devices_title:
        'Número de dispositivos de inclusión dixital',
      number_of_active_quick_access_devices_title: 'Número de dispositivos de acceso rápido',
      number_of_active_devices_device_type_column: 'Dispositivo',
      number_of_active_devices_total_column: 'Total',
      top_users_title: 'Usuarios principais',
      top_users_subtitle: 'Horas de actividade (duración maior ou igual a 5 segundos)',
      top_users_user_column: 'Usuario',
      top_users_total_usage_time_column: 'Tipo de uso',
      top_users_usage_time_column_unit_label: 'horas',
    },
    area_charts: {
      general_system_usage_title: 'Uso xeral do sistema',
      general_system_usage_subtitle: 'Horas de actividade (duración maior ou igual a 5 segundos)',
      general_system_usage_x_label: 'Data',
      general_system_usage_y_label: 'Duración',
      general_system_usage_y_unit_label: 'horas',
    },
    bar_charts: {
      calls_evolution_title: 'Evolución das chamadas',
      calls_evolution_subtitle: 'Duración maior ou igual a 5 segundos',
      calls_evolution_x_label: 'Data',
      calls_evolution_y_label: 'Chamadas',
    },
    pie_charts: {
      most_used_applications_title: 'Aplicacións de uso frecuente',
      most_used_applications_subtitle: 'Horas de actividade (duración maior ou igual a 5 segundos)',
      most_used_applications_x_label: 'Aplicación',
      most_used_applications_y_label: 'Duración',
      most_used_applications_y_unit_label: 'horas',
    },
    stack_charts: {
      applications_usage_title: 'Uso por aplicación',
      applications_usage_subtitle: 'Horas de actividade (duración maior ou igual a 5 segundos)',
      applications_usage_singular_stack_label: 'Aplicación',
      applications_usage_plural_stack_label: 'Aplicacións',
      applications_usage_x_label: 'Data',
      applications_usage_y_label: 'Duración',
      applications_usage_y_unit_label: 'horas',
    },
  },
  senior_statistics_dashboard: {
    title: 'Estatísticas',
    refresh_button: 'Actualizar',
    statistics_per_application_title: 'Estatísticas por Aplicación',
    statistics_per_application_application_label: 'Aplicación',
    statistics_per_application_entity_label: 'Organización',
    statistics_per_application_entity_placeholder: 'Selecciona unha organización',
    statistics_per_application_info_message:
      'Selecciona unha organización para seleccionar unha aplicación e ver as súas estatísticas.',
    text_charts: {
      number_of_informal_caregivers_title: 'Coidadores informais',
      number_of_informal_caregivers_subtitle: 'Relacións das persoas maiores',
      number_of_pending_invites_title: 'Invitacións pendentes',
      number_of_pending_invites_subtitle: 'Invitacións pendentes de resposta',
      number_of_calls_title: 'Chamadas',
      number_of_calls_subtitle: 'Duración maior ou igual a 5 segundos',
      number_of_outgoing_calls_title: 'Chamadas saíntes',
      number_of_outgoing_calls_subtitle: 'Duración maior ou igual a 5 segundos',
      number_of_incoming_calls_title: 'Chamadas recibidas',
      number_of_incoming_calls_subtitle: 'Duración maior ou igual a 5 segundos',
      average_daily_usage_last_title: 'Uso medio mensual',
      average_daily_usage_subtitle: 'Media dos últimos 30 días',
      average_daily_usage_unit_label: 'horas',
      number_of_media_received_title: 'Elemento multimedia recibido',
      number_of_media_received_subtitle: 'Fotos e vídeos',
      number_of_video_messages_received_title: 'Mensaxes de vídeo recibidas',
      number_of_video_messages_sent_title: 'Mensaxes de vídeo enviadas',
      number_of_photos_sent_title: 'Fotos enviadas',
      number_of_drawings_sent_title: 'Deseños enviados',
      number_of_paintings_sent_title: 'Pinturas enviadas',
      last_access_date_time_title: 'Último acceso',
    },
    table_charts: {
      top_preferences_title: 'Preferencias principais',
      top_preferences_subtitle: 'Horas de actividade (duración maior ou igual a 5 segundos)',
      top_preferences_application_column: 'Aplicación',
      top_preferences_total_usage_time_column: 'Tipo de uso',
      top_preferences_total_usage_time_column_unit_label: 'horas',
    },
    area_charts: {
      general_system_usage_title: 'Uso xeral do sistema',
      general_system_usage_subtitle: 'Horas de actividade (duración maior ou igual a 5 segundos)',
      general_system_usage_x_label: 'Data',
      general_system_usage_y_label: 'Duración',
      general_system_usage_y_unit_label: 'horas',
      application_usage_title: 'Usar “{{name}}”',
      application_usage_subtitle: 'Horas de actividade (duración maior ou igual a 5 segundos)',
      application_usage_x_label: 'Data',
      application_usage_y_label: 'Duración',
      application_usage_y_unit_label: 'horas',
    },
    pie_charts: {
      most_used_applications_title: 'Aplicacións de uso frecuente',
      most_used_applications_subtitle: 'Horas de actividade (duración maior ou igual a 5 segundos)',
      most_used_applications_x_label: 'Aplicación',
      most_used_applications_y_label: 'Duración',
      most_used_applications_y_unit_label: 'horas',
    },
    stack_charts: {
      applications_usage_title: 'Uso por aplicación',
      applications_usage_subtitle: 'Horas de actividade (duración maior ou igual a 5 segundos)',
      applications_usage_singular_stack_label: 'Aplicación',
      applications_usage_plural_stack_label: 'Aplicacións',
      applications_usage_x_label: 'Data',
      applications_usage_y_label: 'Duración',
      applications_usage_y_unit_label: 'horas',
    },
    line_charts: {
      application_evolution_title: 'Evolución “{{name}}”',
      application_evolution_subtitle: 'Cambios de nivel',
      application_evolution_x_label: 'Data',
      application_evolution_y_label: 'Nivel',
    },
  },
  charts: {
    no_data: 'Non existen datos.',
  },
  media_gallery: {
    refresh: 'Actualizar',
    no_more_data: 'Non existen máis datos',
    without_data: 'Non existen datos',
  },
  application_category_form: {
    exit_button: 'Saír',
    create_application_category_title: 'Engadir categoría',
    save_button: 'Gardar',
    saving_button: 'Gardando...',
    default_name_input: 'Nome',
    default_description_input: 'Descrición',
    enabled_state_color_input: 'Cor de estado activada',
    pressed_state_color_input: 'Cor de estado premida',
    name_translations_input: 'Traducións para o nome',
    description_translations_input: 'Traducións para a descrición',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar categoría',
    delete_dialog_title: 'Queres eliminar a categoría “{{name}}”?',
    delete_dialog_content: 'Ao eliminar a categoría, eliminarase permanentemente do sistema.',
    delete_dialog_action_yes_button: 'Si, eliminar',
    delete_dialog_action_no_button: 'Cancelar',
    router_prompt_create_title:
      'Seguro que queres pechar sesión sen gardar a creación da categoría “{{name}}”?',
    router_prompt_create_message:
      'Ao pechar sesión, a categoría non se gardará e eliminarase toda a información introducida nela.',
    router_prompt_edit_title:
      'Seguro que queres saír sen gardar os cambios na categoría “{{name}}”?',
    router_prompt_edit_message:
      'Ao pechar sesión, perderás todos os cambios que introduciches recentemente na categoría.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Si, pechar sesión',
    edit_cancel_toast: 'Cancelouse a edición da categoría “{{name}}”.',
    create_cancel_toast: 'Cancelouse a creación da nova categoría.',
    create_dialog_title: 'Gardar a creación da categoría “{{name}}”?',
    create_dialog_message: 'Ao gardar, a nova categoría engadirase ao sistema.',
    edit_dialog_title: 'Gardar os cambios na categoría “{{name}}”?',
    edit_dialog_message:
      'Ao gardar, a categoría actualizarase coa información introducida recentemente.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Gardar',
    success: {
      save_success_button: 'Gardado!',
      delete_success_button: 'Eliminouse!',
      create_success_message: 'A categoría etiquetada como “{{name}}” creouse correctamente!',
      edit_success_message: 'Gardáronse os cambios na categoría.',
    },
    errors: {
      error_on_save_button: 'Produciuse un erro ao gardar',
      error_on_delete_button: 'Erro ao eliminar',
      edit_form_validation_invalid_field: 'Cubre este campo para completar a edición da categoría',
      create_form_validation_invalid_field:
        'Cubre este campo para completar a creación da categoría',
      form_validation_invalid_color: 'A cor indicada non é válida',
    },
  },
  playlist_types: {
    [PlaylistType.Karaoke]: 'Karaoke',
    [PlaylistType.Movies]: 'Vídeos',
    [PlaylistType.Music]: 'Música',
  },
  playlists: {
    system_administrator: {
      root: {
        title: 'Listas de reprodución',
        search_input_placeholder: 'Buscar listas de reprodución',
        type_filter: 'Tipo',
        name_header: 'Nome',
        no_playlists: 'Non existen listas de reprodución.',
        add_button: 'Engadir lista de reprodución',
        deleted_playlist_dialog_title: 'Lista de reprodución “{{name}}” eliminada',
        deleted_playlist_dialog_message: 'A lista de reprodución xa non existe no sistema.',
        deleted_playlist_dialog_ok_button: 'Vale',
      },
      edit: {
        tabs: {details: 'Detalles', items: 'Elementos', entities: 'Organizacións'},
        items: {
          search_input_placeholder: 'Buscar elementos',
          title_header: 'Título',
          no_items: 'Non hai elementos na lista de reprodución.',
          add_button: 'Engadir elemento',
          deleted_playlist_item_dialog_title:
            'O elemento “{{name}}” eliminouse da lista de reprodución',
          deleted_playlist_item_dialog_message: 'O elemento xa non existe no sistema.',
          deleted_playlist_item_dialog_ok_button: 'Vale',
        },
        entities: {
          search_input_placeholder: 'Buscar organizacións',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_entities: 'Non existen organizacións.',
          enable_playlist_dialog_title: 'Activar a lista de reprodución “{{name}}”?',
          enable_playlist_dialog_message:
            'Ao activar a lista de reprodución, estará dispoñible para a organización {{name}}.',
          enable_playlist_dialog_action_yes_button: 'Si, activar',
          enable_playlist_dialog_action_no_button: 'Cancelar',
          disable_playlist_dialog_title: 'Activar a lista de reprodución “{{name}}”?',
          disable_playlist_dialog_message:
            'Ao activar a lista de reprodución, estará dispoñible para a organización “{{name}}”.',
          disable_playlist_dialog_action_yes_button: 'Si, desactivar',
          disable_playlist_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_playlist_error_message:
              'Produciuse un erro. Non foi posible activar a lista de reprodución.',
            disable_playlist_error_message:
              'Produciuse un erro. Non foi posible desactivar a lista de reprodución.',
            update_state_playlist_error_message:
              'Produciuse un erro. Non foi posible actualizar o estado da lista de reprodución.',
          },
        },
        errors: {
          playlist_does_not_exist_error:
            'Non foi posible recuperar a lista de reprodución á que intentaches acceder.',
        },
      },
      playlist_item: {
        tabs: {details: 'Detalles', entities: 'Organizacións'},
        entities: {
          search_input_placeholder: 'Buscar organizacións',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_entities: 'Non existen organizacións.',
          enable_item_dialog_title: 'Queres activar o elemento “{{name}}” na lista de reprodución?',
          enable_item_dialog_message:
            'Ao activar o elemento da lista de reprodución, estará dispoñible para a organización “{{name}}”.',
          enable_item_dialog_action_yes_button: 'Si, activar',
          enable_item_dialog_action_no_button: 'Cancelar',
          disable_item_dialog_title:
            'Queres desactivar o elemento “{{name}}” na lista de reprodución?',
          disable_item_dialog_message:
            'Ao desactivar o elemento da lista de reprodución, deixará de estar dispoñible para a organización “{{name}}”.',
          disable_item_dialog_action_yes_button: 'Si, desactivar',
          disable_item_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_item_error_message:
              'Produciuse un erro. Non foi posible activar o elemento na lista de reprodución.',
            disable_item_error_message:
              'Produciuse un erro. Non foi posible activar o elemento na lista de reprodución.',
            update_state_item_error_message:
              'Produciuse un erro. Non foi posible actualizar o estado do elemento na lista de reprodución.',
          },
        },
        errors: {
          item_does_not_exist_error: 'Non foi posible recuperar o elemento ao que querías acceder.',
        },
      },
    },
    professional_caregiver: {
      root: {
        search_input_placeholder: 'Buscar listas de reprodución',
        type_filter: 'Tipo',
        name_header: 'Nome',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_playlists: 'Non existen listas de reprodución.',
        enable_playlist_dialog_title: 'Activar a lista de reprodución “{{name}}”?',
        enable_playlist_dialog_message:
          'Se activas a lista de reprodución, estará dispoñible para todos os usuarios da organización {{name}}.',
        enable_playlist_dialog_action_yes_button: 'Si, activar',
        enable_playlist_dialog_action_no_button: 'Cancelar',
        disable_playlist_dialog_title: 'Activar a lista de reprodución “{{name}}”?',
        disable_playlist_dialog_message:
          'Se desactivas a lista de reprodución, deixará de estar dispoñible para todos os usuarios da organización {{name}}.',
        disable_playlist_dialog_action_yes_button: 'Si, desactivar',
        disable_playlist_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_playlist_error_message:
            'Produciuse un erro. Non foi posible activar a lista de reprodución.',
          disable_playlist_error_message:
            'Produciuse un erro. Non foi posible desactivar a lista de reprodución.',
          update_state_playlist_error_message:
            'Produciuse un erro. Non foi posible actualizar o estado da lista de reprodución.',
        },
      },
      edit: {
        tabs: {
          items: 'Elementos',
          users: 'Usuarios',
          details: 'Detalles',
        },
        items: {
          search_input_placeholder: 'Buscar elementos',
          title_header: 'Título',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_items: 'Non hai elementos na lista de reprodución.',
          enable_item_dialog_title: 'Queres activar o elemento “{{name}}” na lista de reprodución?',
          enable_item_dialog_message:
            'Cando actives o elemento na lista de reprodución, estará dispoñible para todos os usuarios da organización {{name}}.',
          enable_item_dialog_action_yes_button: 'Si, activar',
          enable_item_dialog_action_no_button: 'Cancelar',
          disable_item_dialog_title:
            'Queres desactivar o elemento “{{name}}” na lista de reprodución?',
          disable_item_dialog_message:
            'Cando desactives o elemento na lista de reprodución, deixará de estar dispoñible para todos os usuarios da organización {{name}}.',
          disable_item_dialog_action_yes_button: 'Si, desactivar',
          disable_item_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_item_error_message:
              'Produciuse un erro. Non foi posible activar o elemento na lista de reprodución.',
            disable_item_error_message:
              'Produciuse un erro. Non foi posible activar o elemento na lista de reprodución.',
            update_state_item_error_message:
              'Produciuse un erro. Non foi posible actualizar o estado do elemento na lista de reprodución.',
          },
        },
        users: {
          search_input_placeholder: 'Buscar usuarios',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_users: 'Non existen usuarios.',
          enable_playlist_dialog_title: 'Activar a lista de reprodución “{{name}}”?',
          enable_playlist_dialog_message:
            'Se activas a lista de reprodución, estará dispoñible para {{name}}.',
          enable_playlist_dialog_action_yes_button: 'Si, activar',
          enable_playlist_dialog_action_no_button: 'Cancelar',
          disable_playlist_dialog_title: 'Activar a lista de reprodución “{{name}}”?',
          disable_playlist_dialog_message:
            'Cando desactives a lista de reprodución, deixará de estar dispoñible para {{name}}.',
          disable_playlist_dialog_action_yes_button: 'Si, desactivar',
          disable_playlist_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_playlist_error_message:
              'Produciuse un erro. Non foi posible activar a lista de reprodución.',
            disable_playlist_error_message:
              'Produciuse un erro. Non foi posible desactivar a lista de reprodución.',
            update_state_playlist_error_message:
              'Produciuse un erro. Non foi posible actualizar o estado da lista de reprodución.',
          },
        },
        errors: {
          playlist_does_not_exist_error:
            'Non foi posible recuperar a lista de reprodución á que intentaches acceder.',
        },
      },
      playlist_item: {
        tabs: {
          users: 'Usuarios',
          details: 'Detalles',
        },
        users: {
          search_input_placeholder: 'Buscar usuarios',
          name_header: 'Nome',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_users: 'Non existen usuarios.',
          enable_item_dialog_title: 'Queres activar o elemento “{{name}}” na lista de reprodución?',
          enable_item_dialog_message:
            'Cando actives o elemento na lista de reprodución, estará dispoñible para {{name}}.',
          enable_item_dialog_action_yes_button: 'Si, activar',
          enable_item_dialog_action_no_button: 'Cancelar',
          disable_item_dialog_title:
            'Queres desactivar o elemento “{{name}}” na lista de reprodución?',
          disable_item_dialog_message:
            'Cando desactives o elemento na lista de reprodución, deixará de estar dispoñible para {{name}}.',
          disable_item_dialog_action_yes_button: 'Si, desactivar',
          disable_item_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_item_error_message:
              'Produciuse un erro. Non foi posible activar o elemento na lista de reprodución.',
            disable_item_error_message:
              'Produciuse un erro. Non foi posible activar o elemento na lista de reprodución.',
            update_state_item_error_message:
              'Produciuse un erro. Non foi posible actualizar o estado do elemento na lista de reprodución.',
          },
        },
        errors: {
          item_does_not_exist_error: 'Non foi posible recuperar o elemento ao que querías acceder.',
        },
      },
    },
  },
  user_playlists: {
    search_input_placeholder: 'Buscar listas de reprodución',
    type_filter: 'Tipo',
    entity_filter: 'Organización',
    name_header: 'Nome',
    state_header: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    state_disabled_tooltip: 'Non tes permisos para editar o estado da lista de reprodución',
    no_playlists: 'Non existen listas de reprodución.',
    no_affiliations_info_message:
      'O usuario non ten afiliación con ningunha organización. Polo tanto, non é posible ver as listas de reprodución do usuario.',
    enable_playlist_dialog_title:
      'Seguro que queres activar a lista de reprodución para “{{name}}”?',
    enable_playlist_dialog_message:
      'Se activas a lista de reprodución, estará dispoñible para {{name}}.',
    enable_playlist_for_entity_dialog_message:
      'Cando actives a lista de reprodución, estará dispoñible para {{name}} na organización {{entity}}.',
    enable_playlist_dialog_action_yes_button: 'Si, activar',
    enable_playlist_dialog_action_no_button: 'Cancelar',
    disable_playlist_dialog_title:
      'Seguro que queres desactivar a lista de reprodución para “{{name}}”?',
    disable_playlist_dialog_message:
      'Cando desactives a lista de reprodución, deixará de estar dispoñible para {{name}}.',
    disable_playlist_for_entity_dialog_message:
      'Ao desactivar a lista de reprodución, deixará de estar dispoñible para {{name}} na organización {{entity}}.',
    disable_playlist_dialog_action_yes_button: 'Si, desactivar',
    disable_playlist_dialog_action_no_button: 'Cancelar',
    errors: {
      enable_playlist_error_message:
        'Produciuse un erro. Non foi posible activar a lista de reprodución.',
      disable_playlist_error_message:
        'Produciuse un erro. Non foi posible desactivar a lista de reprodución.',
      update_state_playlist_error_message:
        'Produciuse un erro. Non foi posible actualizar o estado da lista de reprodución.',
    },
  },
  time_lapse_types: {
    [TimeLapseType.Custom]: 'Personalizado',
    [TimeLapseType.Last180Days]: 'Últimos 180 días',
    [TimeLapseType.Last30Days]: 'Últimos 30 días',
    [TimeLapseType.Last365Days]: 'Últimos 365 días',
    [TimeLapseType.LastMonth]: 'O mes pasado',
    [TimeLapseType.LastWeek]: 'A semana pasada',
    [TimeLapseType.ThisMonth]: 'Este mes',
    [TimeLapseType.ThisWeek]: 'Esta semana',
  },
  time_lapse: {
    dialog_title: 'Escolle un intervalo de tempo',
    range_label: 'Tipo de intervalo',
    start_text: 'Comezar',
    end_text: 'Fin',
    to_text: 'ata',
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
  },
  device_health: {
    no_data: 'Non existen datos.',
    last_recording: 'Último rexistro',
    basic_information_section_title: 'Información básica',
    wifi_section_title: 'Wifi',
    networks_wifi_subsection_title: 'Redes dispoñibles',
    current_wifi_subsection_title: 'Redes conectadas',
    mobile_data_section_title: 'Datos móbiles',
    graphics_section_title: 'Gráficos',
    controllers_graphics_subsection_title: 'Controladores',
    displays_graphics_subsection_title: 'Pantallas',
    system_section_title: 'Sistema',
    battery_section_title: 'Batería',
    disks_section_title: 'Discos',
    operating_system_section_title: 'Sistema operativo',
    cpu_section_title: 'CPU',
    memory_section_title: 'Memoria',
    applications_section_title: 'Aplicacións',
    versions_section_title: 'Versións',
    request_tracking_section_title: 'Supervisión',
    cache_images_section_title: 'Caché (imaxes)',
    creation_date_label: 'Data de creación',
    expiration_date_label: 'Data de caducidade',
    entity_label: 'Organización',
    user_label: 'Usuario',
    data_usage_label: 'Consumo de datos',
    ssid_label: 'SSID',
    quality_label: 'Calidade',
    ip_address_label: 'Enderezo IP',
    is_data_usage_enabled_label: 'O consumo de datos móbiles está activado?',
    carrier_label: 'Operador',
    network_type_label: 'Tipo de rede',
    dialer_label: 'Marcador',
    signal_strength_label: 'Intensidade do sinal',
    signal_strength_level_label: 'Nivel de intensidade do sinal',
    signal_strength_no: 'Sen sinal',
    signal_strength_weak: 'Débil',
    signal_strength_fair: 'Razoable',
    signal_strength_good: 'Boa',
    signal_strength_strong: 'Forte',
    brand_label: 'Marca',
    model_label: 'Modelo',
    is_main_display_label: 'É esta a pantalla principal?',
    width_label: 'Anchura',
    height_label: 'Altura',
    current_width_label: 'Anchura real',
    current_height_label: 'Altura real',
    current_scale_factor_label: 'Factor de escala',
    sku_label: 'SKU',
    serial_number_label: 'Número de serie',
    version_label: 'Versión',
    has_battery_label: 'Ten batería?',
    is_battery_charging_label: 'Está cargando?',
    is_battery_connected_to_power_label: 'Enchufouse?',
    battery_level_label: 'Estado',
    size_label: 'Total',
    used_disks_label: 'Usouse',
    available_label: 'Dispoñible',
    mount_label: 'Punto de montaxe',
    name_label: 'Nome',
    distribution_label: 'Distribución',
    release_label: 'Lanzamento',
    build_label: 'Compilación',
    web_view_version_label: 'Versión de WebView',
    cores_label: 'Número de núcleos',
    used_memory_label: 'Usouse',
    country_label: 'País',
    region_label: 'Rexión',
    city_label: 'Cidade',
    city_longitude_label: 'Lonxitude',
    city_latitude_label: 'Latitude',
    language_label: 'Idioma',
    user_ip_address_label: 'Enderezo IP do usuario',
    yes_label: 'Si',
    no_label: 'Non',
    gb_unit_label: 'GB',
    mb_unit_label: 'MB',
    px_unit_label: 'px',
    agent_label: 'Axente',
    chrome_label: 'Chrome',
    electron_label: 'Electron',
    launcher_binary_label: 'Lanzador binario',
    launcher_frontend_label: 'Lanzador frontal',
  },
  operative_system_names: {
    [OperativeSystemName.Android]: 'Android',
    [OperativeSystemName.Linux]: 'Linux',
    [OperativeSystemName.MacOS]: 'MacOS',
    [OperativeSystemName.Other]: 'Outros',
    [OperativeSystemName.Windows]: 'Windows',
    [OperativeSystemName.iOS]: 'iOS',
  },
  mobile_network_types: {
    [MobileNetworkType.Unspecified]: 'Outros',
    [MobileNetworkType._1G]: '1G',
    [MobileNetworkType._2G]: '2G',
    [MobileNetworkType._3G]: '3G',
    [MobileNetworkType._4G]: '4G',
    [MobileNetworkType._5G]: '5G',
  },
  home: {
    refreshing: 'Actualizando...',
    release_to_refresh: 'Soltar para actualizar',
    pull_to_refresh: 'Tirar para actualizar',
    none: {
      root: {
        no_associations_message_title:
          'A túa conta de acceso á plataforma de {{brand}} non ten ningunha vinculación con ningunha persoa maior nin organización neste momento.',
        no_associations_message_subtitle:
          'Solicita unha invitación a outros coidadores ou ponte en contacto co servizo de atención ao cliente.',
      },
    },
  },
  localized_string_input: {
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
    has_zero_translations_tooltip_message:
      'Non existen traducións definidas do total de {{total}} posibles traducións.',
    has_one_translation_tooltip_message:
      'Defínese unha tradución dun total de {{total}} posibles traducións.',
    has_many_translations_tooltip_message:
      'Existen {{count}} traducións definidas do total de {{total}} posibles traducións.',
  },
  user_playlist_items: {
    title: 'Lista de reprodución “{{name}}”',
    search_input_placeholder: 'Buscar elementos',
    title_header: 'Título',
    state_header: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    state_disabled_tooltip: 'Non tes permisos para editar o estado do elemento',
    no_items: 'Non hai elementos na lista de reprodución.',
    enable_item_dialog_title:
      'Seguro que queres activar o elemento “{{name}}” na lista de reprodución?',
    enable_item_dialog_message:
      'Cando actives o elemento na lista de reprodución, estará dispoñible para {{name}}.',
    enable_item_dialog_action_yes_button: 'Si, activar',
    enable_item_dialog_action_no_button: 'Cancelar',
    disable_item_dialog_title:
      'Seguro que queres desactivar o elemento “{{name}}” na lista de reprodución?',
    disable_item_dialog_message:
      'Cando desactives o elemento na lista de reprodución, deixará de estar dispoñible para {{name}}.',
    disable_item_dialog_action_yes_button: 'Si, desactivar',
    disable_item_dialog_action_no_button: 'Cancelar',
    errors: {
      enable_item_error_message:
        'Produciuse un erro. Non foi posible activar o elemento na lista de reprodución.',
      disable_item_error_message:
        'Produciuse un erro. Non foi posible activar o elemento na lista de reprodución.',
      update_state_item_error_message:
        'Produciuse un erro. Non foi posible actualizar o estado do elemento na lista de reprodución.',
      playlist_does_not_exist_error:
        'Non foi posible recuperar a lista de reprodución á que intentaches acceder.',
    },
  },
  playlist_form: {
    exit_button: 'Saír',
    create_playlist_title: 'Engadir lista de reprodución',
    save_button: 'Gardar',
    saving_button: 'Gardando...',
    default_name_input: 'Nome',
    name_translations_input: 'Traducións para o nome',
    default_description_input: 'Descrición',
    description_translations_input: 'Traducións para a descrición',
    enabled_state_color_input: 'Cor de estado activada',
    pressed_state_color_input: 'Cor de estado premida',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    type_input: 'Tipo',
    active_by_default_for_all_entities_input:
      'Activar de forma predeterminada para todas as organizacións?',
    active_by_default_for_all_entities_placeholder_input: 'Selecciona unha opción',
    yes_value: 'Si',
    no_value: 'Non',
    image_url_input: 'Imaxe',
    image_info_message:
      'A imaxe debe estar en formato JPG/JPEG. Para obter mellores resultados, debes usar unha resolución de 300 x 300 px.',
    regional_lockout_input: 'Bloqueo rexional',
    regional_lockout_placeholder: 'Seleccionar rexións',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar lista de reprodución',
    delete_dialog_title: 'Seguro que queres eliminar a lista de reprodución “{{name}}”?',
    delete_dialog_content:
      'Se eliminas a lista de reprodución, eliminarase permanentemente do sistema.',
    delete_dialog_action_yes_button: 'Si, eliminar',
    delete_dialog_action_no_button: 'Cancelar',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Gardar',
    router_prompt_create_title:
      'Seguro que queres saír sen gardar a lista de reprodución “{{name}}”?',
    router_prompt_create_message:
      'Ao pechar sesión, a lista de reprodución non se gardará e eliminarase toda a información introducida nela.',
    router_prompt_edit_title:
      'Seguro que queres saír sen gardar os cambios na lista de reprodución “{{name}}”?',
    router_prompt_edit_message:
      'Ao pechar sesión, perderás todos os cambios que introduciches recentemente na lista de reprodución.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Si, pechar sesión',
    edit_cancel_toast: 'Cancelouse a edición da lista de reprodución “{{name}}”.',
    create_cancel_toast: 'Cancelouse a creación da nova lista de reprodución.',
    success: {
      save_success_button: 'Gardado!',
      delete_success_button: 'Eliminouse!',
      create_success_message: 'A lista de reprodución “{{name}}” creouse con éxito!',
      edit_success_message: 'Gardáronse os cambios na lista de reprodución.',
    },
    errors: {
      error_on_save_button: 'Produciuse un erro ao gardar',
      error_on_delete_button: 'Erro ao eliminar',
      edit_form_validation_invalid_field:
        'Cubre este campo para completar a edición da lista de reprodución',
      create_form_validation_invalid_field:
        'Cubre este campo para completar a creación da lista de reprodución',
      form_validation_invalid_color: 'A cor indicada non é válida',
    },
  },
  playlist_item_form: {
    exit_button: 'Saír',
    create_playlist_item_title: 'Engadir elemento na lista de reprodución',
    save_button: 'Gardar',
    saving_button: 'Gardando...',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar elemento da lista de reprodución',
    delete_dialog_title:
      'Seguro que queres eliminar o elemento da lista de reprodución “{{name}}”?',
    delete_dialog_content:
      'Se eliminas o elemento da lista de reprodución, eliminarase permanentemente do sistema.',
    delete_dialog_action_yes_button: 'Si, eliminar',
    delete_dialog_action_no_button: 'Cancelar',
    create_dialog_title: 'Gardar a creación do elemento “{{name}}” na lista de reprodución?',
    create_dialog_message:
      'Ao gardar, o novo elemento da lista de reprodución engadirase ao sistema.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Gardar',
    router_prompt_create_title:
      'Seguro que queres pechar sesión sen gardar a creación do elemento “{{name}}” na lista de reprodución?',
    router_prompt_create_message:
      'Ao pechar sesión, o elemento non se gardará na lista de reprodución e eliminarase toda a información introducida nel.',
    router_prompt_edit_title:
      'Seguro que queres pechar sesión sen gardar os cambios no elemento “{{name}}” da lista de reprodución?',
    router_prompt_edit_message:
      'Ao pechar sesión, perderás todos os cambios que fixeches recentemente no elemento da lista de reprodución.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Si, pechar sesión',
    edit_cancel_toast: 'Cancelouse a edición do elemento da lista de reprodución “{{name}}”.',
    create_cancel_toast: 'Cancelouse a creación do novo elemento na lista de reprodución.',
    default_title_input: 'Título',
    title_translations_input: 'Traducións para o título',
    default_description_input: 'Descrición',
    description_translations_input: 'Traducións para a descrición',
    regional_lockout_input: 'Bloqueo rexional',
    regional_lockout_placeholder: 'Seleccionar rexións',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    playlist_input: 'Lista de reprodución',
    resource_url_input: 'URL do recurso',
    resource_input: 'Recurso',
    thumbnail_image_url_input: 'Imaxe',
    thumbnail_image_info_message:
      'A imaxe debe estar en formato JPG/JPEG. Para obter mellores resultados, debes usar unha resolución de 300 x 300 px.',
    success: {
      save_success_button: 'Gardado!',
      delete_success_button: 'Eliminouse!',
      create_success_message:
        'O elemento “{{name}}” creouse correctamente na lista de reprodución!',
      edit_success_message: 'Gardáronse os cambios no elemento da lista de reprodución.',
    },
    errors: {
      error_on_save_button: 'Produciuse un erro ao gardar',
      error_on_delete_button: 'Erro ao eliminar',
      edit_form_validation_invalid_field: 'Cubre este campo para completar a edición do elemento',
      create_form_validation_invalid_field:
        'Cubre este campo para completar a creación do elemento',
      form_validation_invalid_resource_url: 'O URL do recurso indicado non é válido',
    },
  },
  licenses: {
    professional_caregiver: {
      root: {
        refresh_button: 'Actualizar',
        number_of_monthly_active_licenses_title: 'Licenzas mensuais activas',
        number_of_active_seniors_title: 'Persoas maiores activas',
        number_of_monthly_active_licenses_not_defined: 'Sen definir',
      },
    },
  },
  videocall: {
    you: 'Eu,',
    incomingCall: 'Chámante...',
    incomingCallConnecting: 'Conectando..',
    incomingCallConnectingHint: '(pode tardar un pouco)',
    answer: 'Responder',
    decline: 'Rexeitar',
    settingsTitle: 'Configuracións',
    settingsMicrophone: 'Micrófono',
    settingsCamera: 'Cámara',
    settingsSpeaker: 'Altofalante',
    default: 'Estándar',
    callFailed: {
      title: 'Fallou a chamada',
      ok: 'Está ben',
      e486: 'O número de destino está ocupado',
      e487: 'Aplicación pechada',
      e603: 'Rexeitouse a chamada',
      e404: 'Número non válido',
      e480: 'O número de destino non está dispoñible',
      e402: 'Fondos insuficientes',
    },
  },
  entityFrontCardDesignTool: {
    title: 'Deseño de tarxeta frontal para a organización',
    step1: 'Organización',
    step2: 'Usuarios',
    step3: 'Resultado',
    entityPicture: 'Logotipo da organización',
    chooseAnotherEntityPicture: 'Escolle o logotipo doutra organización',
    entityName: 'Nome da organización',
    addEntityName: 'Engadir nome da organización',
    entityNameToBePrinted: 'Nome da organización para imprimir',
    entityNameTextColor: 'Cor do texto para o nome da organización',
    entityNameTextFont: 'Tipo de letra para o nome da organización',
    next: 'Seguinte',
    save: 'Gardar',
    restart: 'Reiniciar',
    failedToLoadUsers: 'Non se puideron cargar os usuarios',
  },
  entityBackCardDesignTool: {
    title: 'Deseño de tarxeta posterior para a organización',
    step1: 'Especificacións',
    step2: 'Vista previa',
    entityName: 'Nome da organización',
    entityNumber: 'Número de organización',
    seniorUsersCount: 'Número de persoas maiores',
    caregiverUsersCount: 'Número de coidadores',
    cardName: 'Nome da tarxeta',
    entityPictureHeight: 'Altura da imaxe para a organización',
    lineColor: 'Cor da liña',
    includeEntityName: 'Incluír o nome da organización',
    generatePreview: 'Xerar vista previa',
    saveCards: 'Gardar tarxetas',
    saveSpreadsheet: 'Gardar folla de cálculo',
    restart: 'Reiniciar',
  },
  cards: {
    pendingInvitation: 'Invitación pendente',
    years: 'anos',
  },
  dashboard_side_menu: {
    dashboard: 'Visión xeral',
    general_activity: 'Actividade xeral',
    statistics: 'Estatísticas',
    general_statistics: 'Estatísticas xerais',
    social_statistics: 'Estatísticas sociais',
  },
  digital_inclusion_side_menu: {
    applications: 'Aplicacións',
    details: 'Detalles',
    users: 'Usuarios',
    photos: 'Fotos',
    cards: 'Tarxetas',
    devices: 'Dispositivos',
    services: 'Servizo',
    playlists: 'Listas de reprodución',
  },
  seniorSideMenu: {
    generalStatistics: 'Estatísticas xerais',
    healthStatistics: 'Estatísticas sanitarias',
    basicActivitiesDailyLife: 'BADL',
    careAndActivity: 'Resumo de coidados',
    individualPlan: 'Plan individual',
    personalData: 'Datos persoais',
    careServices: 'Escala de Servizo',
  },
  teamSideMenu: {
    professionals: 'Profesionais',
    shifts: {
      title: 'Quenda',
      shiftsManagement: 'Xestión de quendas',
      shiftsAllocations: 'Asignación de quendas',
    },
    attendanceTracker: {
      title: 'Rexistro de asistencia',
      assignments: 'Asignacións',
      registry: 'Rexistros',
    },
    careServices: 'Escala Xeral de Servizo',
    permissions: 'Permisos',
    profileDropdown: {
      profile: 'Perfil',
      careServices: 'Escala de Servizo',
      applications: 'Aplicacións',
      cards: 'Tarxetas',
      playLists: 'Listas de reprodución',
    },
  },
  profileSideMenu: {
    profile: 'Perfil',
  },
  settingsSideMenu: {
    institution: 'Institución',
    activities_sectors: {
      load_error: 'Non foi posible cargar os sectores de actividade',
    },
  },
  responsibleUserDropdown: {
    noResponsibleDefined: 'Persoa responsable non definida',
    noCoordinatorDefined: 'Coordinador non definido',
  },
  activities_group_entry: {
    modals: {
      edit: {
        title: 'Editar detalles',
        observations_max_characters: 'Máximo 120 caracteres.',
      },
      confirm_delete: {
        title: 'Eliminar BADL {{ name }}',
        copy: {
          title: 'Seguro que queres eliminar a actividade?',
          subtitle: 'Esta acción eliminará a actividade da lista.',
        },
      },
    },
    activity_types: 'Tarefas',
    no_activity_types: 'Aínda non se engadiron tarefas',
    configuration_type_not_defined: 'Sen definir',
    configuration_type_label: 'Tipo',
    exceptions: 'Excepcións',
    edit_details: 'Editar detalles',
    add_activities: 'Engadir',
    configure_first: 'Primeiro configura o tipo de repetición para o grupo de actividades.',
    actions: {
      exit: 'Saír',
      edit: 'Editar',
      delete: 'Eliminar',
      history: 'Historia',
    },
    snackbar: {
      create: {
        success: 'A actividade engadiuse correctamente!',
        error: 'Non foi posible engadir a actividade.',
      },
      update: {
        success: 'A actividade editouse correctamente!',
        error: 'Non foi posible editar a actividade.',
      },
      delete: {
        success: 'A actividade eliminouse correctamente!',
        error: 'Non foi posible eliminar a actividade.',
      },
      configuration: {
        success: 'A actividade cargouse correctamente.',
        error: 'Non foi posible cargar a actividade.',
      },
    },
    error: 'Non foi posible cargar a actividade dos datos do grupo.',
  },
  activities_type_entry: {
    modals: {
      edit: {
        title: 'Detalles da tarefa',
      },
      delete: {
        title: 'Detalles: {{ type }}',
        copy: {
          title: 'Seguro que queres eliminar {{ type }} das actividades?',
          subtitle: 'A actividade eliminarase da lista de actividades da persoa maior.',
        },
      },
      view: {
        leave: 'Saír',
        view_only: 'Só visualización, non se pode editar.',
      },
    },
    snackbar: {
      update: {
        success: 'A tarefa editouse correctamente!',
        error: 'Non foi posible editar a tarefa.',
      },
      delete: {
        success: 'A tarefa eliminouse correctamente!',
        error: 'Non foi posible eliminar a tarefa.',
      },
      configuration: {
        success: 'A tarefa cargouse correctamente!',
        error: 'Non foi posible cargar a tarefa.',
      },
    },
    templates: {
      name: 'Nome do modelo',
      description: 'Descrición',
      activities_save: 'Actividades para gardar',
      activities_import: 'Seleccionar as actividades',
      check_all: 'Seleccionar todo',
      snackbar: {
        success_message:
          'O modelo “{{name}}” gardouse correctamente. Podes editar ou eliminar modelos na configuración.',
        error_message: 'Non se puido gardar o modelo “{{name}}”.',
        import_success_message: 'O modelo “{{name}}” aplicouse correctamente.',
        import_error_message: 'Non se puido aplicar o modelo “{{name}}”.',
      },
      template: 'Modelo',
      confirmation_title: 'O modelo “{{name}}” seleccionado usarase para este usuario',
      confirmation_subtitle:
        'Podes editar ou eliminar as actividades seleccionadas en calquera momento',
      confirmation_replace_title:
        'Ao importar o modelo “{{name}}”, os datos do plan actual sobrescribiranse',
      confirmation_replace_subtitle: 'Para continuar, introduce a palabra “CONFIRMAR”',
      confirm: 'Confirmo',
      name_error: 'O nome do modelo ten polo menos {{max}} caracteres',
      description_error: 'A descrición do modelo debe ter polo menos {{max}} caracteres',
      select: 'Seleccionar un modelo',
    },
  },
  dynamic_form: {
    required_message: 'Campo obrigatorio.',
    pattern_message: {
      invalid: 'Campo non válido.',
      max_characters: 'Máximo {{ max }} caracteres.',
    },
    error_loading: 'Produciuse un erro ao cargar o formulario.',
    feature_fields: {
      autocomplete: {
        load_more: 'Cargar máis',
        loading: 'Cargando...',
        no_options: 'Sen opcións',
        placeholder: 'Seleccionar un elemento',
        counter: 'Para mostrar {{ current }} de {{ max }} elementos',
      },
    },
  },
  modal: {
    ok: 'Vale',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    add: 'Engadir',
    save: 'Gardar',
    remove: 'Eliminar',
    import: 'Importar',
    back: 'Atrás',
    next: 'Seguinte',
    delete: 'Eliminar',
    revert: 'Reverter',
    archive: 'Arquivo',
  },
  daily_care_log: {
    generics: {
      back: 'Atrás',
    },
    senior_actions: {
      open_actions: 'Abrir accións de {{name}}',
      title: 'Accións para {{name}}',
    },
    tabs: {
      home: 'Comezar',
      occurrences: 'Incidentes',
      attendance_tracker: 'Fichaxe',
    },
    date_carousel: {
      yesterday: 'Onte',
      today: 'Hoxe',
      tomorrow: 'Mañá',
    },
    task_table: {
      tasks: 'Tarefas',
      empty: 'Non existen tarefas rexistradas.',
      select_all: 'Seleccionar todos os usuarios',
      sort: 'Ordenar',
      drawer: {
        clear: 'Claro',
        save: 'Gardar',
        filters: {
          title: 'Filtros',
          social_responses: 'Servizos sociais',
        },
        sort: {
          title: 'Ordenar por',
          name: 'Nome',
          autonomy: 'Independencia',
          status: 'Estado',
          ascending: 'Ascendente',
          descending: 'Descendente',
        },
      },
    },
    cards: {
      entity_user: {
        task_conclusion_summary_label: {
          concluded: 'Concluíu',
          task: 'Tarefa',
        },
      },
    },
    home: {
      my_shifts: 'As miñas quendas',
      care_services: 'Escala de servizos',
      other_shifts: 'Outras quendas',
      no_allocated_shifts: 'Non existen quendas asignadas para este día',
      no_other_shifts: 'Non existen outras quendas para este día',
      no_care_services: 'Non existen servizos asignados para este día',
      previous_days_shift: 'A quenda comezou o día anterior',
    },
    seniors: {
      title: 'Persoas maiores',
      no_seniors: 'Aínda non existen persoas maiores rexistradas',
    },
    task_details: {
      task_not_done_reason: 'Razón para non traballar',
      task_done: 'Realizado',
      task_not_done: 'Sen realizar',
      add_a_note: 'Introduce unha observación...',
      cancel: 'Cancelar',
      save: 'Gardar',
      register: 'Rexistrar',
      edit: 'Editar',
      edit_entry: 'Editar rexistro',
      disabled_tooltip: 'Non tes permisos para editar esta tarefa.',
    },
    shift_collapsible: {
      shift: 'Quenda',
      other_shift_members: 'Outras persoas na quenda',
      no_other_shift_members: 'Non existe outra xente na quenda',
    },
    shift_details: {
      all_tasks_handled: 'Completáronse todas as tarefas da quenda!',
      tabs: {
        simple: {
          title: 'Simple',
        },
        grouped: {title: 'En grupo', task: 'Tarefa'},
        user: {
          title: 'Usuario',
          grid: {
            empty: 'Sen usuarios',
            empty_search_message: {
              title: 'Sentímolo, non se atoparon resultados para “{{ name }}”.',
              subtitle: 'Proba unha busca diferente.',
            },
          },
        },
      },
      unplanned_tasks: {
        header_title: 'Nova tarefa non planificada',
        activity_sector: 'Sector de actividade',
        activity: 'Actividade',
        name: 'Nome da tarefa',
        execution_date: 'Data de implementación',
        execution_hour: 'Hora de implementación',
        add_success_toast: 'A tarefa rexistrouse correctamente!',
        add_error_toast: 'Non foi posible rexistrar a tarefa.',
        upload_error_toast: 'Non foi posible cargar os ficheiros.',
        add_multiple_success_toast: 'As tarefas rexistráronse correctamente!',
        add_cancel_modal_title: 'Seguro que queres saír sen rexistrar a tarefa?',
        add_cancel_modal_subtitle: 'Os datos introducidos perderanse.',
        add_user_placeholder: 'Engadir o usuario',
        add_files: 'Engadir ficheiros',
        delete_attachment: 'Seguro que queres eliminar o ficheiro para “{{ name }}”?',
        files_size: 'Tamaño máximo por ficheiro: {{ size }} MB.',
        file_size_error: 'O tamaño supera o máximo de {{ size }} MB.',
        file_not_supported_error: 'Formato de ficheiro non compatible.',
        file_both_errors: 'O tamaño supera o máximo de {{ size }} MB e o formato non é compatible.',
      },
    },
    grouped_tasks_details: {
      select_bottom_bar: {
        count: '{{ count }} usuario seleccionado',
        count_plural: '{{ count }} usuarios seleccionados',
        next: 'Seguinte',
      },
    },
    grouped_tasks_handling: {
      snackbar: {
        loading_tasks: {
          error: 'Erro ao cargar as tarefas!',
        },
        updating_tasks: {
          success: 'As tarefas rexistráronse correctamente!',
          error: 'Produciuse un erro ao rexistrar as tarefas.',
        },
      },
    },
    attendance_tracker: {
      title: 'Rexistro de asistencia',
      empty_title: 'Aínda non hai rexistros de asistencia',
      empty_subtitle: 'Preme o botón para fichar',
      register: 'Fichar',
      filters: {
        sections: {
          attendanceMovements: 'Movemento',
          attendanceStatus: 'Estado',
          attendancePeriods: 'Franxa do día',
        },
        values: {
          voided: 'Anulado',
          entry: 'Entradas',
          exit: 'Saídas',
          open: 'En curso',
          closed: 'Completado',
          morning: 'Mañá',
          afternoon: 'Tarde',
          night: 'Noite',
        },
      },
      session: {
        entry: 'Entrada',
        exit: 'Saída',
        running: 'En curso agora',
        overdue: 'Por pechar',
        closed: 'Completado',
        elapsed: '{{duration}} transcorridos',
        overdue_alert: 'Tes unha entrada aberta desde {{date}}. Hai que rexistrar a saída.',
        total: 'Total: {{duration}}',
      },
      bar: {
        running: 'En curso',
        since: 'Desde {{date}}',
        not_started: 'Rexistro de xornada sen iniciar',
      },
      manual: {
        registered_by: 'Rexistrado por {{name}}',
        entry_registered_by: 'Entrada rexistrada por {{name}}',
        exit_registered_by: 'Saída rexistrada por {{name}}',
        notice: 'Rexistrado manualmente por {{name}} o {{date}}, en nome do profesional.',
        reason: 'Motivo: {{reason}}',
        no_location: 'Sen localización (rexistro manual)',
        unknown_user: 'outro usuario',
      },
      voided: {
        tag: 'Anulado',
        notice:
          'Esta sesión foi anulada por {{name}} o {{date}} e xa non conta como tempo traballado.',
        reason: 'Motivo: {{reason}}',
      },
      register_entry: 'Rexistrar entrada',
      register_exit: 'Rexistrar saída',
      load_error: 'Non se puideron obter os teus rexistros de asistencia.',
      empty_other_day: 'Non hai rexistros de asistencia este día.',
      details: {
        no_exit: 'Aínda non se rexistrou a saída.',
        open_in_maps: 'Abrir en Google Maps',
        time_zone: 'Fuso horario',
      },
      new: {
        title_entry: 'Rexistrar entrada',
        title_exit: 'Rexistrar saída',
        save_entry: 'Rexistrar',
        save_exit: 'Rexistrar',
        record_type: 'Tipo de rexistro',
        entry: 'Entrada',
        exit: 'Saída',
        professional: 'Profesional',
        entity: 'Entidade',
        date_time: 'Data e hora',
        location: 'Localización',
        latitude: 'Latitude',
        longitude: 'Lonxitude',
        accuracy: 'Precisión',
        accuracy_value: '± {{meters}} m',
        waiting: 'Obtendo a túa localización exacta...',
        accuracy_info:
          'A precisión actual é de ± {{current}} m. É a mellor que o dispositivo pode obter agora e vaise actualizando. Nun espazo interior, achégate a unha xanela ou sae ao exterior para mellorala.',
        unavailable:
          'Non se puido obter a túa localización. Comproba que o GPS do dispositivo está activado.',
        loading_error:
          'Non se puido obter o teu último rexistro de asistencia. Comproba a conexión e téntao de novo.',
        retry: 'Tentar de novo',
        success_entry: 'Entrada rexistrada con éxito!',
        success_exit: 'Saída rexistrada con éxito!',
        type_conflict:
          'O teu último rexistro de asistencia cambiou. Comproba o tipo de rexistro e rexístrao de novo.',
        save_error: 'Non se puido rexistrar a asistencia. Téntao de novo.',
        purpose:
          'A túa localización exacta recóllese só no momento en que rexistras a asistencia, para comprobar o lugar onde se rexistrou. Estes datos son persoais e quedan asociados ao teu rexistro.',
        permission: {
          prompt_title: 'Necesitamos a túa localización',
          prompt_description:
            'Para rexistrar a asistencia hai que comprobar onde te atopas. A localización só se solicita nesta pantalla e gárdase unicamente no momento do rexistro.',
          allow: 'Permitir localización',
          denied_title: 'A localización está bloqueada',
          denied_description:
            'Denegouse o acceso á localización neste navegador, polo que non é posible rexistrar a asistencia. Para volver permitilo:',
          unsupported_title: 'Localización non dispoñible',
          unsupported_description:
            'Este navegador non admite a localización ou a páxina non se abre nunha conexión segura (HTTPS).',
          help: {
            chrome:
              'Fai clic na icona á esquerda do enderezo (cadeado ou axustes), elixe «Configuración do sitio» e, en «Localización», selecciona «Permitir». Despois, recarga a páxina.',
            edge: 'Fai clic no cadeado á esquerda do enderezo, abre «Permisos deste sitio» e, en «Localización», selecciona «Permitir». Despois, recarga a páxina.',
            firefox:
              'Fai clic na icona de permisos á esquerda do enderezo, quita o bloqueo de «Acceder á túa localización» e permíteo. Despois, recarga a páxina.',
            safari:
              'Abre «Safari» > «Axustes» > «Sitios web» > «Localización», elixe este sitio e selecciona «Permitir». Despois, recarga a páxina.',
            ios: 'Abre «Axustes» > «Privacidade e seguridade» > «Localización», actívaa e permite o acceso ao navegador que estás a usar. Despois, recarga a páxina.',
            android_chrome:
              'Toca a icona á esquerda do enderezo, abre «Permisos» > «Localización» e selecciona «Permitir». Se o bloqueo é do teléfono, activa a localización en «Axustes» > «Localización». Despois, recarga a páxina.',
            generic:
              'Abre os axustes de permisos do sitio no teu navegador, permite o acceso á localización e recarga a páxina.',
          },
        },
      },
    },
    occurrences: {
      empty_title: 'Aínda non existen incidentes',
      empty_filters_title: 'Non existen incidentes para os filtros seleccionados',
      empty_subtitle: 'Preme o botón para engadir un novo incidente',
      new: 'Novo',
      new_occurrence: 'Novo incidente',
      add: 'Engadir',
      type: 'Tipo de incidente',
      date: 'Data do incidente',
      time: 'Hora do incidente',
      local: 'Localización do incidente',
      responsible: 'Persoa responsable do incidente',
      patient: 'Usuario implicado',
      details: 'Detalles do incidente',
      add_success_toast: 'O incidente engadiuse correctamente!',
      add_error_toast: 'Non foi posible engadir o incidente.',
      add_cancel_modal_title: 'Seguro que queres saír sen engadir o incidente?',
      add_cancel_modal_subtitle: 'Os datos introducidos perderanse.',
      local_error: 'A localización introducida non debe superar os {{ characters }} caracteres',
    },
  },
  care_and_activities: {
    date: 'Data',
    activity: 'Actividade',
    task: 'Tarefa',
    evaluation: 'Avaliación',
    responsible: 'Xestión a cargo de',
    status: 'Estado',
    empty_state: 'Non existen tarefas rexistradas.',
    permissions: 'Non tes permisos para ver as tarefas.',
    register: 'Rexistro',
    notes: 'Observacións',
    status_types: {
      [CaregiverTaskCompletion.DONE]: 'Realizado',
      [CaregiverTaskCompletion.NOT_DONE]: 'Sen realizar',
      [CaregiverTaskCompletion.PLANNED]: 'Planificado',
      [CaregiverTaskCompletion.MISSED]: 'Sen tratamento',
    },
    sort_icon: {
      sort_in_ascending: 'Ordenar en orde ascendente',
      sort_in_descending: 'Ordenar en orde descendente',
    },
    handle_task_modal: {
      task_status: {
        done: 'Realizado',
        not_done: 'Sen realizar',
      },
      details: 'Detalles',
      observations: 'Notas',
      treated: 'Procesado',
      cancel: 'Cancelar',
      write_observation: 'Introduce unha observación.',
      observation_required_message: 'Proporciona máis información sobre o motivo',
      card_collapse: {
        title: 'Información da tarefa',
      },
      time_input: 'Hora de implementación',
      date_input: 'Data de rexistro',
      select_date: 'Selecciona a data',
      select_time: 'Selecciona a hora',
      task_info_load_error: 'Non foi posible cargar a información da tarefa.',
      feature_input: {
        label: {
          [CareTaskFeature.BloodGlucoseHealthMeasurement]: 'Glicosa no sangue',
          [CareTaskFeature.BloodOxygenHealthMeasurement]: 'Osíxeno no sangue',
          [CareTaskFeature.BloodPressureHealthMeasurement]: 'Presión arterial (SIS-DIA/mmHg)',
          [CareTaskFeature.BodyFatPercentageHealthMeasurement]: 'Porcentaxe de graxa',
          [CareTaskFeature.BodyHeightHealthMeasurement]: 'Altura',
          [CareTaskFeature.BodyMusclePercentageHealthMeasurement]: 'Porcentaxe muscular',
          [CareTaskFeature.BodyTemperatureHealthMeasurement]: 'Temperatura corporal',
          [CareTaskFeature.BodyWaterPercentageHealthMeasurement]: 'Porcentaxe de auga',
          [CareTaskFeature.BodyWeightHealthMeasurement]: 'Peso',
          [CareTaskFeature.HeartRateHealthMeasurement]: 'Frecuencia cardíaca',
          [CareTaskFeature.NatriureticPeptideTestHealthMeasurement]:
            'Proba de péptidos natriuréticos',
          [CareTaskFeature.RespiratoryRateHealthMeasurement]: 'Frecuencia respiratoria',
          [CareTaskFeature.SleepHealthMeasurement]: 'Medición da saúde do sono',
          [CareTaskFeature.StepsHealthMeasurement]: 'Pasos',
        },
        inputs: {
          value: {
            label: 'Cantidade',
            placeholder: 'Introduce unha cantidade',
          },
          minutes: {
            label: 'Minutos',
            placeholder: 'Introduce os minutos',
          },
          percentage: {
            label: 'Porcentaxe',
            placeholder: 'Introduce unha porcentaxe',
          },
          mealTime: {
            label: 'Hora da comida',
            placeholder: 'Selecciona unha hora',
            options: {
              'before-meal': 'Antes da comida',
              'after-meal': 'Despois da comida',
            },
          },
          bnp: {
            label: 'Péptido natriurético',
            placeholder: 'Selecciona un valor',
          },
          nt_proBnp: {
            label: 'Fracción N-terminal do propéptido natriurético tipo B',
            placeholder: 'Selecciona un valor',
          },
          type: {
            label: 'Tipo',
            placeholder: 'Selecciona un tipo',
            options: {
              'in-bed': 'Na cama',
              awake: 'De acordo',
              rem: 'REM',
              core: 'Núcleo',
              deep: 'Profundo',
            },
          },
          startTime: {
            label: 'Hora de inicio',
          },
          endTime: {
            label: 'Hora de finalización',
          },
        },
        units: {
          steps: 'pasos',
        },
        required_message: 'Obrigatorio',
        number_pattern_validation: {
          min: 'Mín. {{ min_suffix }}',
          max: 'Máx. {{ max_suffix }}',
        },
      },
      undo_task: 'Desfacer tarefa',
      undo: 'Desfacer',
      edit_entry: 'Editar rexistro',
      confirm: 'Confirmar',
      snackbar: {
        update: {
          success: 'A tarefa rexistrouse correctamente!',
          error: 'Produciuse un erro ao actualizar o rexistro de tarefas.',
        },
        reset: {
          success: 'A tarefa volveu ao seu estado inicial!',
          error: 'Produciuse un erro ao restablecer o rexistro de tarefas.',
        },
      },
      history: {
        title: 'Historia',
        update: 'editou a tarefa',
        status: 'Estado',
        someone: 'Alguén ',
        remove: 'desfixo a tarefa',
        auto: 'O sistema informou da tarefa como feita',
        validate: 'validou a tarefa',
        create: 'creou a tarefa',
        read: 'leu a tarefa',
        contract_update: 'actualizou o contrato',
        changes: 'Cambios',
        task_not_done_reason: 'Motivo da non implementación',
        notes: 'Notas',
        files_added: 'Ficheiros engadidos',
        files_removed: 'Ficheiros eliminados',
      },
      files: 'Ficheiros',
    },
  },
  configurations_fields: {
    feature: {
      name: {
        placeholder: 'Introduce un nome para a tarefa',
      },
    },
  },
  schedule_modal: {
    trigger_button_label: 'Repetir',
    activity: {
      title: 'Seleccionar período (personalizado)',
    },
    task: {
      title: 'Definir repetición',
    },
  },
  schedule_field: {
    readable_periodicity: {
      not_defined: 'Sen definir',
      no_repetition: '(Sen repetición)',
      defined_per_activity: 'Repetición definida pola actividade',
      at_every: 'Cada',
      hourly: 'hora',
      daily: 'día',
      weekly: 'semana',
      monthly: 'mes',
      weekly_at: 'ás',
      and: 'e',
      at_hour: 'ás',
      until_end_day: 'ata a data',
    },
    quantities: 'Cantidades',
  },
  activity_type_posology_schedule_form: {
    title: 'Definir a dosificación',
    fields: {
      psology: {
        label: 'Dosificación',
      },
      posology_schedule_type: {
        label: 'Dosificación',
        options: {
          [PosologySchedule.PeriodsOfTheDay]: 'Período do día',
          [PosologySchedule.TimesList]: 'Lista de horas',
          [PosologySchedule.Repetition]: 'Horario',
          [PosologySchedule.SOS]: 'SOS',
        },
      },
      frequency: {
        options: {
          [Frequency.Hourly]: 'Horas',
          [Frequency.Daily]: 'Días',
          [Frequency.Weekly]: 'Semanas',
          [Frequency.Monthly]: 'Meses',
        },
      },
      frequency_value: {
        label: 'Repetir cada',
        validations: {
          min: 'O valor debe ser igual ou maior que un.',
        },
      },
      time_list: {
        hour: {
          label: 'Hora',
        },
        quantity: {
          label: 'Cantidade',
        },
        add: 'Engadir tempo',
        required: 'Engade polo menos unha programación.',
        error: 'Tempo xa rexistrado.',
      },
      periods_of_the_day_item: {
        required: 'Engade polo menos un.',
      },
      repetitionHourlyFrequencyValue: 'Toma cada',
      repetitionHourlyFrequencyValueHour: 'hora',
      repetitionHourlyFrequencyValueHours: 'horas',
      repetition_quantity: {
        label: 'Cantidades',
        validations: {
          min: 'O valor debe ser igual ou maior que un.',
        },
      },
      end_date: {
        label: 'Data de peche',
        validations: {
          min: 'Data non válida',
        },
      },
      end_hour: {
        label: 'Hora de finalización',
        validations: {
          min: 'Hora non válida',
        },
      },
    },
  },
  care_plan_sheet_activities_group_entry_configurationvalue: {
    repetition: {
      per_activity: 'Por actividade',
      per_task: 'Por tarefa',
    },
    aivd: {
      at_time: 'ás',
      transportation: 'Transporte',
    },
  },
  periodicity_form: {
    empty_state: 'Aínda non se definiu ningunha repetición.',
    fields: {
      frequency: {
        label: 'Cada',
        hourly: 'Horas',
        daily: 'Días',
        weekly: 'Semanas',
        monthly: 'Meses',
      },
      frequency_value: {
        label: 'Repetición por:',
        activity: 'Actividade',
        task: 'Tarefa',
        validations: {
          required: 'Campo obrigatorio',
          min: 'O valor da frecuencia debe ser maior que un.',
          max: 'A frecuencia debe ser menor ou igual a {{ value }}.',
        },
      },
      start_at_date: {
        label: 'Data de inicio',
        validations: {
          superior_to_end_date: 'A data de inicio debe ser anterior á data de finalización.',
        },
      },
      end_date: {
        label: 'Data de peche',
        validations: {
          superior_to_start_date: 'A data de finalización debe ser posterior á data de inicio.',
        },
      },
      end_hour: {
        label: 'Hora de finalización',
      },
      days: {
        label: 'Días da semana',
        validations: {
          required: 'Campo obrigatorio',
        },
      },
    },
  },
  role_permissions_professional_caregiver_office_screen: {
    generic_header: {
      subtitle: 'Engadir roles e xestionar permisos',
    },
    fields: {
      name: {label: 'Nome', placeholder: 'Engadir un nome ao posto'},
      description: {
        label: 'Descrición',
        placeholder: 'Engadir unha descrición',
      },
    },
    view: {
      tooltip: 'Permitir que o usuario vexa o contido',
      label: 'Ver',
    },
    edit: {
      tooltip: 'Permitir que o usuario edite o contido',
      label: 'Editar',
    },
    select_all_permissions: 'Todos os permisos',
    localized_string_input: {
      name: {
        title: 'Traducións para o nome',
      },
      description: {
        title: 'Traducións para a descrición',
      },
    },
    router_prompt: {
      title: 'Postos e permisos',
      content: {
        title: 'Seguro que queres saír?',
        subtitle: 'Os cambios que fixeches perderanse.',
      },
    },
    snackbar: {
      load: {
        error: 'Produciuse un erro ao cargar a páxina.',
      },
      update: {
        success: 'Actualizouse correctamente!',
        error: 'Produciuse un erro ao actualizar os permisos para este posto.',
      },
      exit: 'Non se gardaron os cambios.',
    },
  },
  shifts_page: {
    shifts: 'Quenda',
    team: 'Equipo',
    create_button: 'Engadir',
    schedule: 'Programación:',
    to_hour: 'ás',
    responsible: {
      label: 'Persoa responsable',
      not_defined: 'Sen definir',
    },
    professionals: {
      singular: 'Profesional',
      plural: 'Profesionais',
    },
    teams: {
      singular: 'equipo',
      plural: 'equipos',
    },
    empty_state: {
      title: 'Aínda non se rexistraron quendas.',
      subtitle: 'Preme en engadir para rexistrar unha quenda.',
    },
    shift_modal: {
      create_shift: 'Engadir quenda',
      edit_shift: 'Editar quenda',
      shift_form: {
        name: 'Nome da quenda',
        name_placeholder: 'Quenda de mañá, tarde e noite',
        required_field_message: 'Campo obrigatorio.',
        min_required_field_message: 'Obrigatorio.',
        pattern_field_message: 'Campo non válido.',
        max_characters_name_message: 'Máximo {{ characters }} caracteres.',
        abbreviation: 'ID',
        abbreviation_placeholder: 'M (mañá), A (tarde), E (noite)',
        color: 'Cor',
        social_response: 'Servizos sociais para a quenda',
        social_responses_placeholder_input: 'Introducir servizos sociais',
        people_per_shift: 'Persoal de quenda',
        people_per_shift_placeholder_input: 'Número máximo de persoal por quenda',
        start_hour: 'Hora de inicio',
        end_hour: 'Hora de finalización',
        shift_responsible: 'Persoa responsable por quenda',
        shift_responsible_placeholder: 'Sen xestor asignado',
        snackbar: {
          create_shift: {
            success: 'A quenda {{ name }} creouse correctamente!',
            error: 'Produciuse un erro ao engadir a quenda.',
          },
          update_shift: {
            success: 'Quenda {{ name }} actualizada correctamente!',
            error: 'Produciuse un erro ao actualizar o quenda.',
          },
        },
      },
    },
    confirm_delete: {
      title: 'Eliminar quenda: {{ name }}',
      copy: {
        title: 'Seguro que queres eliminar a quenda?',
        subtitle: 'Esta acción non se pode reverter.',
      },
    },
    cancel: 'Cancelar',
    create: 'Engadir',
    save: 'Gardar',
    delete: 'Eliminar',
  },
  planner: {
    header: {
      title: 'Calendario',
      filter_all_seniors_label: 'Todos os usuarios',
      filter_input_placeholder: 'Filtros',
      range: {
        day: 'Día',
        week: 'Semana',
        month: 'Mes',
      },
      add: 'Engadir',
      add_disabled: 'Non tes permisos para engadir esta tarefa.',
      toggler: {
        calendar: 'calendario',
        list: 'lista',
      },
    },
    body: {
      untreaten: '',
      untreaten_one: '{{count}} sen xestionar',
      untreaten_other: '{{count}} sen xestionar',
      empty: 'Non hai tarefas planificadas para mostrar.',
    },
    side: {
      see_all: 'Ver todo',
      birthdays: 'Aniversarios',
    },
    modal: {
      create: {
        activity: {
          ativities_group: 'Actividade',
          ativities_group_placeholder: 'Selecciona unha actividade',
          activities_type: 'Tarefa',
          activities_type_placeholder: 'Seleccionar unha tarefa',
          add_users_label: 'Engadir usuarios',
          add_users_placeholder: 'Engadir os usuarios',
          added_users: 'Usuarios engadidos',
          add_users_type: {
            social_response: 'Servizos sociais',
            user: 'Usuarios',
          },
          add_users_mark: '',
          add_users_mark_one: 'Seleccionar todos {{sr}} (usuarios) {{count}}',
          add_users_mark_other: 'Marcar todos {{sr}} (usuarios) {{count}}',
          add_users_mark_all: 'Seleccionar todos os usuarios',
          success: '',
          success_one: 'A tarefa engadiuse correctamente!',
          success_other: 'As tarefas engadíronse correctamente!',
        },
      },
      delete: {
        title: {
          single: 'Seguro que queres eliminar a tarefa para o usuario “{{name}}”?',
          multiple: 'Seguro que queres eliminar as tarefas “{{name}}”?',
        },
        desc: {
          single: 'A tarefa eliminarase do plan de coidados do usuario.',
          multiple: 'As tarefas eliminaranse no plan de coidados de cada usuario.',
        },
      },
    },
    item_menu: {
      activity: {
        users: 'Usuarios',
        action: {
          delete: 'Eliminar',
          delete_disabled: 'Non tes permisos para eliminar esta tarefa.',
          edit: 'Editar plan',
          plan: 'Plan',
          register: 'Rexistro',
        },
      },
      anniversary: {
        birthday: 'Aniversario',
        born: 'Nacido o',
        years: 'anos',
      },
    },
    errors: {
      content: 'Produciuse un erro ao cargar a programación.',
      save_activity: 'Produciuse un erro ao gardar a tarefa. Téntao de novo.',
      delete_activity: 'Produciuse un erro ao eliminar a(s) tarefa(s). Téntao de novo.',
      unknown_item_type: "Tipo de elemento do planificador descoñecido: '{{ type }}'",
    },
  },
  care_services: {
    header: {
      add_button_disabled_tooltip: 'Non tes permisos para engadir servizos',
      seniors_label: 'Maiores',
      filter_caregivers_label: 'Todos os profesionais',
    },
    form: {
      required_field_message: 'Este campo é obrigatorio',
      title_label: 'Título',
      title_placeholder: 'Título do servizo',
      color_label: 'Cor',
      start_date_label: 'Data de inicio',
      end_date_label: 'Data de fin',
      start_time_label: 'Hora de inicio',
      end_time_label: 'Hora de fin',
      repetition_label: 'Recorrencia',
      responsible_users_label: 'Profesionais asociados',
      responsible_users_placeholder: 'Selecciona un responsable',
      senior_label: 'Maior',
      senior_placeholder: 'Seleccionar maior',
      notes_label: 'Observacións',
      notes_placeholder: 'Escriba unha observación',
      permissions_disabled_tooltip: 'Non tes permisos para editar este campo',
      twenty_four_hours_label: 'Servizo 24 horas',
    },
    modals: {
      edit: {
        title: 'Editar servizo',
      },
      create: {
        title: 'Crear servizo',
      },
      delete: {
        title: 'Eliminar servizo',
        description: 'Seguro que queres eliminar este servizo?',
        cannot_be_undone: 'Esta acción non se pode desfacer.',
      },
      repetition_options: {
        only_this_event: 'Só este evento',
        this_and_following: 'Este e os seguintes',
      },
      edit_recurrent: {
        title: 'Editar servizo recorrente',
      },
      delete_recurrent: {
        title: 'Eliminar servizo recorrente',
      },
      edit_confirmation: {
        title: 'Gardar cambios no servizo',
        subtitle: 'Seguro que queres editar o servizo?',
      },
    },
    toasts: {
      edit: {
        success: {
          title: 'Servizo actualizado',
          message: 'O servizo foi actualizado correctamente.',
        },
        error: {
          unknown: {
            title: 'Erro ao actualizar servizo',
            message: 'Produciuse un erro ao actualizar o servizo. Téntao de novo máis tarde.',
          },
          not_found: {
            title: 'Servizo non encontrado',
            message: 'O turno de servizo que estás intentando editar non foi encontrado.',
          },
          access_denied: {
            title: 'Acceso denegado',
            message: 'Non tes permiso para editar este turno de servizo.',
          },
          invalid_data: {
            title: 'Datos inválidos',
            message:
              'Os datos proporcionados son inválidos. Por favor, verifica a información e téntao de novo.',
          },
          end_date_invalid: {
            title: 'Data de fin inválida',
            message: 'A data de fin debe ser posterior á data de inicio.',
          },
          repetition_required: {
            title: 'Erro de edición',
            message: 'A recorrencia é requirida cando o servizo é recorrente.',
          },
          repetition_removal_scope: {
            title: 'Erro de edición',
            message: 'Para eliminar a recorrencia necesita editar todos os eventos da serie.',
          },
          repetition_change_scope: {
            title: 'Erro de edición',
            message: 'Para cambiar a recorrencia necesita editar todos os eventos da serie.',
          },
        },
      },
      create: {
        success: {
          title: 'Servizo creado',
          message: 'O servizo foi creado correctamente.',
        },
        error: {
          title: 'Erro ao crear novo servizo',
          message: 'Produciuse un erro ao crear o novo servizo. Téntao de novo máis tarde.',
        },
      },
      delete: {
        error: {
          title: 'Erro ao eliminar servizo',
          message: 'Produciuse un erro ao eliminar o servizo. Téntao de novo máis tarde.',
        },
      },
    },
    export: {
      repetition: {
        ends_on: 'Remata o',
        ends_after: 'Remata despois de',
        occurrences: 'ocorrencias',
      },
    },
    permissions: {
      view: 'Non tes permisos para ver os servizos.',
    },
  },
  shared: {
    handle_care_task_modal: {
      confirm_edit: {
        title: 'Seguro que queres editar a tarefa?',
      },
      confirm_edit_cancel: {
        title: 'Aínda non gardaches a información. Seguro que queres saír?',
        subtitle: 'Os datos non gardados perderanse.',
      },
      confirm_reset: {
        title: 'Seguro que queres desfacer a tarefa?',
        subtitle: 'A tarefa volverá ao seu estado inicial',
      },
    },
    input_disabled_tooltip: 'Non tes permisos para editar este campo',
  },
  export: {
    plan: 'Plan',
    address: 'Enderezo',
    contributor_number: 'Nº Contribuinte',
    sns_number: 'Nº SNS',
    contact: 'Contacto',
    others: 'Outros',
    details: 'Detalles',
    tasks: 'Tarefas',
    task: 'Tarefa',
    register: 'Rexistro',
    date: 'Data',
    start_date: 'Inicio',
    end_date: 'Fin',
    periodicity: 'Periodicidade',
    observations: 'Notas',
    no_records: 'Sen rexistros',
    no_repetition: '(Sen repetición)',
    senior_profile: {
      save_pdf: 'Ficha de seguimento (.pdf)',
      report_title: 'Ficha de seguimento',
      user_section_title: 'Datos da persoa usuaria',
      profile_section_subtitle: 'Perfil',
      full_name: 'Nome completo',
      preferred_name: 'Como lle gusta que lle chamen',
      birth_date: 'Data de nacemento',
      sns_number: 'Número de usuario - SNS',
      social_security_number: 'Número da Seguridade Social - NISS',
      taxpayer_number: 'Número de contribuínte',
      additional_information: 'Información adicional',
      health_conditions_subtitle: 'Patoloxías/Limitacións',
      food_intolerances_title: 'Alerxias / Intolerancias',
      food_intolerances_subtitle: 'Intolerancias (Alimentación)',
      medication_allergies_subtitle: 'Alerxias (Medicación)',
      medication_title: 'Medicación',
      latest_vital_signs_title: 'Historial de signos vitais (últimos 7 días)',
    },
    error: {
      pdf_generation: 'Produciuse un erro ao xerar o PDF. Téntao de novo máis tarde.',
      xlsx_generation: 'Produciuse un erro ao xerar o Excel. Téntao de novo máis tarde.',
      pdf_monthly_not_supported: 'A exportación mensual non é compatible.',
    },
  },
};
